import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Typography, Button, /* ToggleButton, ToggleButtonGroup - commented out with map style buttons */ Tooltip, TextField, Chip, Snackbar, Alert, Menu, MenuItem, ListItemIcon, ListItemText, InputAdornment, List, ListItem, ListItemButton, CircularProgress, ImageList, ImageListItem, ImageListItemBar } from '@mui/material';
import CyberSpinner from './CyberSpinner';
import { telegram } from '../services/telegram';
import CloseIcon from '@mui/icons-material/Close';
// Map style icons - commented out with ToggleButtonGroup
// import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
// import TerrainIcon from '@mui/icons-material/Terrain';
import LayersIcon from '@mui/icons-material/Layers';
import FlagIcon from '@mui/icons-material/Flag';
import NavigationIcon from '@mui/icons-material/Navigation';
// DirectionsIcon moved to WellDetailsModal
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TurnLeftIcon from '@mui/icons-material/TurnLeft';
import TurnRightIcon from '@mui/icons-material/TurnRight';
import StraightIcon from '@mui/icons-material/Straight';
import UTurnLeftIcon from '@mui/icons-material/UTurnLeft';
import RoundaboutLeftIcon from '@mui/icons-material/RoundaboutLeft';
import MergeIcon from '@mui/icons-material/Merge';
// ContentCopyIcon moved to WellDetailsModal
// import NightlightIcon from '@mui/icons-material/Nightlight';
// import ContrastIcon from '@mui/icons-material/Contrast';
// import LightModeIcon from '@mui/icons-material/LightMode';
// import DarkModeIcon from '@mui/icons-material/DarkMode';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
// import StarIcon from '@mui/icons-material/Star'; // Removed with long-press menu
import PlaceIcon from '@mui/icons-material/Place';
import SearchIcon from '@mui/icons-material/Search';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ConstructionIcon from '@mui/icons-material/Construction';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ContrastIcon from '@mui/icons-material/Contrast';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; // Hazmat/Heavy Haul mode
import ExploreIcon from '@mui/icons-material/Explore';
// import ViewInArIcon from '@mui/icons-material/ViewInAr'; // Removed - 3D tilt button removed
// Vehicle icons for user location marker selection
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import FireTruckIcon from '@mui/icons-material/FireTruck';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import ElectricCarIcon from '@mui/icons-material/ElectricCar';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import AirIcon from '@mui/icons-material/Air';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import { Well } from '../types';
import { RoadHazard, roadHazardsService } from '../services/roadHazards';
import { CommunityFacility, communityFacilitiesService } from '../services/communityFacilities';
import { addBasinLayer } from '../services/basins';
import { leafletMapService, SavedRoute } from '../services/leafletMap';
import { NavigationService } from '../services/navigation';
import { routePhotoService, RoutePhoto } from '../services/routePhotoService';
import { PhotoUploader } from './PhotoUploader';
// RoutePhotosGallery moved to WellDetailsModal
import { CommunityRoutes } from './CommunityRoutes';
import { RouteCreationFAB } from './RouteCreationFAB';
import { LandmarkFAB, LandmarkType, buildMaterialLandmarkIcon, LANDMARK_TYPES } from './LandmarkFAB';
import { MapFabManager } from './MapFabManager';
import { AddFacilityForm } from './AddFacilityForm';
import AISpeedDial from './AISpeedDial';
import { useGPSState } from './GPSSpeedDial';
import { WeatherOverlay } from './WeatherOverlay';
import { WindVisualization } from './WindVisualization';
import { NearbyServicesWidget } from './NearbyServicesWidget';
import { PollenAlertWidget } from './PollenAlertWidget';
import { ElevationWidget } from './ElevationWidget';
import { StreetViewModal } from './StreetViewModal';
import { WellDetailsModal } from './WellDetailsModal';
import { TruckStopsLayer } from './TruckStopsLayer';
import { OfflineCacheManager } from './OfflineCacheManager';
import { offlineMapCache } from '../services/offlineMapCache';
import RoadHazardDialog from './RoadHazardDialog';
import { useHazardNotifications } from '../contexts/HazardNotificationContext';
import RouteIcon from '@mui/icons-material/Route';
import { useAppStore } from '@stores/appStore';
import { useAutoDismiss } from '../hooks/useAutoDismiss';
import { getCyberColors, type CyberColors } from '../theme/cyberColors';
import { LaserRouteOverlay, SelectedRouteInfo } from './LaserRouteOverlay';
import { poopPinService, PoopPin } from '../services/poopPinService';
import { tacoPinService, TacoPin } from '../services/tacoPinService';
import { requestPreciseLocation, storeExternalFix } from '../services/highPrecisionLocation';
// import { featureAccess } from '../services/featureAccess'; // Commented out - OCD FAB disabled

// ===== MAP STYLE DEFINITIONS =====
// Night Mode - Dark background, light roads for night driving
const NIGHT_MODE_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#263c3f' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6b9a76' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#f3d19c' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
  { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#17263c' }] },
];

// High Contrast - Bold colors, thicker appearance for bright daylight
const HIGH_CONTRAST_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'on' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#000000' }, { weight: 2 }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#000000' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#c5e1a5' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#000000' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#a5d6a7' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }, { weight: 1.5 }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffeb3b' }] },
  { featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#ff9800' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#000000' }, { weight: 2 }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#000000' }] },
  { featureType: 'road.local', elementType: 'geometry.stroke', stylers: [{ color: '#666666' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#757575' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#2196f3' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#000000' }] },
];

// Light Cyberpunk Mode - Clean white/blue with subtle neon accents
const LIGHT_CYBER_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#f0f4ff' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#1e293b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#3b82f6' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#dcfce7' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#16a34a' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e2e8f0' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#475569' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#fef3c7' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#fbbf24' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#92400e' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#e2e8f0' }] },
  { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#3b82f6' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#bfdbfe' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#1d4ed8' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
];

// Dark Cyberpunk Mode - Deep blue/purple with neon accents  
const DARK_CYBER_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#60a5fa' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1e3a2f' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#34d399' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#334155' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#78350f' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#fbbf24' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#fbbf24' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#60a5fa' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0c4a6e' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#38bdf8' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }] },
];

// Desert/Arid Mode - Optimized for desert visibility, sand tones with high contrast roads
const DESERT_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#e8dcc4' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5c4a32' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f0e6' }, { weight: 2 }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#8b4513' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#d4c4a8' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#c9b896' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6b5a3e' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#8fbc8f' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#2e5a2e' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#f5f5dc' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#8b7355' }, { weight: 1 }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#4a3728' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffd700' }] },
  { featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ color: '#8b7355' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#ff8c00' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#654321' }, { weight: 2 }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.highway', elementType: 'labels.text.stroke', stylers: [{ color: '#654321' }, { weight: 3 }] },
  { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#fffacd' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#c9b896' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#4682b4' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#4682b4' }] },
];

// Roads Only Mode - Minimal style emphasizing roads for navigation
const ROADS_ONLY_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8892b0' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
  { featureType: 'administrative', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#16213e' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2d3a5e' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a2e' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#a8b2d1' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#3d5a80' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#00d9ff' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#0a192f' }, { weight: 2 }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#00d9ff' }] },
  { featureType: 'road.highway', elementType: 'labels.text.stroke', stylers: [{ color: '#0a192f' }, { weight: 3 }] },
  { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#2d3a5e' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0a192f' }] },
  { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

// Topographic Style - Emphasizes terrain elevation for desert/mountain navigation
const TOPO_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#e8e4d9' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#4a4a4a' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f0ebe0' }, { weight: 2 }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#8b4513' }, { weight: 1.5 }] },
  { featureType: 'landscape.natural.landcover', elementType: 'geometry', stylers: [{ color: '#c9d4a5' }] },
  { featureType: 'landscape.natural.terrain', elementType: 'geometry', stylers: [{ color: '#d4c4a8' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#2e5a2e' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#a8d4a8' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#8b7355' }, { weight: 0.5 }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#5c4a32' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffe4b5' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#ff6b35' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#8b0000' }, { weight: 1 }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#8b0000' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#7eb5d6' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#1e4d6b' }] },
];

// Shared overlay to make road labels bolder and more legible across all map styles
const ROAD_LABEL_EMPHASIS: google.maps.MapTypeStyle[] = [
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'on' }] },
  // Heavy strokes to visually enlarge text; prioritize highways first
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#0b1120' }, { weight: 24 }] },
  { featureType: 'road.highway', elementType: 'labels.text.stroke', stylers: [{ color: '#f8fafc' }, { weight: 24 }] },
  { featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'on' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#0f172a' }, { weight: 20 }] },
  { featureType: 'road.arterial', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 20 }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#0f172a' }, { weight: 18 }] },
  { featureType: 'road', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 18 }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#111827' }, { weight: 14 }] },
];

const withRoadLabelEmphasis = (styles: google.maps.MapTypeStyle[] = []) => [...styles, ...ROAD_LABEL_EMPHASIS];

// Custom fork badge icon used in turn-by-turn banner
const ForkBadge = ({ direction }: { direction: 'left' | 'right' | 'split' }) => {
  const rotate = direction === 'left' ? -12 : direction === 'right' ? 12 : 0;
  return (
    <svg
      width="44"
      height="44"
      viewBox="0 0 64 64"
      style={{ transform: `rotate(${rotate}deg)` }}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M32 52V30L20 18M32 30L44 18"
        stroke="#000"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M14 22L20 14L26 22" fill="#000" />
      <path d="M38 22L44 14L50 22" fill="#000" />
      <circle cx="32" cy="56" r="4" fill="#000" />
    </svg>
  );
};

// Map style type
type MapStyleType = 'terrain' | 'satellite' | 'hybrid' | 'night' | 'highcontrast' | 'cyber' | 'desert' | 'roads' | 'topo';

// ===== FACILITY TYPE MARKER ICONS =====
// Large markers with MUI-style SVG icons - matching MapFabManager filter button exactly
// Designed for gloved users with large touch targets
const MARKER_SIZE = 56; // Larger for better visibility

// Theme-aware accent mapping for facility markers (align with FAB palette)
const FACILITY_ACCENT_MAP: Record<string, keyof CyberColors> = {
  // Main types
  well: 'neonOrange',
  swd: 'neonGreen',
  rig: 'neonYellow',
  frk: 'neonPink',
  drl: 'neonOrange',

  // Tank & Storage
  tb: 'neonCyan',
  gs: 'neonYellow',
  bgt: 'textMuted',

  // Flare & Emissions
  fs: 'neonRed',

  // Compressor & Pumps
  cs: 'neonBlue',
  cp: 'textMuted',

  // Plants & Processing
  gp: 'neonPurple',
  rf: 'neonPurple',
  tp: 'neonPink',
  inj: 'neonGreen',

  // Pipelines
  plg: 'neonBlue',
  plc: 'textMuted',
  plw: 'neonCyan',

  // Gathering & Metering
  ggs: 'neonBlue',
  mgs: 'neonPurple',
  wh: 'neonOrange',

  // Brine & Water
  bw: 'neonCyan',

  // Waste & Environmental
  pit: 'textMuted',
  wm: 'neonRed',
  rfl: 'neonGreen',
  rm: 'neonYellow',
  ab: 'neonOrange',

  // Service & Regulatory
  sc: 'textMuted',
  ci: 'neonGreen',

  // Community overlays
  taco: 'neonOrange',
  taco_truck: 'neonOrange',
  food: 'neonPink',
  food_truck: 'neonPink',
  cafe: 'neonYellow',
  coffee: 'neonYellow',

  // Generic fallback
  facility: 'neonPurple',
  default: 'neonCyan',
};

const DEFAULT_ACCENT_KEY: keyof CyberColors = 'neonCyan';

const hexToRgba = (hex: string, alpha: number): string => {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) {
    return hex;
  }
  const r = parseInt(normalized.substring(0, 2), 16);
  const g = parseInt(normalized.substring(2, 4), 16);
  const b = parseInt(normalized.substring(4, 6), 16);
  const safeAlpha = Math.min(Math.max(alpha, 0), 1);
  return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`;
};

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const escapeAttr = (value: string): string => escapeHtml(value);

const getFacilityAccentColor = (typeCode: string, cyber: CyberColors): string => {
  const palette = cyber as Record<string, string>;
  const key = FACILITY_ACCENT_MAP[typeCode] || DEFAULT_ACCENT_KEY;
  return palette[key] || palette[DEFAULT_ACCENT_KEY] || '#06b6d4';
};

const formatCoordinate = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value ?? 'N/A');
  return numeric.toFixed(5);
};

type TooltipWell = {
  id?: string | number;
  wellName?: string;
  api?: string;
  status?: string;
  [key: string]: unknown;
};

const renderFacilityTooltip = (
  params: {
    well: TooltipWell;
    statusColors: { bg: string; text: string; border: string };
    accentColor: string;
    latitude: number;
    longitude: number;
    darkMode: boolean;
  }
): string => {
  const { well, statusColors, accentColor, latitude, longitude, darkMode } = params;
  const safeName = escapeHtml(well.wellName || 'Unknown Facility');
  const apiValueRaw = well.api ? String(well.api) : 'N/A';
  const apiValueDisplay = escapeHtml(apiValueRaw);
  const apiValueAttr = apiValueRaw !== 'N/A' ? escapeAttr(apiValueRaw) : '';
  const latStr = formatCoordinate(latitude);
  const lngStr = formatCoordinate(longitude);
  const coordsDisplay = latStr === 'N/A' || lngStr === 'N/A' ? 'N/A' : `${latStr}, ${lngStr}`;
  const coordsDisplayEscaped = escapeHtml(coordsDisplay);
  const coordsAttr = coordsDisplay !== 'N/A' ? escapeAttr(coordsDisplay) : '';
  const statusLabel = escapeHtml(well.status || 'Unknown');
  const accentBorder = hexToRgba(accentColor, darkMode ? 0.55 : 0.4);
  const accentGlow = hexToRgba(accentColor, darkMode ? 0.35 : 0.24);
  const cardBackground = darkMode ? 'rgba(15, 23, 42, 0.94)' : 'rgba(248, 250, 252, 0.96)';
  const textColor = darkMode ? '#e5e7eb' : '#111827';
  const labelColor = darkMode ? '#94a3b8' : '#475569';
  const buttonBg = darkMode ? 'rgba(30, 41, 59, 0.85)' : 'rgba(226, 232, 240, 0.85)';
  const buttonText = darkMode ? '#f8fafc' : '#0f172a';
  const badgeShadow = hexToRgba(statusColors.text, 0.35);

  return `
    <div
      data-map-tooltip="true"
      data-well-id="${escapeAttr(String(well.id ?? ''))}"
      data-api="${apiValueAttr}"
      data-coords="${coordsAttr}"
      style="
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        background: ${cardBackground};
        border-radius: 12px;
        border: 1px solid ${accentBorder};
        box-shadow: 0 12px 28px ${accentGlow};
        padding: 14px 16px;
        min-width: 220px;
        color: ${textColor};
        position: relative;
      "
    >
      <button
        type="button"
        data-tooltip-close="true"
        style="
          position: absolute;
          top: 8px;
          right: 8px;
          width: 22px;
          height: 22px;
          border-radius: 999px;
          border: 1px solid ${hexToRgba(textColor, 0.25)};
          background: ${buttonBg};
          color: ${buttonText};
          font-size: 12px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        "
        aria-label="Close info"
      >×</button>
      <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 10px;">
        <div style="width: 12px; height: 12px; border-radius: 999px; background: ${statusColors.text}; box-shadow: 0 0 14px ${badgeShadow};"></div>
        <div style="display: flex; flex-direction: column; gap: 4px;">
          <span style="font-weight: 600; font-size: 15px; line-height: 1.1; color: ${textColor}; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${safeName}</span>
          <span style="font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: ${statusColors.text};">${statusLabel}</span>
        </div>
      </div>
      <div style="display: flex; flex-direction: column; gap: 10px;">
        <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
          <div style="display: flex; flex-direction: column;">
            <span style="font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: ${labelColor};">API</span>
            <span style="font-size: 13px; font-family: 'SFMono-Regular', 'Menlo', 'Monaco', monospace; color: ${textColor};">${apiValueDisplay}</span>
          </div>
          <button
            type="button"
            data-copy-value="api"
            data-copy-label="Copy"
            data-copy-text="${apiValueAttr}"
            ${apiValueAttr ? '' : 'disabled'}
            style="
              border: 1px solid ${hexToRgba(buttonText, 0.2)};
              background: ${buttonBg};
              color: ${buttonText};
              border-radius: 6px;
              padding: 4px 8px;
              font-size: 11px;
              font-weight: 600;
              cursor: ${apiValueAttr ? 'pointer' : 'not-allowed'};
              min-width: 64px;
            "
            aria-label="Copy API"
          >${apiValueAttr ? 'Copy' : 'N/A'}</button>
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px;">
          <div style="display: flex; flex-direction: column;">
            <span style="font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: ${labelColor};">Lat / Long</span>
            <span style="font-size: 13px; font-family: 'SFMono-Regular', 'Menlo', 'Monaco', monospace; color: ${textColor};">${coordsDisplayEscaped}</span>
          </div>
          <button
            type="button"
            data-copy-value="coords"
            data-copy-label="Copy"
            data-copy-text="${coordsAttr}"
            ${coordsAttr ? '' : 'disabled'}
            style="
              border: 1px solid ${hexToRgba(buttonText, 0.2)};
              background: ${buttonBg};
              color: ${buttonText};
              border-radius: 6px;
              padding: 4px 8px;
              font-size: 11px;
              font-weight: 600;
              cursor: ${coordsAttr ? 'pointer' : 'not-allowed'};
              min-width: 64px;
            "
            aria-label="Copy coordinates"
          >${coordsAttr ? 'Copy' : 'N/A'}</button>
        </div>
      </div>
    </div>
  `;
};

// Get status color based on well status - for tooltip display
const getStatusColors = (status: string | undefined): { bg: string; text: string; border: string } => {
  const s = (status || '').toLowerCase();
  
  // Active/Producing states - Green
  if (s.includes('active') || s.includes('producing') || s.includes('drilling') || s.includes('completed')) {
    return { bg: 'rgba(16, 185, 129, 0.2)', text: '#10b981', border: '#10b981' };
  }
  // New/Permit states - Cyan
  if (s.includes('new') || s.includes('permit') || s.includes('approved') || s.includes('pending')) {
    return { bg: 'rgba(6, 182, 212, 0.2)', text: '#06b6d4', border: '#06b6d4' };
  }
  // Inactive/Shut-in states - Amber
  if (s.includes('inactive') || s.includes('shut') || s.includes('suspended') || s.includes('idle')) {
    return { bg: 'rgba(245, 158, 11, 0.2)', text: '#f59e0b', border: '#f59e0b' };
  }
  // Plugged/Abandoned states - Red
  if (s.includes('plugged') || s.includes('abandoned') || s.includes('p&a') || s.includes('ta')) {
    return { bg: 'rgba(239, 68, 68, 0.2)', text: '#ef4444', border: '#ef4444' };
  }
  // Cancelled/Denied states - Purple
  if (s.includes('cancelled') || s.includes('denied') || s.includes('expired') || s.includes('void')) {
    return { bg: 'rgba(168, 85, 247, 0.2)', text: '#a855f7', border: '#a855f7' };
  }
  // Default - Gray
  return { bg: 'rgba(148, 163, 184, 0.2)', text: '#94a3b8', border: '#94a3b8' };
};

// MUI-style SVG paths (24x24 viewBox) - matching Quick Search button icons exactly
const FACILITY_SVG_PATHS: Record<string, string> = {
  // LocalGasStation for wells
  well: 'M19.77 7.23l.01-.01-3.72-3.72L15 4.56l2.11 2.11c-.94.36-1.61 1.26-1.61 2.33 0 1.38 1.12 2.5 2.5 2.5.36 0 .69-.08 1-.21v7.21c0 .55-.45 1-1 1s-1-.45-1-1V14c0-1.1-.9-2-2-2h-1V5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v16h10v-7.5h1.5v5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V9c0-.69-.28-1.32-.73-1.77zM12 10H6V5h6v5zm6 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z',
  // Water waves for SWD
  swd: 'M17.66 8L12 2.35 6.34 8C4.78 9.56 4 11.64 4 13.64s.78 4.11 2.34 5.67 3.61 2.35 5.66 2.35 4.1-.79 5.66-2.35S20 15.64 20 13.64 19.22 9.56 17.66 8zM6 14c.01-2 .62-3.27 1.76-4.4L12 5.27l4.24 4.38C17.38 10.77 17.99 12 18 14H6z',
  // Construction for rigs
  rig: 'M22 22H2V20H22V22ZM5 4H7L8 6H10L11 4H13L14 6H16L17 4H19V6L18 8V18H6V8L5 6V4ZM8 18H16V9H8V18Z',
  // AutoFixHigh spark for fracking
  frk: 'M7.5 5.6 10 7 8.6 4.5 10 2 7.5 3.4 5 2l1.4 2.5L5 7zm12 9.8L17 14l1.4 2.5L17 19l2.5-1.4L22 19l-1.4-2.5L22 14zM22 2l-2.5 1.4L17 2l1.4 2.5L17 7l2.5-1.4L22 7l-1.4-2.5zm-7.63 5.29a.996.996 0 0 0-1.41 0L1.29 18.96c-.39.39-.39 1.02 0 1.41l2.34 2.34c.39.39 1.02.39 1.41 0L16.7 11.05c.39-.39.39-1.02 0-1.41zm-1.03 5.49-2.12-2.12 2.44-2.44 2.12 2.12z',
  // PrecisionManufacturing drill head for drilling
  drl: 'm19.93 8.21-3.6 1.68L14 7.7V6.3l2.33-2.19 3.6 1.68c.38.18.82.01 1-.36.18-.38.01-.82-.36-1L16.65 2.6c-.38-.18-.83-.1-1.13.2l-1.74 1.6c-.18-.24-.46-.4-.78-.4-.55 0-1 .45-1 1v1H8.82C8.34 4.65 6.98 3.73 5.4 4.07c-1.16.25-2.15 1.25-2.36 2.43-.22 1.32.46 2.47 1.48 3.08L7.08 18H4v3h13v-3h-3.62L8.41 8.77c.17-.24.31-.49.41-.77H12v1c0 .55.45 1 1 1 .32 0 .6-.16.78-.4l1.74 1.6c.3.3.75.38 1.13.2l3.92-1.83c.38-.18.54-.62.36-1-.18-.37-.62-.54-1-.36M6 8c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1',
  // Bolt for tank battery
  tb: 'M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z',
  // Whatshot/flame for flare stack
  fs: 'M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z',
  // Settings/gear for compressor
  cs: 'M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z',
  // Factory for gas plant
  gp: 'M22 22H2V10l7-3v2l5-2v3h3l1-8h3l1 8v12zM4 20h16v-8h-3l-1-8h-1l1 8h-2V9.95l-5 2V10l-5 2.14V20z',
  // OilBarrel for refinery
  rf: 'M4 6.5C4 4.57 7.58 3 12 3s8 1.57 8 3.5V18c0 1.93-3.58 3.5-8 3.5S4 19.93 4 18V6.5zM12 5c-3.35 0-6 1.12-6 2s2.65 2 6 2 6-1.12 6-2-2.65-2-6-2zm6 13V9.27C16.78 9.75 14.53 10 12 10s-4.78-.25-6-.73V18c0 .88 2.65 2 6 2s6-1.12 6-2z',
  // Science/flask for treating plant
  tp: 'M19.8 18.4L14 10.67V6.5l1.35-1.69c.26-.33.03-.81-.39-.81H9.04c-.42 0-.65.48-.39.81L10 6.5v4.17L4.2 18.4c-.49.66-.02 1.6.8 1.6h14c.82 0 1.29-.94.8-1.6z',
  // MedicationLiquid for injection plant
  inj: 'M3 3h18v2H3zm5 4h8v2l-1 1v4l3 3v3H6v-3l3-3v-4l-1-1V7zm2 2v3.5l-3 3V18h10v-2.5l-3-3V9h-4z',
  // LinearScale for pipelines
  plg: 'M19.5 9.5c-1.03 0-1.9.62-2.29 1.5h-2.92c-.39-.88-1.26-1.5-2.29-1.5s-1.9.62-2.29 1.5H6.79c-.39-.88-1.26-1.5-2.29-1.5C3.12 9.5 2 10.62 2 12s1.12 2.5 2.5 2.5c1.03 0 1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5s1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5 1.38 0 2.5-1.12 2.5-2.5s-1.12-2.5-2.5-2.5z',
  plc: 'M19.5 9.5c-1.03 0-1.9.62-2.29 1.5h-2.92c-.39-.88-1.26-1.5-2.29-1.5s-1.9.62-2.29 1.5H6.79c-.39-.88-1.26-1.5-2.29-1.5C3.12 9.5 2 10.62 2 12s1.12 2.5 2.5 2.5c1.03 0 1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5s1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5 1.38 0 2.5-1.12 2.5-2.5s-1.12-2.5-2.5-2.5z',
  plw: 'M19.5 9.5c-1.03 0-1.9.62-2.29 1.5h-2.92c-.39-.88-1.26-1.5-2.29-1.5s-1.9.62-2.29 1.5H6.79c-.39-.88-1.26-1.5-2.29-1.5C3.12 9.5 2 10.62 2 12s1.12 2.5 2.5 2.5c1.03 0 1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5s1.9-.62 2.29-1.5h2.92c.39.88 1.26 1.5 2.29 1.5 1.38 0 2.5-1.12 2.5-2.5s-1.12-2.5-2.5-2.5z',
  // Hub for gas gathering
  ggs: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-5-9c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm10 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-5-5c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm0 10c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm0-5c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z',
  // Speed/meter for metering station
  mgs: 'M20.38 8.57l-1.23 1.85a8 8 0 01-.22 7.58H5.07A8 8 0 0115.58 6.85l1.85-1.23A10 10 0 003.35 19a2 2 0 001.72 1h13.85a2 2 0 001.74-1 10 10 0 00-.27-10.44zm-9.79 6.84a2 2 0 002.83 0l5.66-8.49-8.49 5.66a2 2 0 000 2.83z',
  // GridView for well pad
  wh: 'M3 3v8h8V3H3zm6 6H5V5h4v4zm-6 4v8h8v-8H3zm6 6H5v-4h4v4zm4-16v8h8V3h-8zm6 6h-4V5h4v4zm-6 4v8h8v-8h-8zm6 6h-4v-4h4v4z',
  // Opacity/droplet for brine well
  bw: 'M17.66 8L12 2.35 6.34 8C4.78 9.56 4 11.64 4 13.64s.78 4.11 2.34 5.67 3.61 2.35 5.66 2.35 4.1-.79 5.66-2.35S20 15.64 20 13.64 19.22 9.56 17.66 8zM6 14c.01-2 .62-3.27 1.76-4.4L12 5.27l4.24 4.38C17.38 10.77 17.99 12 18 14H6z',
  // Landscape/mountain for pits & below grade
  pit: 'M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z',
  bgt: 'M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z',
  // PropaneTank for gas storage
  gs: 'M4 15v3c0 1.1.9 2 2 2h2v2h8v-2h2c1.1 0 2-.9 2-2v-3H4zm14-5.86V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v5.14c-1.72.45-3 2-3 3.86h18c0-1.86-1.28-3.41-3-3.86zM8 4h8v5h-2V6h-1v3h-2V6h-1v3H8V4z',
  // Delete/trash for waste mgmt
  wm: 'M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z',
  // Recycling for recycling
  rfl: 'M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0020 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 004 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z',
  // Healing for remediate
  rm: 'M17.73 12.02l3.98-3.98c.39-.39.39-1.02 0-1.41l-4.34-4.34c-.39-.39-1.02-.39-1.41 0l-3.98 3.98L8 2.29C7.8 2.1 7.55 2 7.29 2c-.25 0-.51.1-.7.29L2.25 6.63c-.39.39-.39 1.02 0 1.41l3.98 3.98L2.25 16c-.39.39-.39 1.02 0 1.41l4.34 4.34c.39.39 1.02.39 1.41 0l3.98-3.98 3.98 3.98c.2.2.45.29.71.29.26 0 .51-.1.71-.29l4.34-4.34c.39-.39.39-1.02 0-1.41l-3.99-3.98z',
  // CleaningServices for abatement
  ab: 'M16 11h-1V3c0-1.1-.9-2-2-2h-2c-1.1 0-2 .9-2 2v8H8c-2.76 0-5 2.24-5 5v7h18v-7c0-2.76-2.24-5-5-5zm-5-8h2v8h-2V3zm8 18H5v-5c0-1.65 1.35-3 3-3h8c1.65 0 3 1.35 3 3v5z',
  // Engineering for service co
  sc: 'M9 15c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4zm4.94-5.41c.02-.13.06-.25.06-.39 0-.12-.04-.24-.06-.37l.79-.62c.06-.05.07-.14.03-.21l-.75-1.3c-.04-.07-.13-.1-.2-.08l-.93.37c-.15-.12-.32-.22-.51-.3l-.14-.98c-.02-.08-.08-.14-.17-.14H10.9c-.08 0-.15.06-.16.14l-.14.98c-.19.08-.36.18-.51.3l-.93-.37c-.08-.03-.16 0-.2.08l-.75 1.3c-.04.07-.03.16.03.21l.79.62c-.02.12-.06.24-.06.37s.04.24.06.37l-.79.62c-.06.05-.07.14-.03.21l.75 1.3c.04.07.13.1.2.08l.93-.37c.15.12.32.22.51.3l.14.98c.02.08.08.14.17.14h1.5c.08 0 .15-.06.16-.14l.14-.98c.19-.08.36-.18.51-.3l.93.37c.08.03.16 0 .2-.08l.75-1.3c.04-.07.03-.16-.03-.21l-.79-.62zM11.6 10.5c-.73 0-1.33-.6-1.33-1.33 0-.73.6-1.33 1.33-1.33s1.33.6 1.33 1.33c0 .73-.6 1.33-1.33 1.33zM22 6.5v10c0 .83-.67 1.5-1.5 1.5H16v-2h4V7h-4V5h4.5c.83 0 1.5.67 1.5 1.5z',
  // Verified for class I/II
  ci: 'M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82L8.6 22.5l3.4-1.47 3.4 1.46 1.89-3.19 3.61-.82-.34-3.69L23 12zm-12.91 4.72l-3.8-3.81 1.48-1.48 2.32 2.33 5.85-5.87 1.48 1.48-7.33 7.35z',
  // Air/wind for crude pump
  cp: 'M14.5 17c0 1.65-1.35 3-3 3s-3-1.35-3-3h2c0 .55.45 1 1 1s1-.45 1-1-.45-1-1-1H2v-2h9.5c1.65 0 3 1.35 3 3zM19 6.5C19 4.57 17.43 3 15.5 3S12 4.57 12 6.5h2c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S16.33 8 15.5 8H2v2h13.5c1.93 0 3.5-1.57 3.5-3.5zm-.5 4.5H2v2h16.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5v2c1.93 0 3.5-1.57 3.5-3.5S20.43 11 18.5 11z',
  // Generic facility (building)
  facility: 'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z',
  // 🌮 Taco icon - half circle taco shape
  taco: 'M21 11c0-4.97-4.03-9-9-9s-9 4.03-9 9c0 1.5.37 2.91 1.02 4.16l.13.22C5.94 18.31 8.74 21 12 21s6.06-2.69 7.85-5.62l.13-.22c.65-1.25 1.02-2.66 1.02-4.16zm-9 7c-2.21 0-4.15-1.14-5.28-2.86C7.87 13.87 9.81 13 12 13s4.13.87 5.28 2.14C16.15 16.86 14.21 18 12 18zm0-7c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z',
  // 🍔 Food truck - hamburger/takeout box icon
  food: 'M1 21.98c0 .56.45 1.01 1.01 1.01H22c.55 0 1-.45 1-1.01v-.98H1v.98zM8.17 4 4.59 7.58c-.56.56-.59 1.47.01 1.99l6.89 6.89-1.88 1.88a.996.996 0 1 0 1.41 1.41l7.08-7.08a.996.996 0 0 0 0-1.41l-6.89-6.89c-.56-.56-1.47-.56-2.03 0l-.01.01L8.17 5.4V4h-.01zM1 18h22v2H1v-2z',
  // ☕ Coffee cup icon
  cafe: 'M18.5 3H6c-1.1 0-2 .9-2 2v5.71c0 3.83 2.95 7.18 6.78 7.29 3.96.12 7.22-3.06 7.22-7V8h1.5c1.38 0 2.5-1.12 2.5-2.5S19.88 3 18.5 3zm0 3H18V5h.5c.27 0 .5.22.5.5s-.23.5-.5.5zM4 19h16v2H4v-2z',
  // Default location pin
  default: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'
};

// Extract facility type code from type string
// Input could be: 'well', 'swd', 'rig', 'facility', or 'Tank Battery - (TB)'
function getFacilityTypeCode(typeStr: string | undefined, facilityTypeStr?: string): string {
  if (!typeStr) return 'default';
  
  const lowerType = typeStr.toLowerCase();
  
  // Direct matches for basic types
  if (lowerType === 'well' || lowerType === 'wells' || lowerType === 'wl') return 'well';
  if (lowerType === 'swd' || lowerType === 'swds') return 'swd';
  if (lowerType === 'rig' || lowerType === 'rigs') return 'rig';
  // Food vendor types - use specific icons
  if (lowerType === 'taco_truck' || lowerType === 'taco' || lowerType === 'tacos') return 'taco';
  if (lowerType === 'food_truck' || lowerType === 'food') return 'food';
  if (lowerType === 'cafe' || lowerType === 'coffee') return 'cafe';
  
  // For facilities, try to extract the code from facilityType field like "Tank Battery - (TB)"
  if (lowerType === 'facility' && facilityTypeStr) {
    const match = facilityTypeStr.match(/\(([A-Z0-9]+)\)/);
    if (match) {
      return match[1].toLowerCase(); // Returns 'tb', 'fs', 'cs', etc.
    }

    const normalized = facilityTypeStr.toLowerCase();
    if (normalized === 'frk' || normalized.includes('frack')) {
      return 'frk';
    }
    if (normalized === 'drl' || normalized.includes('drill')) {
      return 'drl';
    }
  }
  
  // Fallback to facility or default
  if (lowerType === 'facility') return 'facility';
  return 'default';
}

// Calculate offset position for route endpoint icons to avoid blocking the polyline
// Offset in meters based on zoom level and direction
function calculateOffsetPosition(
  lat: number,
  lng: number,
  direction: 'north' | 'south' | 'east' | 'west' = 'north',
  offsetMeters: number = 50
): { lat: number; lng: number } {
  // 1 degree latitude ≈ 111 km
  // 1 degree longitude ≈ 111 km * cos(latitude)
  const metersPerDegreeLat = 111000;
  const metersPerDegreeLng = 111000 * Math.cos((lat * Math.PI) / 180);
  
  const deltaLat = offsetMeters / metersPerDegreeLat;
  const deltaLng = offsetMeters / metersPerDegreeLng;
  
  switch (direction) {
    case 'north':
      return { lat: lat + deltaLat, lng };
    case 'south':
      return { lat: lat - deltaLat, lng };
    case 'east':
      return { lat, lng: lng + deltaLng };
    case 'west':
      return { lat, lng: lng - deltaLng };
    default:
      return { lat, lng };
  }
}

// Generate SVG marker icon with MUI-style SVG path as data URL
// Icons mirror voice-navigation FAB styling with cyber gradients and neon glow
function createMarkerIcon(
  typeCode: string,
  isSelected: boolean = false,
  theme?: { cyber: CyberColors; darkMode: boolean; accentOverride?: string }
): google.maps.Icon {
  const cyber = theme?.cyber;
  const darkMode = theme?.darkMode ?? false;
  const accent = theme?.accentOverride
    ? theme.accentOverride
    : cyber
      ? getFacilityAccentColor(typeCode, cyber)
      : '#06b6d4';
  const svgPath = FACILITY_SVG_PATHS[typeCode] || FACILITY_SVG_PATHS.default;

  const size = isSelected ? MARKER_SIZE + 10 : MARKER_SIZE;
  const center = size / 2;
  const innerRadius = center - 6;

  const gradientId = `marker_grad_${typeCode}_${isSelected ? 'sel' : 'norm'}`;
  const glowId = `marker_glow_${typeCode}_${isSelected ? 'sel' : 'norm'}`;

  const accentSoft = hexToRgba(accent, darkMode ? 0.55 : 0.35);
  const accentStrong = hexToRgba(accent, darkMode ? 0.85 : 0.55);
  const accentGlow = hexToRgba(accent, darkMode ? 0.6 : 0.45);
  const panelFill = cyber?.bgCard ?? (darkMode ? 'rgba(17, 24, 39, 0.92)' : 'rgba(248, 250, 252, 0.95)');
  const innerStroke = hexToRgba(accent, darkMode ? 0.25 : 0.18);
  const iconFill = darkMode ? '#fef3c7' : '#0f172a';
  const highlight = darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.35)';

  const iconScale = (innerRadius * 1.6) / 24;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <defs>
        <linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${accentSoft}" />
          <stop offset="100%" stop-color="${accentStrong}" />
        </linearGradient>
        <filter id="${glowId}" x="-70%" y="-70%" width="240%" height="240%">
          <feDropShadow dx="0" dy="1.2" stdDeviation="${darkMode ? 2.8 : 2.1}" flood-color="${accentGlow}" flood-opacity="1" />
        </filter>
      </defs>
      <circle cx="${center}" cy="${center}" r="${center - 2}" fill="url(#${gradientId})" stroke="${accent}" stroke-width="3" filter="url(#${glowId})" />
      <circle cx="${center}" cy="${center}" r="${innerRadius}" fill="${panelFill}" stroke="${innerStroke}" stroke-width="2" />
      <ellipse cx="${center}" cy="${center - innerRadius * 0.55}" rx="${innerRadius * 0.55}" ry="${innerRadius * 0.28}" fill="${highlight}" />
      <g transform="translate(${center}, ${center}) scale(${iconScale}) translate(-12, -12)">
        <path d="${svgPath}" fill="${iconFill}" />
      </g>
      ${isSelected ? `<circle cx="${center}" cy="${center}" r="${center - 3}" fill="none" stroke="${iconFill}" stroke-width="2" stroke-dasharray="6 3" opacity="0.85"/>` : ''}
    </svg>
  `;

  const encoded = encodeURIComponent(svg);

  return {
    url: `data:image/svg+xml,${encoded}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2)
  };
}

// Generate search marker icon with distinct magenta/cyan glow for search results
// This uses the same facility icons but with bright accent colors to distinguish from favorites
function createSearchMarkerIcon(typeCode: string): google.maps.Icon {
  const svgPath = FACILITY_SVG_PATHS[typeCode] || FACILITY_SVG_PATHS.default;
  
  // Use bright magenta/cyan color scheme for search results
  const searchColor = '#ec4899'; // Bright magenta/pink
  const glowColor = '#06b6d4';   // Bright cyan
  
  const size = MARKER_SIZE + 8; // Slightly larger
  const glowSize = 6;
  const strokeWidth = 5;
  
  const iconSize = (size - 12) * 0.65;
  const iconOffset = (size - iconSize) / 2;
  const iconScale = iconSize / 24;
  
  const filterId = `search_glow_${typeCode}`;
  
  const svg = `
    <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"${size}\" height=\"${size}\" viewBox=\"0 0 ${size} ${size}\">
      <defs>
        <filter id=\"${filterId}\" x=\"-100%\" y=\"-100%\" width=\"300%\" height=\"300%\">
          <feGaussianBlur stdDeviation=\"${glowSize}\" result=\"coloredBlur\"/>
          <feMerge>
            <feMergeNode in=\"coloredBlur\"/>
            <feMergeNode in=\"SourceGraphic\"/>
          </feMerge>
        </filter>
        <linearGradient id=\"search_grad_${typeCode}\" x1=\"0%\" y1=\"0%\" x2=\"100%\" y2=\"100%\">
          <stop offset=\"0%\" style=\"stop-color:${searchColor};stop-opacity:0.4\"/>
          <stop offset=\"100%\" style=\"stop-color:#1a1a2e;stop-opacity:0.95\"/>
        </linearGradient>
      </defs>
      <!-- Outer glow ring with cyan color -->
      <circle cx=\"${size/2}\" cy=\"${size/2}\" r=\"${size/2 - 3}\" 
        fill=\"none\" 
        stroke=\"${glowColor}\" 
        stroke-width=\"3\"
        opacity=\"0.6\"
        filter=\"url(#${filterId})\"
      />
      <!-- Main marker background -->
      <rect x=\"4\" y=\"4\" width=\"${size-8}\" height=\"${size-8}\" rx=\"12\" 
        fill=\"url(#search_grad_${typeCode})\" 
        stroke=\"${searchColor}\" 
        stroke-width=\"${strokeWidth}\"
        filter=\"url(#${filterId})\"
      />
      <!-- MUI Icon -->
      <g transform=\"translate(${iconOffset}, ${iconOffset}) scale(${iconScale})\">
        <path d=\"${svgPath}\" fill=\"${searchColor}\"/>
      </g>
      <!-- Search indicator - small magnifying glass icon in corner -->
      <circle cx=\"${size - 12}\" cy=\"12\" r=\"6\" fill=\"${glowColor}\" opacity=\"0.9\"/>
      <text x=\"${size - 12}\" y=\"15\" 
        font-size=\"8\" 
        fill=\"#1a1a2e\" 
        text-anchor=\"middle\" 
        font-weight=\"bold\">🔍</text>
    </svg>
  `;
  
  const encoded = encodeURIComponent(svg);
  
  return {
    url: `data:image/svg+xml,${encoded}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2)
  };
}

// ===== POLYLINE SIMPLIFICATION (Douglas-Peucker Algorithm) =====
// Reduces excessive points from hand-drawn routes while preserving shape
function simplifyPolyline(
  points: Array<{ lat: number; lng: number }>,
  tolerance: number = 0.00005 // ~5 meters at equator
): Array<{ lat: number; lng: number }> {
  if (points.length <= 2) return points;

  // Find the point with maximum distance from the line between first and last
  let maxDistance = 0;
  let maxIndex = 0;

  const first = points[0];
  const last = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i++) {
    const distance = perpendicularDistance(points[i], first, last);
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }

  // If max distance is greater than tolerance, recursively simplify
  if (maxDistance > tolerance) {
    const left = simplifyPolyline(points.slice(0, maxIndex + 1), tolerance);
    const right = simplifyPolyline(points.slice(maxIndex), tolerance);
    return [...left.slice(0, -1), ...right];
  }

  // Otherwise, return just the endpoints
  return [first, last];
}

function perpendicularDistance(
  point: { lat: number; lng: number },
  lineStart: { lat: number; lng: number },
  lineEnd: { lat: number; lng: number }
): number {
  const dx = lineEnd.lng - lineStart.lng;
  const dy = lineEnd.lat - lineStart.lat;

  if (dx === 0 && dy === 0) {
    // Line is a point
    return Math.sqrt(
      Math.pow(point.lat - lineStart.lat, 2) +
      Math.pow(point.lng - lineStart.lng, 2)
    );
  }

  const t = Math.max(0, Math.min(1,
    ((point.lng - lineStart.lng) * dx + (point.lat - lineStart.lat) * dy) /
    (dx * dx + dy * dy)
  ));

  const nearestLat = lineStart.lat + t * dy;
  const nearestLng = lineStart.lng + t * dx;

  return Math.sqrt(
    Math.pow(point.lat - nearestLat, 2) +
    Math.pow(point.lng - nearestLng, 2)
  );
}
// ================================================================

// ===== PERMANENT Z-INDEX LEVELS (DO NOT CHANGE) =====
// These values ensure all UI elements are visible above the map canvas
const Z_INDEX = {
  MAP_CANVAS: 0,                 // Google Maps / Leaflet base layer
  CONTROLS: 1050,                // Map controls, buttons, overlays, badges
  LOADING_SPINNER: 1100,         // Loading indicators (above controls)
  MODAL_CONTENT: 1200,           // Modal content sections
  MODAL_CONTAINER: 1300,         // Modal wrapper (topmost)
  NOTIFICATIONS: 10001,          // Notifications/snackbars (above FAB taskbar)
} as const;
// ===================================================

const NM_FALLBACK_LOCATION = { lat: 34.5199, lng: -105.8701 } as const;

// Marker icon map removed; using default Google pins for reliability

interface FavoritesMapViewProps {
  open: boolean;
  favorites: Well[];
  onClose: () => void;
  onSelectWell: (well: Well) => void;
  roadHazards?: RoadHazard[];
  onRouteCreated?: () => void;
  onHazardReported?: (hazard: RoadHazard) => void;
}

export function FavoritesMapView({ open, favorites, onClose, onSelectWell, roadHazards = [], onRouteCreated, onHazardReported }: FavoritesMapViewProps) {
  console.log('[FavoritesMapView] Component rendered. open:', open, 'favorites count:', favorites.length, 'favorites:', favorites);
  
  // ===== MapFabManager toggle - set to true to use new 4-zone FAB system =====
  const useNewFabManager = true;
  
  // Get theme state and addFavorite function
  const darkMode = useAppStore((state) => state.darkMode);
  const highContrast = useAppStore((state) => state.highContrast);
  const toggleDarkMode = useAppStore((state) => state.toggleDarkMode);
  const toggleHighContrast = useAppStore((state) => state.toggleHighContrast);
  const addFavorite = useAppStore((state) => state.addFavorite);
  const removeFavorite = useAppStore((state) => state.removeFavorite);
  const cyber = getCyberColors(darkMode, highContrast);
  const gpsState = useGPSState();
  
  // Sound toggle state
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Landmarks drawer removed - not needed for current implementation
  
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const hazardMarkersRef = useRef<google.maps.Marker[]>([]);
  const basinLayerCleanupRef = useRef<(() => void) | null>(null);
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const hasInitialBoundsRef = useRef<boolean>(false);
  const [selectedWell, setSelectedWell] = useState<Well | null>(null);
  const [mapStyle, setMapStyle] = useState<MapStyleType>('hybrid');
  const [isDrawing, setIsDrawing] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showAddFacilityForm, setShowAddFacilityForm] = useState(false);
  const [addFacilityLocation, setAddFacilityLocation] = useState<{ lat: number; lng: number } | null>(null);
  
  // Track nearest facility for route auto-naming (alternative to selectedWell)
  const [nearestFacilityForRoute, setNearestFacilityForRoute] = useState<{ id: string; api: string; name: string } | null>(null);
  const [_containerReady, setContainerReady] = useState(false);
  
  // Ref to allow placing destination landmark from facility clicks
  const placeDestinationAtRef = useRef<((lat: number, lng: number, facilityName?: string) => void) | null>(null);
  
  // Facility layer filtering state - expanded to include ALL facility type codes
  // These match the codes from FACILITY_ACCENT_MAP (tb, fs, cs, gp, etc.)
  const FACILITY_LAYERS = [
    'all',       // Show all facilities
    'favorites', // Show only personal favorites (star)
    'well',      // Oil/Gas Wells
    'swd',       // Salt Water Disposal
    'rig',       // Active Rigs
    'gp',       // Gas Plant (moved up for easier access)
    'tb',       // Tank Battery
    'fs',       // Flare Stack
    'cs',       // Compressor Station
    'gs',       // Gas Storage
    'cp',       // Crude Pump
    'rf',       // Refinery
    'tp',       // Treating Plant
    'inj',      // Injection Plant
    'plg',      // Gas Pipeline
    'plc',      // Crude Pipeline
    'plw',      // Water Pipeline
    'ggs',      // Gas Gathering
    'mgs',      // Metering Station
    'wh',       // Well Head
    'bw',       // Brine Well
    'pit',      // Pit
    'bgt',      // Below Grade Tank
    'wm',       // Waste Management
    'rfl',      // Recycling Facility
    'rm',       // Remediate
    'ab',       // Abatement
    'sc',       // Service Company
    'ci',       // Class I/II
    'facility', // Generic Facility (catch-all)
    'taco',     // Taco trucks
  ] as const;
  type FacilityLayerType = typeof FACILITY_LAYERS[number];
  const [currentFacilityLayer, setCurrentFacilityLayer] = useState<FacilityLayerType>('all');
  
  // Search state
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Well[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isVoiceListening, setIsVoiceListening] = useState(false);
  const voiceRecognitionRef = useRef<any>(null);

  useAutoDismiss(searchError, () => setSearchError(null));
  
  // Address autocomplete state
  interface AddressPrediction {
    description: string;
    placeId: string;
    mainText: string;
    secondaryText: string;
    isAddress?: boolean;
    isCity?: boolean;
    isEstablishment?: boolean;
  }
  const [addressPredictions, setAddressPredictions] = useState<AddressPrediction[]>([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const voiceFabStatus: 'idle' | 'listening' | 'processing' | 'success' | 'error' =
    isVoiceListening ? 'listening' : (isSearching || isLoadingAddresses) ? 'processing' : 'idle';
  const [searchMode, setSearchMode] = useState<'facilities' | 'address'>('facilities');
  const addressSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Weather overlay state - hidden by default until user clicks weather FAB
  const [showWeatherOverlay, setShowWeatherOverlay] = useState(false);
  const [weatherExpanded, setWeatherExpanded] = useState(false);
  const [showWindOnMap, setShowWindOnMap] = useState(true); // For animated wind particles
  const [windData, setWindData] = useState<{ speed: number; direction: number; gust?: number; cardinal: string } | null>(null);
  
  // Temperature and UV for FAB display
  const [temperature, setTemperature] = useState<number | null>(null);
  const [temperatureColor, setTemperatureColor] = useState<string>('#94a3b8');
  const [feelsLike, setFeelsLike] = useState<number | null>(null);
  const [uvIndex, setUvIndex] = useState<number | null>(null);
  const [uvColor, setUvColor] = useState<string>('#94a3b8');
  
  // Precipitation/Rain data for FAB display and flash flood warnings
  const [precipProbability, setPrecipProbability] = useState<number | null>(null);
  const [precipColor, setPrecipColor] = useState<string>('#94a3b8');
  const [thunderstormProbability, setThunderstormProbability] = useState<number | null>(null);
  
  // Weather warning notification state (high UV, extreme temp, high wind, flash flood)
  const [weatherWarning, setWeatherWarning] = useState<{ open: boolean; message: string; severity: 'warning' | 'error' }>({ 
    open: false, 
    message: '', 
    severity: 'warning' 
  });
  const [shownWarnings, setShownWarnings] = useState<Set<string>>(new Set()); // Track shown warnings to avoid spam
  
  // Google API Widget toggle states - each FAB controls its own widget visibility
  const [showAirQuality, setShowAirQuality] = useState(false); // AQI shown in FAB, expanded view optional
  const [aqiValue, setAqiValue] = useState<number | null>(null); // Current AQI for FAB display
  const [aqiColor, setAqiColor] = useState<string>('#94a3b8'); // AQI color based on severity
  const [showPollen, setShowPollen] = useState(true);
  const [pollenIndex, setPollenIndex] = useState<number | null>(null); // Current pollen index for FAB display
  const [pollenColor, setPollenColor] = useState<string>('#94a3b8'); // Pollen color based on severity
  const [showNearbyServices, setShowNearbyServices] = useState(false); // Modal - opens on FAB click
  
  // Truck Stops layer state - shows Loves, Pilot, TA, Flying J etc.
  const [showTruckStops, setShowTruckStops] = useState(false);
  const [truckStopCount, setTruckStopCount] = useState(0);
  const [isLoadingTruckStops, setIsLoadingTruckStops] = useState(false);
  
  // Street View Modal state
  const [showStreetViewModal, setShowStreetViewModal] = useState(false);
  
  // Offline Cache Manager state
  const [showOfflineCacheManager, setShowOfflineCacheManager] = useState(false);
  const [streetViewLocation, setStreetViewLocation] = useState<{ lat: number; lng: number; title?: string } | null>(null);
  
  // Settings mini FAB menu state
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  
  // Cop reports state - flashing police markers on map
  interface CopReport {
    id: number;
    latitude: number;
    longitude: number;
    reported_at: string;
    expires_at: string;
    report_type: string;
    confirmations: number;
  }
  const [copReports, setCopReports] = useState<CopReport[]>([]);
  const [copMarkersRef] = useState<{ current: google.maps.marker.AdvancedMarkerElement[] }>({ current: [] });
  const [poopPins, setPoopPins] = useState<PoopPin[]>([]);
  const poopMarkersRef = useRef<google.maps.Marker[]>([]);
  const [tacoPins, setTacoPins] = useState<TacoPin[]>([]);
  const tacoMarkersRef = useRef<google.maps.Marker[]>([]);
  
  // Well Details Modal state (replaces drawer)
  const [wellModalOpen, setWellModalOpen] = useState(false);
  const [modalWell, setModalWell] = useState<Well | null>(null);
  
  // InfoWindow for marker hover tooltips
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const hoveredMarkerRef = useRef<google.maps.Marker | null>(null);
  const hoverPinnedRef = useRef(false);
  const hoverCloseTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const cancelPendingTooltipClose = useCallback(() => {
    if (hoverCloseTimerRef.current) {
      window.clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  }, []);

  const scheduleTooltipClose = useCallback((delay: number = 150) => {
    if (hoverPinnedRef.current) return;
    cancelPendingTooltipClose();
    hoverCloseTimerRef.current = window.setTimeout(() => {
      if (!hoverPinnedRef.current) {
        infoWindowRef.current?.close();
      }
    }, delay);
  }, [cancelPendingTooltipClose]);

  useEffect(() => {
    return () => {
      cancelPendingTooltipClose();
      infoWindowRef.current?.close();
      hoveredMarkerRef.current = null;
      hoverPinnedRef.current = false;
    };
  }, [cancelPendingTooltipClose]);
  const lastTappedWellIdRef = useRef<string | null>(null); // For mobile tap-to-show logic
  const isTouchDevice = typeof window !== 'undefined' && 'ontouchstart' in window;
  
  // Elevation Widget state
  const [showElevationWidget, setShowElevationWidget] = useState(false);
  const [elevationPath, setElevationPath] = useState<Array<{ lat: number; lng: number }>>([]);
  
  // User vehicle icon state - multiple vehicle options
  type VehicleType = 'dot' | 'tractor' | 'semi' | 'welding' | 'pickup' | 'company' | 'vacuum' | 'rig' | 'car' | 'safety' | 'water' | 'hotOiler' | 'oilTruck' | 'monster' | 'electric' | 'vapor' | 'crane' | 'sandTank' | 'raceCar' | 'motorcycle' | 'eagle' | 'fracking' | 'drilling' | 'roadrunner';
  const [userVehicle, setUserVehicle] = useState<VehicleType>('pickup');
  const [showVehicleMenu, setShowVehicleMenu] = useState(false);
  
  // Heavy/Hazmat route mode - optimizes routes for oversized loads, semis, heavy equipment
  // Avoids highways where possible, prefers truck routes, avoids low bridges/weight restrictions
  const [heavyHaulMode, setHeavyHaulMode] = useState(false);
  
  // OCD Activity Layer - shows incidents, pits, and frac activity from OCD data
  // This is a separate historical layer with its own toggle (not part of facility filters)
  // const [showOcdActivity, /* setShowOcdActivity */] = useState(false); // Commented out - OCD FAB disabled
  /* const [ocdActivityData, setOcdActivityData] = useState<{
    incidents: any[];
    pits: any[];
    fracActivity: any[];
  }>({ incidents: [], pits: [], fracActivity: [] }); */
  // const [isLoadingOcdActivity, setIsLoadingOcdActivity] = useState(false); // Commented out - OCD FAB disabled
  // const ocdMarkersRef = useRef<google.maps.Marker[]>([]); // Commented out - OCD FAB disabled
  
  // Caliche Routes Layer - shows saved landmark routes from the community
  const [showCalichemRoutes, setShowCalichemRoutes] = useState(false);
  const [calichemRoutesData, setCalichemRoutesData] = useState<any[]>([]);
  const [isLoadingCalichemRoutes, setIsLoadingCalichemRoutes] = useState(false);
  const calichemRoutePolylinesRef = useRef<google.maps.Polyline[]>([]);
  const calichemRouteMarkersRef = useRef<google.maps.Marker[]>([]);
  const navigationMarkersRef = useRef<google.maps.Marker[]>([]);
  
  // Caliche Route Detail View (turn-by-turn navigation)
  const [showCalichemRouteDetail, setShowCalichemRouteDetail] = useState(false);
  const [selectedCalichemRoute, setSelectedCalichemRoute] = useState<any>(null);
  const [selectedCalichemRouteLandmarks, setSelectedCalichemRouteLandmarks] = useState<any[]>([]);
  const [currentLandmarkIndex, setCurrentLandmarkIndex] = useState(0);
  
  // Laser Route Info Panel - shows when clicking on distance badge
  const [laserRouteSelection, setLaserRouteSelection] = useState<SelectedRouteInfo | null>(null);
  
  const clearNavigationMarkers = useCallback(() => {
    navigationMarkersRef.current.forEach(m => m.setMap(null));
    navigationMarkersRef.current = [];
  }, []);
  
  // Road Hazard Dialog - for reporting hazards and cop sightings
  const [showRoadHazardDialog, setShowRoadHazardDialog] = useState(false);
  
  // Community Facilities - user-submitted facilities visible to all
  const [communityFacilities, setCommunityFacilities] = useState<CommunityFacility[]>([]);
  const communityFacilityMarkersRef = useRef<google.maps.Marker[]>([]);
  
  // Search Result Markers - persistent markers from search results
  const [searchResultMarkers, setSearchResultMarkers] = useState<Well[]>([]);
  const searchResultMarkersRef = useRef<google.maps.Marker[]>([]);
  
  // Hazard notification context for broadcasting hazards to all users
  const { showHazardNotification, setZoomCallback } = useHazardNotifications();
  
  // 3D Map tilt state - removed with compass/3D buttons
  // const [mapTilt, setMapTilt] = useState<number>(0);
  
  const [toast, setToast] = useState<{ open: boolean; message: string; severity?: 'success' | 'error' | 'info' | 'warning' }>({ open: false, message: '' });

  // Marker drag-to-trash state
  const [draggingMarker, setDraggingMarker] = useState<{ well: Well; x: number; y: number } | null>(null);
  const [isOverTrash, setIsOverTrash] = useState(false);
  const isOverTrashRef = useRef(false); // Ref to avoid closure issues
  const trashZoneRef = useRef<HTMLDivElement | null>(null);

  // Favorite marker long-press drag state (600ms hold to enable drag)
  const markerLongPressTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const markerDragEnabledRef = useRef<Map<string, boolean>>(new Map());
  const MARKER_LONG_PRESS_DURATION = 600; // Match FAB drag duration

  // Marker right-click context menu state
  const [markerContextMenu, setMarkerContextMenu] = useState<{ well: Well; x: number; y: number } | null>(null);

  // Auto-request user location when map opens
  useEffect(() => {
    if (!open) return;
    
    console.log('[FavoritesMapView] Map opened, requesting user location...');
    
    const acquire = async () => {
      const result = await requestPreciseLocation({
        desiredAccuracyMeters: 30,
        refinementTimeoutMs: 6000,
        allowLastKnownFallback: true,
        maxLastKnownAgeMs: 120000,
      });

      if (result.fix) {
        console.log('[FavoritesMapView] Got location:', {
          latitude: result.fix.lat,
          longitude: result.fix.lng,
          accuracy: result.fix.accuracy,
        });
        setUserLocation({ lat: result.fix.lat, lng: result.fix.lng });
        if (!result.fulfilled && result.fix.accuracy !== null) {
          console.warn('[FavoritesMapView] Initial location accuracy:', result.fix.accuracy, 'meters');
        }
      } else if (result.reason) {
        console.warn('[FavoritesMapView] Geolocation error:', result.reason);
      }
    };

    void acquire();
  }, [open]);

  // Inject CSS to hide Google Maps InfoWindow default white background
  useEffect(() => {
    const styleId = 'zia-infowindow-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* Hide Google InfoWindow default styling */
        .gm-style-iw-c {
          background: transparent !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          padding: 0 !important;
        }
        .gm-style-iw-d {
          overflow: visible !important;
          background: transparent !important;
        }
        .gm-style-iw-tc {
          display: none !important;
        }
        .gm-style-iw-t::after {
          display: none !important;
        }
        /* Hide the default close button - we handle it ourselves */
        .gm-style-iw button.gm-ui-hover-effect {
          display: none !important;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Keep ref in sync with state
  useEffect(() => {
    isOverTrashRef.current = isOverTrash;
  }, [isOverTrash]);

  // Cleanup zoom callback on unmount
  useEffect(() => {
    return () => {
      setZoomCallback(null);
    };
  }, [setZoomCallback]);

  // Track actual mouse/touch position during drag (screen coordinates)
  useEffect(() => {
    if (!draggingMarker) return;

    const TRASH_ZONE_BOTTOM = 100;
    const TRASH_ZONE_HEIGHT = 80;
    const TRASH_ZONE_WIDTH = 120;

    const checkTrashZone = (clientX: number, clientY: number) => {
      const screenHeight = window.innerHeight;
      const screenWidth = window.innerWidth;
      
      const trashLeft = (screenWidth - TRASH_ZONE_WIDTH) / 2;
      const trashRight = trashLeft + TRASH_ZONE_WIDTH;
      const trashTop = screenHeight - TRASH_ZONE_BOTTOM - TRASH_ZONE_HEIGHT;
      const trashBottom = screenHeight - TRASH_ZONE_BOTTOM;
      
      const overTrash = clientX >= trashLeft && clientX <= trashRight && 
                        clientY >= trashTop && clientY <= trashBottom;
      
      setIsOverTrash(overTrash);
      setDraggingMarker(prev => prev ? { ...prev, x: clientX, y: clientY } : null);
    };

    const handleMouseMove = (e: MouseEvent) => {
      checkTrashZone(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        checkTrashZone(touch.clientX, touch.clientY);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [draggingMarker]);

  // Map provider and drawing canvas state
  const [mapProvider, setMapProvider] = useState<'google' | 'leaflet'>('google');
  const [showDrawingCanvas, setShowDrawingCanvas] = useState(false);
  const leafletCanvasRef = useRef<HTMLDivElement>(null);
  const [savedRoutes, setSavedRoutes] = useState<SavedRoute[]>([]);
  const [routeName, setRouteName] = useState('');
  const [routeDescription, setRouteDescription] = useState('');
  const [pendingRouteCoordinates, setPendingRouteCoordinates] = useState<Array<[number, number]> | null>(null);
  
  // Drawn route save dialog state (for Google Maps drawing)
  const [showSaveRouteDialog, setShowSaveRouteDialog] = useState(false);
  const [drawnRoutePoints, setDrawnRoutePoints] = useState<Array<{ lat: number; lng: number }>>([]);
  const [originalPointCount, setOriginalPointCount] = useState(0);
  const [isSavingRoute, setIsSavingRoute] = useState(false);

  // Route refs (kept for potential future use)
  const displayedRouteRef = useRef<google.maps.Polyline | null>(null);
  const routeWaypointsRef = useRef<google.maps.Marker[]>([]);

  // Photo gallery and uploader state
  const [showPhotoUploader, setShowPhotoUploader] = useState(false);

  // Right-click context menu for adding route photos
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    lat: number;
    lng: number;
  } | null>(null);
  
  // Caliche Routes dialog state (opened from right-click)
  const [showCommunityRoutes, setShowCommunityRoutes] = useState(false);
  const [routePhotoLocation, setRoutePhotoLocation] = useState<{ lat: number; lng: number } | null>(null);
  // const [photoRefreshKey, setPhotoRefreshKey] = useState(0); // Temporarily disabled with Site Photos

  // Route preview state
  const [routeInfo, setRouteInfo] = useState<{ distance: string; duration: string; summary: string } | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [weatherLocation, setWeatherLocation] = useState<{ lat: number; lng: number } | null>(null); // Separate from userLocation - can fall back to map center
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const userMarkerRef = useRef<google.maps.Marker | null>(null);
  const [showRoutePhotosModal, setShowRoutePhotosModal] = useState(false);
  const [routePhotoFeed, setRoutePhotoFeed] = useState<RoutePhoto[]>([]);
  const [isLoadingRoutePhotos, setIsLoadingRoutePhotos] = useState(false);

  // Turn-by-turn navigation state
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigationSteps, setNavigationSteps] = useState<Array<{
    instructions: string;
    distance: string;
    duration: string;
    maneuver?: string;
  }>>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [navigationDestination, setNavigationDestination] = useState<Well | null>(null);
  const [ingressOrigin, setIngressOrigin] = useState<{ lat: number; lng: number } | null>(null);
  const [ingressWaypoints, setIngressWaypoints] = useState<Array<{ lat: number; lng: number }>>([]);
  const [ingressConnectorLabel, setIngressConnectorLabel] = useState<string>('Main road');
  const navigationWatchId = useRef<number | null>(null);
  const [myLocationRadiusMiles, setMyLocationRadiusMiles] = useState<number | null>(null);

  // Community route overlays state
  const [showRouteOverlays, setShowRouteOverlays] = useState(false);
  const [calicheRoutes, setCalicheRoutes] = useState<Array<{
    id: string;
    name: string;
    coordinates: Array<{ lat: number; lng: number }>;
    waypointCount: number;
    destinationName: string | null;
    createdByUsername: string | null;
    forkCount: number;
  }>>([]);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  const routePolylinesRef = useRef<google.maps.Polyline[]>([]);
  const routeMarkersRef = useRef<google.maps.Marker[]>([]);

  // Suppress unused variable warnings
  void mapProvider; void setMapProvider; void savedRoutes; void setSavedRoutes; void routeName; void setRouteName; void pendingRouteCoordinates; void userLocation;

  // Fetch weather data (wind, temp, UV) for FAB display and warnings
  useEffect(() => {
    if (!open) return;
    
    const fetchWeatherData = async () => {
      // Get location for weather - either cached userLocation or try to get fresh location
      let coords = userLocation;
      if (!coords) {
        const result = await requestPreciseLocation({
          desiredAccuracyMeters: 50,
          refinementTimeoutMs: 4000,
          allowLastKnownFallback: true,
          maxLastKnownAgeMs: 180000,
        });
        if (!result.fix) {
          // No location available yet, try again later
          return;
        }
        coords = { lat: result.fix.lat, lng: result.fix.lng };
      }

      try {
        const response = await fetch(`/api/google/weather?lat=${coords.lat}&lng=${coords.lng}`);
        if (response.ok) {
          const data = await response.json();
          if (data.current) {
            // Wind data
            const windSpeed = data.current.windSpeed || 0;
            setWindData({
              speed: windSpeed,
              direction: data.current.windDirection || 0,
              gust: data.current.windGust,
              cardinal: data.current.windCardinal || 'N',
            });
            
            // Temperature data
            const temp = data.current.temp;
            const feels = data.current.feelsLike;
            if (temp !== undefined) {
              setTemperature(temp);
              setFeelsLike(feels);
              // Color based on temperature
              if (temp >= 100) setTemperatureColor('#dc2626'); // Extreme heat - red
              else if (temp >= 90) setTemperatureColor('#f97316'); // Hot - orange
              else if (temp >= 75) setTemperatureColor('#eab308'); // Warm - yellow
              else if (temp >= 60) setTemperatureColor('#22c55e'); // Nice - green
              else if (temp >= 45) setTemperatureColor('#06b6d4'); // Cool - cyan
              else if (temp >= 32) setTemperatureColor('#3b82f6'); // Cold - blue
              else setTemperatureColor('#8b5cf6'); // Freezing - purple
            }
            
            // UV Index data
            const uv = data.current.uvIndex;
            if (uv !== undefined) {
              setUvIndex(uv);
              // Color based on UV severity (EPA scale)
              if (uv <= 2) setUvColor('#22c55e'); // Low - green
              else if (uv <= 5) setUvColor('#eab308'); // Moderate - yellow
              else if (uv <= 7) setUvColor('#f97316'); // High - orange
              else if (uv <= 10) setUvColor('#dc2626'); // Very High - red
              else setUvColor('#7c3aed'); // Extreme - purple
            }
            
            // Precipitation data - critical for desert flash flood warnings
            const precip = data.current.precipProbability;
            const thunder = data.current.thunderstormProbability;
            if (precip !== undefined) {
              setPrecipProbability(precip);
              setThunderstormProbability(thunder || 0);
              // Color based on precipitation probability
              if (precip <= 10) setPrecipColor('#94a3b8'); // Unlikely - gray
              else if (precip <= 30) setPrecipColor('#22c55e'); // Low - green
              else if (precip <= 50) setPrecipColor('#eab308'); // Moderate - yellow
              else if (precip <= 70) setPrecipColor('#f97316'); // High - orange
              else setPrecipColor('#dc2626'); // Very High - red (flash flood risk!)
            }
            
            // Weather warnings - only show once per session per type
            const warnings: Array<{ key: string; message: string; severity: 'warning' | 'error' }> = [];
            
            // FLASH FLOOD WARNING - Critical for desert oilfield workers!
            // Desert areas flood quickly due to hard-packed soil that doesn't absorb water
            if (precip >= 70 && !shownWarnings.has('flashFlood')) {
              warnings.push({
                key: 'flashFlood',
                message: `🌊 FLASH FLOOD RISK: ${precip}% chance of heavy rain. Avoid low-lying areas, arroyos, and washes. Move to high ground if water is rising!`,
                severity: 'error'
              });
            } else if (precip >= 50 && !shownWarnings.has('heavyRain')) {
              warnings.push({
                key: 'heavyRain',
                message: `🌧️ HEAVY RAIN LIKELY: ${precip}% chance. Watch for flash floods in arroyos and low areas. Do not drive through standing water.`,
                severity: 'warning'
              });
            }
            
            // Thunderstorm warning - dangerous in open desert areas
            if (thunder >= 50 && !shownWarnings.has('thunderstorm')) {
              warnings.push({
                key: 'thunderstorm',
                message: `⛈️ THUNDERSTORM WARNING: ${thunder}% chance. Seek shelter away from tall equipment, tanks, and open areas. Lightning is deadly!`,
                severity: thunder >= 70 ? 'error' : 'warning'
              });
            }
            
            // High wind warning (25+ mph)
            if (windSpeed >= 25 && !shownWarnings.has('highWind')) {
              warnings.push({
                key: 'highWind',
                message: `⚠️ HIGH WIND WARNING: ${Math.round(windSpeed)} mph winds. Secure loose equipment and materials.`,
                severity: windSpeed >= 35 ? 'error' : 'warning'
              });
            }
            
            // Extreme heat warning (100°F+)
            if (temp >= 100 && !shownWarnings.has('extremeHeat')) {
              warnings.push({
                key: 'extremeHeat',
                message: `🔥 EXTREME HEAT: ${Math.round(temp)}°F. Stay hydrated and take breaks in shade.`,
                severity: 'error'
              });
            } else if (temp >= 95 && !shownWarnings.has('heatAdvisory')) {
              warnings.push({
                key: 'heatAdvisory',
                message: `☀️ HEAT ADVISORY: ${Math.round(temp)}°F. Drink plenty of water.`,
                severity: 'warning'
              });
            }
            
            // Freezing warning (32°F or below)
            if (temp <= 32 && !shownWarnings.has('freezing')) {
              warnings.push({
                key: 'freezing',
                message: `❄️ FREEZING CONDITIONS: ${Math.round(temp)}°F. Watch for ice on roads and equipment.`,
                severity: 'warning'
              });
            }
            
            // High UV warning (8+)
            if (uv >= 8 && !shownWarnings.has('highUV')) {
              warnings.push({
                key: 'highUV',
                message: `☀️ HIGH UV INDEX: ${Math.round(uv)}. Wear sunscreen and protective clothing.`,
                severity: uv >= 11 ? 'error' : 'warning'
              });
            }
            
            // Show the first warning (queue them)
            if (warnings.length > 0) {
              const warning = warnings[0];
              setWeatherWarning({ open: true, message: warning.message, severity: warning.severity });
              setShownWarnings(prev => new Set([...prev, warning.key]));
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch weather data:', error);
      }
    };

    fetchWeatherData();
    // Refresh weather data every 5 minutes
    const interval = setInterval(fetchWeatherData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [open, userLocation, shownWarnings]);

  // Fetch AQI data for FAB display when user location is available
  useEffect(() => {
    if (!userLocation) return;

    const fetchAqiData = async () => {
      try {
        const response = await fetch(`/api/google/air-quality?lat=${userLocation.lat}&lng=${userLocation.lng}`);
        if (response.ok) {
          const data = await response.json();
          if (data.aqi !== undefined) {
            setAqiValue(data.aqi);
            // Set color based on AQI severity
            if (data.aqi <= 50) setAqiColor('#00e400'); // Good - green
            else if (data.aqi <= 100) setAqiColor('#ffff00'); // Moderate - yellow
            else if (data.aqi <= 150) setAqiColor('#ff7e00'); // Unhealthy for sensitive - orange
            else if (data.aqi <= 200) setAqiColor('#ff0000'); // Unhealthy - red
            else if (data.aqi <= 300) setAqiColor('#8f3f97'); // Very unhealthy - purple
            else setAqiColor('#7e0023'); // Hazardous - maroon
          }
        }
      } catch (error) {
        console.error('Failed to fetch AQI data:', error);
      }
    };

    fetchAqiData();
    // Refresh AQI data every 10 minutes
    const interval = setInterval(fetchAqiData, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userLocation]);

  // Fetch Pollen data for FAB display when user location is available
  useEffect(() => {
    if (!userLocation) return;

    const fetchPollenData = async () => {
      try {
        const response = await fetch(`/api/google/pollen?lat=${userLocation.lat}&lng=${userLocation.lng}`);
        if (response.ok) {
          const data = await response.json();
          if (data.overallIndex !== undefined) {
            setPollenIndex(data.overallIndex);
            // Set color based on pollen severity
            if (data.overallIndex <= 1) setPollenColor('#00e400'); // Very Low - green
            else if (data.overallIndex <= 2) setPollenColor('#a8e05f'); // Low - light green
            else if (data.overallIndex <= 3) setPollenColor('#fdd64b'); // Moderate - yellow
            else if (data.overallIndex <= 4) setPollenColor('#ff9b57'); // High - orange
            else setPollenColor('#fe6a69'); // Very High - red
          }
        }
      } catch (error) {
        console.error('Failed to fetch pollen data:', error);
      }
    };

    fetchPollenData();
    // Refresh pollen data every hour
    const interval = setInterval(fetchPollenData, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [userLocation]);

  // Fetch cop reports and display flashing markers on map
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    const fetchCopReports = async () => {
      try {
        const response = await fetch('/api/cop-reports');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.reports) {
            setCopReports(data.reports);
          }
        }
      } catch (error) {
        console.error('Failed to fetch cop reports:', error);
      }
    };

    fetchCopReports();
    // Refresh cop reports every 30 seconds for real-time updates
    const interval = setInterval(fetchCopReports, 30 * 1000);
    return () => clearInterval(interval);
  }, [mapLoaded]);

  // Create flashing cop markers on map when reports change
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded || copReports.length === 0) return;

    // Clear existing cop markers
    copMarkersRef.current.forEach(marker => marker.map = null);
    copMarkersRef.current = [];

    // Create flashing police markers for each report
    copReports.forEach((report) => {
      // Create a DOM element for the flashing cop icon
      const markerElement = document.createElement('div');
      markerElement.innerHTML = `
        <div class="cop-marker" style="
          display: flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: #dc2626;
          border: 4px solid white;
          box-shadow: 0 0 30px rgba(220, 38, 38, 1), 0 0 60px rgba(37, 99, 235, 0.8), 0 0 90px rgba(255, 255, 255, 0.6);
          animation: copFlashRWB 0.3s infinite;
          cursor: pointer;
        ">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="white">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
          </svg>
        </div>
      `;

      // Add keyframe animation if not already present
      if (!document.getElementById('cop-flash-style')) {
        const style = document.createElement('style');
        style.id = 'cop-flash-style';
        style.textContent = `
          @keyframes copFlashRWB {
            0%, 100% { 
              background: #dc2626;
              border-color: white;
              box-shadow: 0 0 40px #dc2626, 0 0 80px #dc2626;
              transform: scale(1);
            }
            33% { 
              background: white;
              border-color: #2563eb;
              box-shadow: 0 0 40px white, 0 0 80px white;
              transform: scale(1.15);
            }
            66% { 
              background: #2563eb;
              border-color: white;
              box-shadow: 0 0 40px #2563eb, 0 0 80px #2563eb;
              transform: scale(1.1);
            }
          }
          @keyframes searchMarkerPulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.2);
              opacity: 0.8;
            }
          }
        `;
        document.head.appendChild(style);
      }

      // Create the advanced marker
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: mapInstanceRef.current,
        position: { lat: report.latitude, lng: report.longitude },
        content: markerElement,
        title: `🚨 ${report.report_type.toUpperCase()} reported ${new Date(report.reported_at).toLocaleTimeString()}`,
        zIndex: 9999, // Above all other markers
      });

      // Click to show details and option to dismiss
      marker.addListener('click', () => {
        const expiresAt = new Date(report.expires_at);
        const remainingMs = expiresAt.getTime() - Date.now();
        const remainingMin = Math.ceil(remainingMs / 60000);
        
        setToast({
          open: true,
          message: `🚨 ${report.report_type.toUpperCase()} - ${report.confirmations > 0 ? `${report.confirmations + 1} reports` : 'Just reported'} - Expires in ${remainingMin} min`,
          severity: 'warning'
        });
      });

      copMarkersRef.current.push(marker);
    });

    // Cleanup function
    return () => {
      copMarkersRef.current.forEach(marker => marker.map = null);
      copMarkersRef.current = [];
    };
  }, [copReports, mapLoaded]);

  const createPoopMarkerIcon = (isGhost: boolean): google.maps.Icon => {
    const opacity = isGhost ? 0.55 : 1;
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="84" height="84" viewBox="0 0 84 84">
        <text x="42" y="57" font-size="48" text-anchor="middle" opacity="${opacity}">💩</text>
      </svg>
    `;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(60, 60),
      anchor: new google.maps.Point(30, 60),
    };
  };

  const createTacoMarkerIcon = (isGhost: boolean): google.maps.Icon => {
    const opacity = isGhost ? 0.55 : 1;
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="84" height="84" viewBox="0 0 84 84">
        <text x="42" y="57" font-size="48" text-anchor="middle" opacity="${opacity}">🌮</text>
      </svg>
    `;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(60, 60),
      anchor: new google.maps.Point(30, 60),
    };
  };

  const fetchPoopPins = useCallback(async () => {
    const map = mapInstanceRef.current;
    if (!map) {
      return;
    }

    const bounds = map.getBounds();
    if (!bounds) {
      return;
    }

    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    const centerLat = (northEast.lat() + southWest.lat()) / 2;
    const centerLng = (northEast.lng() + southWest.lng()) / 2;

    const latDiff = Math.abs(northEast.lat() - southWest.lat());
    const lngDiff = Math.abs(northEast.lng() - southWest.lng());
    const approxLatKm = latDiff * 111;
    const approxLngKm = Math.max(0.0001, Math.cos((centerLat * Math.PI) / 180)) * lngDiff * 111;
    const radiusKm = Math.max(5, Math.max(approxLatKm, approxLngKm) * 0.6);

    try {
      const pins = await poopPinService.getPinsNearby(centerLat, centerLng, radiusKm);
      setPoopPins(pins);
    } catch (error) {
      console.error('[FavoritesMapView] Failed to fetch poop pins:', error);
    }
  }, []);

  const fetchTacoPins = useCallback(async () => {
    const map = mapInstanceRef.current;
    if (!map) {
      return;
    }

    const bounds = map.getBounds();
    if (!bounds) {
      return;
    }

    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    const centerLat = (northEast.lat() + southWest.lat()) / 2;
    const centerLng = (northEast.lng() + southWest.lng()) / 2;

    const latDiff = Math.abs(northEast.lat() - southWest.lat());
    const lngDiff = Math.abs(northEast.lng() - southWest.lng());
    const approxLatKm = latDiff * 111;
    const approxLngKm = Math.max(0.0001, Math.cos((centerLat * Math.PI) / 180)) * lngDiff * 111;
    const radiusKm = Math.max(5, Math.max(approxLatKm, approxLngKm) * 0.6);

    try {
      const pins = await tacoPinService.getPinsNearby(centerLat, centerLng, radiusKm);
      setTacoPins(pins);
    } catch (error) {
      console.error('[FavoritesMapView] Failed to fetch taco pins:', error);
    }
  }, []);

  const activePoopPinCount = useMemo(() => poopPins.filter(pin => !pin.is_ghost).length, [poopPins]);
  const activeTacoPinCount = useMemo(() => tacoPins.filter(pin => !pin.is_ghost).length, [tacoPins]);

  // Fetch poop pins for current viewport whenever the map settles
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current) {
      return;
    }

    let isMounted = true;
    let fetchInProgress = false;

    const triggerFetch = async () => {
      if (!isMounted || fetchInProgress) {
        return;
      }
      fetchInProgress = true;
      try {
        await Promise.all([fetchPoopPins(), fetchTacoPins()]);
      } finally {
        fetchInProgress = false;
      }
    };

    void triggerFetch();
    const listener = mapInstanceRef.current.addListener('idle', triggerFetch);

    return () => {
      isMounted = false;
      if (listener) {
        listener.remove();
      }
    };
  }, [mapLoaded, fetchPoopPins, fetchTacoPins]);

  // Render poop pins as custom emoji markers
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current) {
      poopMarkersRef.current.forEach(marker => marker.setMap(null));
      poopMarkersRef.current = [];
      return;
    }

    const map = mapInstanceRef.current;

    poopMarkersRef.current.forEach(marker => marker.setMap(null));
    const newMarkers: google.maps.Marker[] = [];

    poopPins.forEach((pin) => {
      const marker = new google.maps.Marker({
        map,
        position: { lat: pin.latitude, lng: pin.longitude },
        icon: createPoopMarkerIcon(Boolean(pin.is_ghost)),
        title: pin.is_ghost ? '💩 Ghost restroom pin' : '💩 Restroom pin',
        zIndex: pin.is_ghost ? 900 : 1300,
      });

      marker.addListener('click', () => {
        const activeUntil = pin.active_until || pin.expires_at;
        const expiresAt = activeUntil ? new Date(activeUntil) : null;
        let message = '💩 Restroom pin';
        if (expiresAt) {
          const msRemaining = expiresAt.getTime() - Date.now();
          const hoursRemaining = Math.max(0, Math.ceil(msRemaining / (1000 * 60 * 60)));
          const descriptor = pin.is_ghost ? 'Ghost mode' : 'Active';
          message = `💩 ${descriptor} restroom - ~${hoursRemaining}h left`;
        }
        const coords = `${pin.latitude},${pin.longitude}`;
        const routeUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(coords)}&travelmode=driving`;
        const openExternalDirections = () => {
          try {
            if (window.Telegram?.WebApp?.openLink) {
              window.Telegram.WebApp.openLink(routeUrl, { try_instant_view: false });
            } else {
              window.open(routeUrl, '_blank', 'noopener');
            }
          } catch (error) {
            console.error('[FavoritesMapView] Failed to open Google Maps:', error);
            setToast({ open: true, message: '❌ Unable to open Google Maps directions.', severity: 'error' });
            return;
          }
        };

        setToast({
          open: true,
          message: `${message} - opening Google Maps...`,
          severity: pin.is_ghost ? 'info' : 'success'
        });

        openExternalDirections();
      });

      newMarkers.push(marker);
    });

    poopMarkersRef.current = newMarkers;

    return () => {
      newMarkers.forEach(marker => marker.setMap(null));
      if (poopMarkersRef.current === newMarkers) {
        poopMarkersRef.current = [];
      }
    };
  }, [poopPins, mapLoaded]);

  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current) {
      tacoMarkersRef.current.forEach(marker => marker.setMap(null));
      tacoMarkersRef.current = [];
      return;
    }

    const map = mapInstanceRef.current;

    tacoMarkersRef.current.forEach(marker => marker.setMap(null));
    const newMarkers: google.maps.Marker[] = [];

    tacoPins.forEach((pin) => {
      const marker = new google.maps.Marker({
        map,
        position: { lat: pin.latitude, lng: pin.longitude },
        icon: createTacoMarkerIcon(Boolean(pin.is_ghost)),
        title: pin.is_ghost ? '🌮 Ghost taco truck pin' : '🌮 Taco truck pin',
        zIndex: pin.is_ghost ? 900 : 1300,
      });

      marker.addListener('click', () => {
        const activeUntil = pin.active_until || pin.expires_at;
        const expiresAt = activeUntil ? new Date(activeUntil) : null;
        let message = '🌮 Taco truck pin';
        if (expiresAt) {
          const msRemaining = expiresAt.getTime() - Date.now();
          const hoursRemaining = Math.max(0, Math.ceil(msRemaining / (1000 * 60 * 60)));
          const descriptor = pin.is_ghost ? 'Ghost mode' : 'Active';
          message = `🌮 ${descriptor} taco truck - ~${hoursRemaining}h left`;
        }
        const coords = `${pin.latitude},${pin.longitude}`;
        const routeUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(coords)}&travelmode=driving`;
        const openExternalDirections = () => {
          try {
            if (window.Telegram?.WebApp?.openLink) {
              window.Telegram.WebApp.openLink(routeUrl, { try_instant_view: false });
            } else {
              window.open(routeUrl, '_blank', 'noopener');
            }
          } catch (error) {
            console.error('[FavoritesMapView] Failed to open Google Maps:', error);
            setToast({ open: true, message: '❌ Unable to open Google Maps directions.', severity: 'error' });
            return;
          }
        };

        setToast({
          open: true,
          message: `${message} - opening Google Maps...`,
          severity: pin.is_ghost ? 'info' : 'success'
        });

        openExternalDirections();
      });

      newMarkers.push(marker);
    });

    tacoMarkersRef.current = newMarkers;

    return () => {
      newMarkers.forEach(marker => marker.setMap(null));
      if (tacoMarkersRef.current === newMarkers) {
        tacoMarkersRef.current = [];
      }
    };
  }, [tacoPins, mapLoaded]);

  // Auto-cache nearby work areas for offline use when user location is available
  useEffect(() => {
    if (!userLocation) return;

    const autoCacheNearbyAreas = async () => {
      try {
        const results = await offlineMapCache.autoCacheNearby(
          userLocation.lat,
          userLocation.lng,
          // Progress callback (silent - runs in background)
          undefined,
          // Complete callback - show toast if anything was cached
          (results) => {
            const cached = results.filter(r => r.success);
            if (cached.length > 0) {
              const totalMB = cached.reduce((sum, r) => sum + r.sizeMB, 0);
              console.log(`📥 Auto-cached ${cached.length} work area(s) for offline use (${totalMB.toFixed(1)} MB)`);
              // Show subtle notification via Telegram if available
              if (window.Telegram?.WebApp?.showPopup && cached.length > 0) {
                window.Telegram.WebApp.showPopup({
                  title: '📥 Offline Maps Ready',
                  message: `Cached ${cached.length} work area(s) for offline use (${totalMB.toFixed(1)} MB)`,
                  buttons: [{ type: 'ok' }]
                });
              }
            }
          }
        );
        
        if (results.length === 0) {
          console.log('📍 No new areas to cache (already cached or not near work areas)');
        }
      } catch (error) {
        console.error('Auto-cache failed:', error);
      }
    };

    // Run auto-cache after a short delay to not block initial load
    const timeout = setTimeout(autoCacheNearbyAreas, 3000);
    return () => clearTimeout(timeout);
  }, [userLocation]);

  // Vehicle SVG definitions - oilfield themed!
  const getVehicleSvg = (type: VehicleType, color: string): string => {
    const svgs: Record<VehicleType, string> = {
      dot: '', // Handled separately as circle
      tractor: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <rect x="8" y="16" width="32" height="12" rx="3" fill="${color}" stroke="#fff" stroke-width="2"/>
        <rect x="26" y="10" width="12" height="10" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>
        <rect x="28" y="12" width="8" height="5" rx="1" fill="#1e3a5f"/>
        <circle cx="14" cy="32" r="8" fill="#333" stroke="${color}" stroke-width="3"/>
        <circle cx="34" cy="32" r="8" fill="#333" stroke="${color}" stroke-width="3"/>
        <circle cx="14" cy="32" r="4" fill="#222"/><circle cx="34" cy="32" r="4" fill="#222"/>
        <circle cx="14" cy="32" r="2" fill="${color}"/><circle cx="34" cy="32" r="2" fill="${color}"/>
      </svg>`,
      semi: `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="48" viewBox="0 0 56 48">
        <!-- Trailer -->
        <rect x="2" y="14" width="32" height="18" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>
        <line x1="10" y1="14" x2="10" y2="32" stroke="#fff" stroke-width="1" opacity="0.5"/>
        <line x1="18" y1="14" x2="18" y2="32" stroke="#fff" stroke-width="1" opacity="0.5"/>
        <line x1="26" y1="14" x2="26" y2="32" stroke="#fff" stroke-width="1" opacity="0.5"/>
        <!-- Cab -->
        <rect x="34" y="16" width="18" height="16" rx="3" fill="${color}" stroke="#fff" stroke-width="2"/>
        <rect x="42" y="18" width="8" height="8" rx="1" fill="#1e3a5f"/>
        <!-- Wheels -->
        <circle cx="10" cy="36" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="24" cy="36" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="44" cy="36" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
      </svg>`,
      welding: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <!-- Truck bed with welding rig -->
        <rect x="4" y="18" width="28" height="14" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Welding equipment boxes -->
        <rect x="6" y="20" width="8" height="10" fill="#444" stroke="${color}" stroke-width="1"/>
        <rect x="16" y="20" width="8" height="10" fill="#444" stroke="${color}" stroke-width="1"/>
        <!-- Cab -->
        <rect x="32" y="16" width="12" height="16" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>
        <rect x="34" y="18" width="8" height="6" rx="1" fill="#1e3a5f"/>
        <!-- Wheels -->
        <circle cx="12" cy="36" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="38" cy="36" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <!-- Welding torch icon -->
        <path d="M26 14 L30 10 L32 12 L28 16" stroke="#ff6b00" stroke-width="2" fill="none"/>
        <circle cx="31" cy="11" r="2" fill="#ff6b00"/>
      </svg>`,
      pickup: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <!-- Truck bed -->
        <rect x="4" y="20" width="20" height="10" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Cab -->
        <rect x="24" y="16" width="18" height="14" rx="3" fill="${color}" stroke="#fff" stroke-width="2"/>
        <rect x="28" y="18" width="12" height="7" rx="1" fill="#1e3a5f"/>
        <!-- Wheels -->
        <circle cx="12" cy="34" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="36" cy="34" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="12" cy="34" r="2" fill="${color}"/>
        <circle cx="36" cy="34" r="2" fill="${color}"/>
      </svg>`,
      company: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <!-- Company man truck - nice white pickup -->
        <rect x="4" y="20" width="18" height="10" rx="2" fill="#f0f0f0" stroke="${color}" stroke-width="2"/>
        <rect x="22" y="14" width="20" height="16" rx="3" fill="#f0f0f0" stroke="${color}" stroke-width="2"/>
        <rect x="26" y="16" width="14" height="8" rx="1" fill="#1e3a5f"/>
        <!-- Company logo/star -->
        <text x="12" y="28" font-size="8" fill="${color}" text-anchor="middle">★</text>
        <!-- Wheels -->
        <circle cx="12" cy="34" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="36" cy="34" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
      </svg>`,
      vacuum: `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="48" viewBox="0 0 56 48">
        <!-- Vacuum/Poop truck tank -->
        <ellipse cx="20" cy="24" rx="18" ry="10" fill="#8B4513" stroke="${color}" stroke-width="2"/>
        <rect x="10" y="14" width="20" height="6" fill="#8B4513" stroke="${color}" stroke-width="1"/>
        <!-- Cab -->
        <rect x="38" y="18" width="14" height="14" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>
        <rect x="40" y="20" width="10" height="6" rx="1" fill="#1e3a5f"/>
        <!-- Wheels -->
        <circle cx="10" cy="38" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="30" cy="38" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="46" cy="38" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <!-- Hose -->
        <path d="M2 20 Q6 16 10 20" stroke="#333" stroke-width="3" fill="none"/>
        <!-- Poop emoji hint -->
        <text x="20" y="28" font-size="10" text-anchor="middle">💩</text>
      </svg>`,
      rig: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="56" viewBox="0 0 48 56">
        <!-- Drilling rig derrick -->
        <polygon points="24,2 8,48 40,48" fill="none" stroke="${color}" stroke-width="3"/>
        <line x1="24" y1="2" x2="24" y2="48" stroke="${color}" stroke-width="2"/>
        <!-- Cross braces -->
        <line x1="12" y1="20" x2="36" y2="20" stroke="${color}" stroke-width="2"/>
        <line x1="14" y1="32" x2="34" y2="32" stroke="${color}" stroke-width="2"/>
        <!-- Platform -->
        <rect x="6" y="48" width="36" height="6" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Drill string -->
        <line x1="24" y1="48" x2="24" y2="56" stroke="#666" stroke-width="4"/>
      </svg>`,
      car: `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="40" viewBox="0 0 44 40">
        <!-- Car body -->
        <rect x="4" y="16" width="36" height="12" rx="4" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Roof -->
        <rect x="12" y="8" width="20" height="10" rx="3" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Windows -->
        <rect x="14" y="10" width="7" height="6" rx="1" fill="#1e3a5f"/>
        <rect x="23" y="10" width="7" height="6" rx="1" fill="#1e3a5f"/>
        <!-- Wheels -->
        <circle cx="12" cy="32" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="32" cy="32" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="12" cy="32" r="2" fill="${color}"/>
        <circle cx="32" cy="32" r="2" fill="${color}"/>
        <!-- Headlights -->
        <rect x="38" y="19" width="3" height="3" rx="1" fill="#ffff00"/>
        <rect x="3" y="19" width="3" height="3" rx="1" fill="#ff0000"/>
      </svg>`,
      safety: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="44" viewBox="0 0 48 44">
        <!-- Truck body - Safety Orange -->
        <rect x="4" y="16" width="28" height="14" rx="2" fill="#FF6B00" stroke="#fff" stroke-width="2"/>
        <!-- Cab -->
        <rect x="32" y="14" width="12" height="16" rx="2" fill="#FF6B00" stroke="#fff" stroke-width="2"/>
        <!-- Window -->
        <rect x="34" y="16" width="8" height="6" rx="1" fill="#1e3a5f"/>
        <!-- Safety Cross Symbol -->
        <rect x="14" y="19" width="12" height="4" fill="#fff"/>
        <rect x="18" y="15" width="4" height="12" fill="#fff"/>
        <!-- Wheels -->
        <circle cx="12" cy="34" r="5" fill="#333" stroke="#FF6B00" stroke-width="2"/>
        <circle cx="36" cy="34" r="5" fill="#333" stroke="#FF6B00" stroke-width="2"/>
        <circle cx="12" cy="34" r="2" fill="#FF6B00"/>
        <circle cx="36" cy="34" r="2" fill="#FF6B00"/>
        <!-- Warning lights -->
        <rect x="16" y="12" width="4" height="3" rx="1" fill="#ffff00"/>
        <rect x="22" y="12" width="4" height="3" rx="1" fill="#ffff00"/>
      </svg>`,
      water: `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="44" viewBox="0 0 56 44">
        <!-- Water tank - cylindrical -->
        <ellipse cx="18" cy="20" rx="14" ry="10" fill="#00BFFF" stroke="#fff" stroke-width="2"/>
        <rect x="4" y="10" width="28" height="20" fill="#00BFFF" stroke="#fff" stroke-width="2"/>
        <ellipse cx="18" cy="20" rx="12" ry="8" fill="#00BFFF" opacity="0.8"/>
        <!-- Water droplet symbol -->
        <path d="M18 14 L22 20 Q22 24 18 24 Q14 24 14 20 Z" fill="#fff" opacity="0.8"/>
        <!-- Cab -->
        <rect x="36" y="12" width="14" height="18" rx="2" fill="#00BFFF" stroke="#fff" stroke-width="2"/>
        <rect x="38" y="14" width="10" height="8" rx="1" fill="#1e3a5f"/>
        <!-- Wheels -->
        <circle cx="12" cy="36" r="6" fill="#333" stroke="#00BFFF" stroke-width="2"/>
        <circle cx="28" cy="36" r="6" fill="#333" stroke="#00BFFF" stroke-width="2"/>
        <circle cx="44" cy="36" r="6" fill="#333" stroke="#00BFFF" stroke-width="2"/>
      </svg>`,
      hotOiler: `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="44" viewBox="0 0 56 44">
        <!-- Hot oil tank - cylindrical -->
        <ellipse cx="18" cy="20" rx="14" ry="10" fill="#FF4500" stroke="#fff" stroke-width="2"/>
        <rect x="4" y="10" width="28" height="20" fill="#FF4500" stroke="#fff" stroke-width="2"/>
        <!-- Flame symbol -->
        <path d="M16 12 Q14 18 16 22 Q18 18 20 22 Q22 18 20 12 Q18 16 16 12" fill="#FFD700" stroke="#FF6600" stroke-width="1"/>
        <!-- HOT text -->
        <text x="18" y="28" font-size="6" fill="#fff" text-anchor="middle" font-weight="bold">HOT</text>
        <!-- Cab -->
        <rect x="36" y="12" width="14" height="18" rx="2" fill="#FF4500" stroke="#fff" stroke-width="2"/>
        <rect x="38" y="14" width="10" height="8" rx="1" fill="#1e3a5f"/>
        <!-- Wheels -->
        <circle cx="12" cy="36" r="6" fill="#333" stroke="#FF4500" stroke-width="2"/>
        <circle cx="28" cy="36" r="6" fill="#333" stroke="#FF4500" stroke-width="2"/>
        <circle cx="44" cy="36" r="6" fill="#333" stroke="#FF4500" stroke-width="2"/>
      </svg>`,
      oilTruck: `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="44" viewBox="0 0 56 44">
        <!-- Oil tank - cylindrical black -->
        <ellipse cx="18" cy="20" rx="14" ry="10" fill="#1a1a1a" stroke="#fff" stroke-width="2"/>
        <rect x="4" y="10" width="28" height="20" fill="#1a1a1a" stroke="#fff" stroke-width="2"/>
        <!-- Oil barrel symbol -->
        <rect x="14" y="16" width="8" height="10" rx="1" fill="#333" stroke="#fff" stroke-width="1"/>
        <line x1="14" y1="19" x2="22" y2="19" stroke="#fff" stroke-width="1"/>
        <line x1="14" y1="23" x2="22" y2="23" stroke="#fff" stroke-width="1"/>
        <!-- Cab -->
        <rect x="36" y="12" width="14" height="18" rx="2" fill="#1a1a1a" stroke="#fff" stroke-width="2"/>
        <rect x="38" y="14" width="10" height="8" rx="1" fill="#1e3a5f"/>
        <!-- Wheels -->
        <circle cx="12" cy="36" r="6" fill="#333" stroke="#666" stroke-width="2"/>
        <circle cx="28" cy="36" r="6" fill="#333" stroke="#666" stroke-width="2"/>
        <circle cx="44" cy="36" r="6" fill="#333" stroke="#666" stroke-width="2"/>
      </svg>`,
      monster: `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <!-- Monster truck body - lifted high -->
        <rect x="12" y="14" width="32" height="14" rx="3" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Cab with roll cage -->
        <rect x="28" y="6" width="14" height="12" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>
        <rect x="30" y="8" width="10" height="6" rx="1" fill="#1e3a5f"/>
        <!-- Roll cage bars -->
        <line x1="28" y1="6" x2="24" y2="14" stroke="#fff" stroke-width="2"/>
        <line x1="42" y1="6" x2="46" y2="14" stroke="#fff" stroke-width="2"/>
        <!-- Massive wheels -->
        <circle cx="16" cy="42" r="12" fill="#333" stroke="${color}" stroke-width="3"/>
        <circle cx="40" cy="42" r="12" fill="#333" stroke="${color}" stroke-width="3"/>
        <!-- Wheel detail - treads -->
        <circle cx="16" cy="42" r="6" fill="#222"/>
        <circle cx="40" cy="42" r="6" fill="#222"/>
        <circle cx="16" cy="42" r="3" fill="${color}"/>
        <circle cx="40" cy="42" r="3" fill="${color}"/>
        <!-- Suspension -->
        <line x1="16" y1="28" x2="16" y2="32" stroke="#666" stroke-width="3"/>
        <line x1="40" y1="28" x2="40" y2="32" stroke="#666" stroke-width="3"/>
      </svg>`,
      electric: `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="44" viewBox="0 0 56 44">
        <!-- Electric truck body - sleek design -->
        <rect x="4" y="14" width="32" height="16" rx="4" fill="#00D4AA" stroke="#fff" stroke-width="2"/>
        <!-- Cab - modern aerodynamic -->
        <rect x="36" y="12" width="16" height="18" rx="4" fill="#00D4AA" stroke="#fff" stroke-width="2"/>
        <rect x="40" y="14" width="10" height="8" rx="2" fill="#1e3a5f"/>
        <!-- Lightning bolt symbol -->
        <path d="M18 16 L14 22 L18 22 L16 28 L24 20 L20 20 L22 16 Z" fill="#FFD700" stroke="#fff" stroke-width="1"/>
        <!-- Battery indicator -->
        <rect x="6" y="26" width="12" height="3" rx="1" fill="#333"/>
        <rect x="7" y="26.5" width="10" height="2" rx="0.5" fill="#00FF00"/>
        <!-- Wheels - aero covers -->
        <circle cx="12" cy="36" r="6" fill="#333" stroke="#00D4AA" stroke-width="2"/>
        <circle cx="28" cy="36" r="6" fill="#333" stroke="#00D4AA" stroke-width="2"/>
        <circle cx="46" cy="36" r="6" fill="#333" stroke="#00D4AA" stroke-width="2"/>
        <circle cx="12" cy="36" r="3" fill="#00D4AA"/>
        <circle cx="28" cy="36" r="3" fill="#00D4AA"/>
        <circle cx="46" cy="36" r="3" fill="#00D4AA"/>
      </svg>`,
      vapor: `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="48" viewBox="0 0 56 48">
        <!-- Vapor recovery truck tank -->
        <ellipse cx="18" cy="22" rx="14" ry="10" fill="#9370DB" stroke="#fff" stroke-width="2"/>
        <rect x="4" y="12" width="28" height="20" fill="#9370DB" stroke="#fff" stroke-width="2"/>
        <!-- Vapor collection dome -->
        <ellipse cx="18" cy="10" rx="6" ry="4" fill="#9370DB" stroke="#fff" stroke-width="2"/>
        <path d="M14 10 Q18 4 22 10" fill="none" stroke="#fff" stroke-width="2"/>
        <!-- Vapor swirls -->
        <path d="M12 6 Q14 2 16 6" stroke="#fff" stroke-width="1.5" fill="none" opacity="0.7"/>
        <path d="M18 4 Q20 0 22 4" stroke="#fff" stroke-width="1.5" fill="none" opacity="0.7"/>
        <!-- VRU text -->
        <text x="18" y="26" font-size="6" fill="#fff" text-anchor="middle" font-weight="bold">VRU</text>
        <!-- Cab -->
        <rect x="36" y="14" width="14" height="18" rx="2" fill="#9370DB" stroke="#fff" stroke-width="2"/>
        <rect x="38" y="16" width="10" height="8" rx="1" fill="#1e3a5f"/>
        <!-- Wheels -->
        <circle cx="12" cy="40" r="6" fill="#333" stroke="#9370DB" stroke-width="2"/>
        <circle cx="28" cy="40" r="6" fill="#333" stroke="#9370DB" stroke-width="2"/>
        <circle cx="46" cy="40" r="6" fill="#333" stroke="#9370DB" stroke-width="2"/>
      </svg>`,
      crane: `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <!-- Crane truck base -->
        <rect x="4" y="32" width="48" height="12" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Cab -->
        <rect x="36" y="26" width="14" height="18" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>
        <rect x="38" y="28" width="10" height="8" rx="1" fill="#1e3a5f"/>
        <!-- Crane arm base/turret -->
        <circle cx="20" cy="30" r="8" fill="#666" stroke="${color}" stroke-width="2"/>
        <!-- Crane boom -->
        <line x1="20" y1="28" x2="8" y2="6" stroke="${color}" stroke-width="4"/>
        <line x1="20" y1="28" x2="8" y2="6" stroke="#fff" stroke-width="2"/>
        <!-- Boom sections -->
        <line x1="16" y1="20" x2="12" y2="12" stroke="#fff" stroke-width="1"/>
        <!-- Cable -->
        <line x1="8" y1="6" x2="8" y2="18" stroke="#333" stroke-width="1.5"/>
        <!-- Hook -->
        <path d="M6 18 Q4 20 6 22 Q10 22 8 18" stroke="#333" stroke-width="2" fill="none"/>
        <!-- Outriggers -->
        <line x1="8" y1="44" x2="4" y2="52" stroke="${color}" stroke-width="3"/>
        <line x1="48" y1="44" x2="52" y2="52" stroke="${color}" stroke-width="3"/>
        <rect x="2" y="50" width="6" height="4" fill="#333"/>
        <rect x="48" y="50" width="6" height="4" fill="#333"/>
        <!-- Wheels -->
        <circle cx="16" cy="48" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="40" cy="48" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
      </svg>`,
      sandTank: `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="48" viewBox="0 0 56 48">
        <!-- Sand tank - V-bottom hopper -->
        <path d="M4 10 L32 10 L32 24 L24 36 L12 36 L4 24 Z" fill="#C2B280" stroke="#fff" stroke-width="2"/>
        <!-- Tank top hatch -->
        <rect x="14" y="6" width="8" height="6" rx="1" fill="#A09060" stroke="#fff" stroke-width="1"/>
        <!-- Sand texture dots -->
        <circle cx="12" cy="18" r="1" fill="#A09060"/>
        <circle cx="18" cy="16" r="1" fill="#A09060"/>
        <circle cx="24" cy="18" r="1" fill="#A09060"/>
        <circle cx="16" cy="24" r="1" fill="#A09060"/>
        <circle cx="20" cy="22" r="1" fill="#A09060"/>
        <!-- FRAC SAND text -->
        <text x="18" y="32" font-size="5" fill="#fff" text-anchor="middle" font-weight="bold">SAND</text>
        <!-- Discharge chute -->
        <rect x="16" y="36" width="4" height="4" fill="#666"/>
        <!-- Cab -->
        <rect x="36" y="14" width="14" height="18" rx="2" fill="#C2B280" stroke="#fff" stroke-width="2"/>
        <rect x="38" y="16" width="10" height="8" rx="1" fill="#1e3a5f"/>
        <!-- Wheels -->
        <circle cx="12" cy="42" r="5" fill="#333" stroke="#C2B280" stroke-width="2"/>
        <circle cx="28" cy="42" r="5" fill="#333" stroke="#C2B280" stroke-width="2"/>
        <circle cx="46" cy="42" r="5" fill="#333" stroke="#C2B280" stroke-width="2"/>
      </svg>`,
      raceCar: `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="36" viewBox="0 0 52 36">
        <!-- Race car body - low sleek -->
        <path d="M8 18 L16 12 L40 12 L48 18 L48 24 L4 24 L4 18 Z" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Windshield -->
        <path d="M18 12 L24 8 L32 8 L36 12" fill="#1e3a5f" stroke="#fff" stroke-width="1"/>
        <!-- Racing stripes -->
        <line x1="8" y1="18" x2="48" y2="18" stroke="#fff" stroke-width="2"/>
        <!-- Number circle -->
        <circle cx="26" cy="20" r="4" fill="#fff"/>
        <text x="26" y="22" font-size="6" fill="${color}" text-anchor="middle" font-weight="bold">1</text>
        <!-- Spoiler -->
        <rect x="2" y="14" width="2" height="8" fill="${color}"/>
        <rect x="0" y="12" width="6" height="2" fill="${color}"/>
        <!-- Rear wing -->
        <rect x="48" y="10" width="2" height="12" fill="#333"/>
        <rect x="46" y="8" width="6" height="2" fill="#333"/>
        <!-- Wheels -->
        <circle cx="14" cy="28" r="6" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="38" cy="28" r="6" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="14" cy="28" r="2" fill="${color}"/>
        <circle cx="38" cy="28" r="2" fill="${color}"/>
        <!-- Exhaust flames (optional flair) -->
        <path d="M2 20 L-2 18 L0 20 L-2 22 L2 20" fill="#FF4500"/>
      </svg>`,
      motorcycle: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="44" viewBox="0 0 48 44">
        <!-- Motorcycle frame -->
        <path d="M16 24 L28 18 L36 18 L40 24" fill="none" stroke="${color}" stroke-width="3"/>
        <!-- Gas tank -->
        <ellipse cx="26" cy="18" rx="6" ry="4" fill="${color}" stroke="#fff" stroke-width="1"/>
        <!-- Seat -->
        <path d="M30 16 Q34 14 38 18" fill="${color}" stroke="#fff" stroke-width="1"/>
        <!-- Engine block -->
        <rect x="22" y="22" width="8" height="6" rx="1" fill="#444" stroke="${color}" stroke-width="1"/>
        <!-- Exhaust pipes -->
        <path d="M30 26 Q36 28 40 32" stroke="#666" stroke-width="2" fill="none"/>
        <path d="M30 28 Q34 30 38 34" stroke="#666" stroke-width="2" fill="none"/>
        <!-- Front fork -->
        <line x1="16" y1="24" x2="12" y2="34" stroke="#666" stroke-width="3"/>
        <!-- Rear swing arm -->
        <line x1="32" y1="26" x2="38" y2="34" stroke="#666" stroke-width="3"/>
        <!-- Handlebars -->
        <line x1="14" y1="16" x2="20" y2="20" stroke="${color}" stroke-width="2"/>
        <line x1="10" y1="14" x2="18" y2="14" stroke="${color}" stroke-width="2"/>
        <!-- Headlight -->
        <circle cx="12" cy="20" r="3" fill="#ffff00" stroke="${color}" stroke-width="1"/>
        <!-- Wheels -->
        <circle cx="12" cy="36" r="7" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="38" cy="36" r="7" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="12" cy="36" r="3" fill="#222"/>
        <circle cx="38" cy="36" r="3" fill="#222"/>
        <!-- Spokes hint -->
        <circle cx="12" cy="36" r="1" fill="${color}"/>
        <circle cx="38" cy="36" r="1" fill="${color}"/>
      </svg>`,
      eagle: `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="48" viewBox="0 0 56 48">
        <!-- Eagle soaring - majestic bird -->
        <!-- Body -->
        <ellipse cx="28" cy="24" rx="8" ry="12" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Head -->
        <circle cx="28" cy="12" r="6" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Beak -->
        <path d="M28 10 L32 8 L28 6" fill="#FFD700" stroke="#fff" stroke-width="1"/>
        <!-- Eye -->
        <circle cx="30" cy="10" r="1.5" fill="#1e3a5f"/>
        <!-- Left wing extended -->
        <path d="M20 20 Q4 16 2 24 Q4 28 8 26 Q12 24 16 22 L20 20" fill="${color}" stroke="#fff" stroke-width="2"/>
        <path d="M8 26 L4 32" stroke="#fff" stroke-width="1.5"/>
        <path d="M12 24 L10 30" stroke="#fff" stroke-width="1.5"/>
        <path d="M16 22 L16 28" stroke="#fff" stroke-width="1.5"/>
        <!-- Right wing extended -->
        <path d="M36 20 Q52 16 54 24 Q52 28 48 26 Q44 24 40 22 L36 20" fill="${color}" stroke="#fff" stroke-width="2"/>
        <path d="M48 26 L52 32" stroke="#fff" stroke-width="1.5"/>
        <path d="M44 24 L46 30" stroke="#fff" stroke-width="1.5"/>
        <path d="M40 22 L40 28" stroke="#fff" stroke-width="1.5"/>
        <!-- Tail feathers -->
        <path d="M28 36 L24 44 L28 42 L32 44 L28 36" fill="${color}" stroke="#fff" stroke-width="1.5"/>
        <!-- Talons -->
        <path d="M24 32 L22 38 M26 32 L26 38" stroke="#333" stroke-width="2"/>
        <path d="M30 32 L30 38 M32 32 L34 38" stroke="#333" stroke-width="2"/>
      </svg>`,
      fracking: `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="56" viewBox="0 0 64 56">
        <!-- Fracking unit - hydraulic fracturing equipment -->
        <!-- Pump truck base -->
        <rect x="4" y="38" width="56" height="14" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Cab -->
        <rect x="48" y="32" width="14" height="20" rx="2" fill="${color}" stroke="#fff" stroke-width="2"/>
        <rect x="50" y="34" width="10" height="8" rx="1" fill="#1e3a5f"/>
        <!-- Pump skid/equipment -->
        <rect x="8" y="24" width="36" height="14" rx="2" fill="#666" stroke="${color}" stroke-width="2"/>
        <!-- Pumps/motors (3 units) -->
        <rect x="10" y="26" width="10" height="10" rx="1" fill="#444" stroke="${color}" stroke-width="1"/>
        <rect x="22" y="26" width="10" height="10" rx="1" fill="#444" stroke="${color}" stroke-width="1"/>
        <rect x="34" y="26" width="8" height="10" rx="1" fill="#444" stroke="${color}" stroke-width="1"/>
        <!-- Control panel -->
        <rect x="10" y="16" width="12" height="6" rx="1" fill="#00D4AA" stroke="#fff" stroke-width="1"/>
        <circle cx="14" cy="18" r="1" fill="#0F0"/>
        <circle cx="18" cy="18" r="1" fill="#FF0"/>
        <!-- High pressure hose reels -->
        <circle cx="8" cy="18" r="4" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="42" cy="18" r="4" fill="#333" stroke="${color}" stroke-width="2"/>
        <!-- Hoses/lines -->
        <path d="M8 22 Q4 28 2 34" stroke="#333" stroke-width="3" fill="none"/>
        <path d="M42 22 Q46 28 48 34" stroke="#333" stroke-width="3" fill="none"/>
        <!-- Pressure gauge symbols -->
        <circle cx="26" cy="18" r="3" fill="#1e3a5f" stroke="${color}" stroke-width="1"/>
        <line x1="26" y1="18" x2="26" y2="15" stroke="${color}" stroke-width="1"/>
        <!-- FRAC text -->
        <text x="26" y="32" font-size="6" fill="#fff" text-anchor="middle" font-weight="bold">FRAC</text>
        <!-- Wheels -->
        <circle cx="14" cy="54" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="28" cy="54" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="42" cy="54" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
        <circle cx="56" cy="54" r="5" fill="#333" stroke="${color}" stroke-width="2"/>
      </svg>`,
      drilling: `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="64" viewBox="0 0 52 64">
        <!-- Drilling rig - full scale oilfield rig -->
        <!-- Substructure/base -->
        <rect x="4" y="52" width="44" height="10" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Rig floor/platform -->
        <rect x="8" y="42" width="36" height="10" fill="${color}" stroke="#fff" stroke-width="2"/>
        <!-- Drawworks house -->
        <rect x="12" y="32" width="12" height="10" rx="1" fill="#666" stroke="${color}" stroke-width="2"/>
        <!-- Derrick structure - pyramid/tower -->
        <polygon points="26,4 12,42 40,42" fill="none" stroke="${color}" stroke-width="3"/>
        <line x1="26" y1="4" x2="26" y2="42" stroke="${color}" stroke-width="2"/>
        <!-- Cross braces -->
        <line x1="16" y1="18" x2="36" y2="18" stroke="${color}" stroke-width="1.5"/>
        <line x1="14" y1="28" x2="38" y2="28" stroke="${color}" stroke-width="1.5"/>
        <line x1="13" y1="36" x2="39" y2="36" stroke="${color}" stroke-width="1.5"/>
        <!-- Crown block at top -->
        <rect x="22" y="2" width="8" height="4" fill="#666" stroke="${color}" stroke-width="1"/>
        <!-- Traveling block/hook -->
        <rect x="23" y="24" width="6" height="4" fill="#333" stroke="${color}" stroke-width="1"/>
        <line x1="26" y1="28" x2="26" y2="32" stroke="#666" stroke-width="2"/>
        <!-- Drill string -->
        <rect x="24" y="42" width="4" height="20" fill="#666" stroke="#333" stroke-width="1"/>
        <!-- Kelly/rotary table -->
        <rect x="22" y="40" width="8" height="4" fill="#333" stroke="${color}" stroke-width="1"/>
        <!-- Mud pumps -->
        <rect x="32" y="48" width="8" height="6" rx="1" fill="#444" stroke="${color}" stroke-width="1"/>
        <rect x="32" y="56" width="8" height="4" rx="1" fill="#444" stroke="${color}" stroke-width="1"/>
        <!-- Pipe rack -->
        <rect x="4" y="46" width="6" height="6" fill="#666" stroke="${color}" stroke-width="1"/>
        <line x1="4" y1="48" x2="10" y2="48" stroke="#fff" stroke-width="1"/>
        <!-- Catwalk -->
        <rect x="40" y="48" width="8" height="2" fill="${color}" stroke="#fff" stroke-width="1"/>
        <!-- Guy wires -->
        <line x1="26" y1="4" x2="2" y2="32" stroke="#666" stroke-width="1" opacity="0.5"/>
        <line x1="26" y1="4" x2="50" y2="32" stroke="#666" stroke-width="1" opacity="0.5"/>
      </svg>`,
      roadrunner: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <!-- Road Runner Bird - New Mexico State Bird! Beep Beep! -->
        <!-- Body -->
        <ellipse cx="22" cy="28" rx="12" ry="7" fill="${color}" stroke="#fff" stroke-width="1.5"/>
        <!-- Head -->
        <circle cx="36" cy="22" r="6" fill="${color}" stroke="#fff" stroke-width="1.5"/>
        <!-- Crest/Mohawk feathers - iconic road runner look -->
        <path d="M38 16 L40 8 M36 15 L38 9 M34 16 L36 11" stroke="${color}" stroke-width="2.5" stroke-linecap="round"/>
        <!-- Beak - long and pointy -->
        <polygon points="42,22 52,20 42,24" fill="#FF8C00" stroke="#fff" stroke-width="1"/>
        <!-- Eye -->
        <circle cx="38" cy="20" r="2.5" fill="#fff"/>
        <circle cx="39" cy="20" r="1.2" fill="#000"/>
        <!-- Tail feathers - long and streaked -->
        <path d="M10 26 L2 22 M10 28 L1 26 M10 30 L2 32 M10 32 L4 36" stroke="${color}" stroke-width="2.5" stroke-linecap="round"/>
        <!-- Legs - long runner legs -->
        <line x1="18" y1="35" x2="16" y2="44" stroke="#FF8C00" stroke-width="2.5"/>
        <line x1="26" y1="35" x2="28" y2="44" stroke="#FF8C00" stroke-width="2.5"/>
        <!-- Speed lines - zooming! -->
        <line x1="0" y1="24" x2="6" y2="24" stroke="${color}" stroke-width="2" opacity="0.7"/>
        <line x1="2" y1="28" x2="8" y2="28" stroke="${color}" stroke-width="2" opacity="0.7"/>
        <line x1="0" y1="32" x2="6" y2="32" stroke="${color}" stroke-width="2" opacity="0.7"/>
      </svg>`,
    };
    return svgs[type] || '';
  };

  const loadGlobalRoutePhotos = useCallback(async () => {
    setIsLoadingRoutePhotos(true);
    try {
      const bounds = mapInstanceRef.current?.getBounds();
      const b = bounds
        ? {
            north: bounds.getNorthEast().lat(),
            south: bounds.getSouthWest().lat(),
            east: bounds.getNorthEast().lng(),
            west: bounds.getSouthWest().lng(),
          }
        : undefined;
      const photos = await routePhotoService.getGlobalRoutePhotos(b, 80);
      setRoutePhotoFeed(photos);
    } catch (error) {
      console.error('Failed to load route photos', error);
      setRoutePhotoFeed([]);
    } finally {
      setIsLoadingRoutePhotos(false);
    }
  }, []);

  // Vehicle names for display
  const vehicleInfo: Record<VehicleType, { name: string }> = {
    dot: { name: 'Green Dot' },
    tractor: { name: 'Tractor' },
    semi: { name: 'Semi Truck' },
    welding: { name: 'Welding Rig' },
    pickup: { name: 'Pickup' },
    company: { name: 'Company Man' },
    vacuum: { name: 'Vacuum Truck' },
    rig: { name: 'Drilling Rig' },
    car: { name: 'Car' },
    safety: { name: 'Safety Truck' },
    water: { name: 'Water Hauler' },
    hotOiler: { name: 'Hot Oiler' },
    oilTruck: { name: 'Oil Truck' },
    monster: { name: 'Monster Truck' },
    electric: { name: 'Electric Truck' },
    vapor: { name: 'Vapor Recovery' },
    crane: { name: 'Crane' },
    sandTank: { name: 'Sand Tank' },
    raceCar: { name: 'Race Car' },
    motorcycle: { name: 'Motorcycle' },
    eagle: { name: 'Eagle' },
    fracking: { name: 'Fracking Unit' },
    drilling: { name: 'Drilling Rig' },
    roadrunner: { name: 'Road Runner' },
  };
  
  // Get MUI icon for vehicle type
  const getVehicleMuiIcon = (type: VehicleType, size: number = 32, selected: boolean = false) => {
    const color = selected ? cyber.bgPrimary : cyber.neonGreen;
    const sx = { fontSize: size, color };
    switch (type) {
      case 'dot': return <MyLocationIcon sx={sx} />;
      case 'tractor': return <AgricultureIcon sx={sx} />;
      case 'semi': return <LocalShippingIcon sx={sx} />;
      case 'welding': return <FireTruckIcon sx={sx} />;
      case 'pickup': return <LocalShippingIcon sx={{ ...sx, fontSize: size - 4 }} />;
      case 'company': return <DirectionsCarIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#ffffff' }} />;
      case 'vacuum': return <LocalShippingIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#8B4513' }} />;
      case 'rig': return <OilBarrelIcon sx={sx} />;
      case 'car': return <DirectionsCarIcon sx={sx} />;
      case 'safety': return <HealthAndSafetyIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#FF6B00' }} />;
      case 'water': return <LocalShippingIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#00BFFF' }} />;
      case 'hotOiler': return <LocalShippingIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#FF4500' }} />;
      case 'oilTruck': return <LocalShippingIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#1a1a1a' }} />;
      case 'monster': return <LocalShippingIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#FF1493' }} />;
      case 'electric': return <ElectricCarIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#00D4AA' }} />;
      case 'vapor': return <AirIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#9370DB' }} />;
      case 'crane': return <PrecisionManufacturingIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#FFD700' }} />;
      case 'sandTank': return <LocalShippingIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#C2B280' }} />;
      case 'raceCar': return <SportsScoreIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#FF0000' }} />;
      case 'motorcycle': return <TwoWheelerIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#FF6600' }} />;
      case 'eagle': return <AirIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#FFD700' }} />;
      case 'fracking': return <OilBarrelIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#FF4500' }} />;
      case 'drilling': return <ConstructionIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#8B4513' }} />;
      case 'roadrunner': return <DirectionsRunIcon sx={{ ...sx, color: selected ? cyber.bgPrimary : '#00CED1' }} />;
      default: return <MyLocationIcon sx={sx} />;
    }
  };

  // Update user marker icon when vehicle type changes
  useEffect(() => {
    if (!userMarkerRef.current || !mapInstanceRef.current) return;
    
    if (userVehicle === 'dot') {
      userMarkerRef.current.setIcon({
        path: google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: cyber.neonGreen,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      });
      userMarkerRef.current.setTitle('Your Location (tap to change vehicle)');
    } else {
      const svg = getVehicleSvg(userVehicle, cyber.neonGreen);
      const svgUrl = `data:image/svg+xml,${encodeURIComponent(svg)}`;
      const size = userVehicle === 'semi' || userVehicle === 'vacuum' ? 56 : userVehicle === 'rig' ? 48 : 48;
      const height = userVehicle === 'rig' ? 56 : userVehicle === 'car' ? 40 : 48;
      
      userMarkerRef.current.setIcon({
        url: svgUrl,
        scaledSize: new google.maps.Size(size, height),
        anchor: new google.maps.Point(size / 2, height * 0.7),
      });
      userMarkerRef.current.setTitle(`${vehicleInfo[userVehicle].name}`);
    }
  }, [userVehicle, cyber.neonGreen]);

  // Keyboard shortcuts for map controls (simplified - no panning, just zoom and shortcuts)
  useEffect(() => {
    if (!open) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      const map = mapInstanceRef.current;
      if (!map) return;
      
      // Get current center and zoom
      const center = map.getCenter();
      const zoom = map.getZoom() || 10;
      
      switch (e.key.toLowerCase()) {
        // Zoom controls - +/- or =/- keys
        case '+':
        case '=':
          e.preventDefault();
          map.setZoom(Math.min(zoom + 1, 21));
          telegram.hapticFeedback('light');
          break;
        case '-':
        case '_':
          e.preventDefault();
          map.setZoom(Math.max(zoom - 1, 1));
          telegram.hapticFeedback('light');
          break;
          
        // Add route photo - P key
        case 'p':
          e.preventDefault();
          if (center) {
            setRoutePhotoLocation({ lat: center.lat(), lng: center.lng() });
            setShowCommunityRoutes(true);
            telegram.hapticFeedback('light');
          }
          break;
          
        // Close/Escape
        case 'escape':
          if (showCommunityRoutes) {
            setShowCommunityRoutes(false);
            setRoutePhotoLocation(null);
          } else if (contextMenu) {
            setContextMenu(null);
          } else if (selectedWell) {
            setSelectedWell(null);
          }
          break;
          
        // Map style toggle - M key
        case 'm':
          e.preventDefault();
          setMapStyle(prev => {
            const styles: MapStyleType[] = ['cyber', 'satellite', 'terrain', 'night', 'highcontrast', 'hybrid', 'desert', 'roads', 'topo'];
            const currentIdx = styles.indexOf(prev);
            return styles[(currentIdx + 1) % styles.length];
          });
          telegram.hapticFeedback('light');
          break;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, showCommunityRoutes, contextMenu, selectedWell]);

  // Map facility from API to Well type
  const mapFacilityToWell = (facility: Record<string, unknown>): Well => ({
    id: String(facility.id || facility.api || ''),
    api: String(facility.api || facility.api_number || ''),
    wellName: String(facility.name || facility.well_name || facility.wellName || 'Unknown'),
    operator: String(facility.operator || 'Unknown'),
    latitude: Number(facility.latitude) || 0,
    longitude: Number(facility.longitude) || 0,
    status: String(facility.status || 'Unknown'),
    basin: String(facility.basin || 'Unknown'),
    county: String(facility.county || 'Unknown'),
    state: String(facility.state || 'NM'),
    type: String(facility.type || facility.facility_type || 'well'),
    // Preserve facilityType for specific facility types like "Tank Battery - (TB)"
    facilityType: facility.facilityType ? String(facility.facilityType) : undefined,
    distance: facility.distance ? Number(facility.distance) : undefined,
  });

  // Handle search for all facility types
  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      // Clear search result markers when search is cleared
      setSearchResultMarkers([]);
      return;
    }
    
    // Clear previous search result markers when starting new search
    setSearchResultMarkers([]);
    
    setIsSearching(true);
    setSearchError(null);
    
    try {
      // Build search URL - search ALL facility types
      let url = `/api/facilities/search?type=all&query=${encodeURIComponent(query.trim())}`;
      
      // Add location for distance sorting if available
      if (mapInstanceRef.current) {
        const center = mapInstanceRef.current.getCenter();
        if (center) {
          url += `&lat=${center.lat()}&lng=${center.lng()}`;
        }
      }
      
      console.log('🔍 [FAVORITES MAP] Searching facilities:', url);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.facilities) {
        const results = data.facilities.map(mapFacilityToWell);
        console.log('✅ [FAVORITES MAP] Found', results.length, 'facilities');
        setSearchResults(results);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchError(error instanceof Error ? error.message : 'Search failed');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Fetch address predictions as user types
  const fetchAddressPredictions = useCallback(async (input: string) => {
    if (input.length < 3) {
      setAddressPredictions([]);
      return;
    }
    
    setIsLoadingAddresses(true);
    try {
      let url = `/api/google/autocomplete?input=${encodeURIComponent(input)}&types=geocode`;
      
      // Bias to user location if available
      if (userLocation) {
        url += `&lat=${userLocation.lat}&lng=${userLocation.lng}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Autocomplete failed');
      
      const data = await response.json();
      setAddressPredictions(data.predictions || []);
    } catch (error) {
      console.error('Address autocomplete error:', error);
      setAddressPredictions([]);
    } finally {
      setIsLoadingAddresses(false);
    }
  }, [userLocation]);
  
  // Debounced address search
  const handleSearchInputChange = (value: string) => {
    setSearchQuery(value);
    
    // Clear previous debounce timer
    if (addressSearchDebounce.current) {
      clearTimeout(addressSearchDebounce.current);
    }
    
    // If in address mode, fetch predictions with debounce
    if (searchMode === 'address' && value.length >= 3) {
      addressSearchDebounce.current = setTimeout(() => {
        fetchAddressPredictions(value);
      }, 300);
    } else {
      setAddressPredictions([]);
    }
  };
  
  // Select an address prediction and navigate to it
  const handleSelectAddress = async (prediction: AddressPrediction) => {
    try {
      setIsSearching(true);
      
      // Use geocode API to get coordinates from place ID
      const response = await fetch(`/api/google/geocode?placeId=${prediction.placeId}`);
      if (!response.ok) throw new Error('Geocode failed');
      
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        const { lat, lng } = result.location;
        
        // Pan map to location
        if (mapInstanceRef.current) {
          mapInstanceRef.current.panTo({ lat, lng });
          mapInstanceRef.current.setZoom(15); // Zoom in for address level
          
          // Add a temporary marker (only if marker library is loaded)
          if (google.maps.marker?.AdvancedMarkerElement) {
            new google.maps.marker.AdvancedMarkerElement({
              map: mapInstanceRef.current,
              position: { lat, lng },
              title: prediction.mainText,
            });
          }
          
          telegram.hapticFeedback('success');
          setToast({
            open: true,
            message: `📍 Navigated to ${prediction.mainText}`,
            severity: 'success'
          });
        }
        
        // Close dialog
        setShowSearchDialog(false);
        setSearchQuery('');
        setAddressPredictions([]);
      }
    } catch (error) {
      console.error('Geocode error:', error);
      setSearchError('Failed to navigate to address');
    } finally {
      setIsSearching(false);
    }
  };

  // Handle voice search
  const toggleVoiceSearch = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      // Fallback: focus the text input to trigger keyboard (Android mic button)
      const input = document.querySelector('#search-facilities-input') as HTMLInputElement;
      if (input) {
        input.focus();
        telegram.hapticFeedback('warning');
      }
      return;
    }

    if (isVoiceListening && voiceRecognitionRef.current) {
      // Stop listening
      voiceRecognitionRef.current.stop();
      setIsVoiceListening(false);
      telegram.hapticFeedback('light');
      return;
    }

    // Start voice recognition
    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      recognition.onstart = () => {
        setIsVoiceListening(true);
        telegram.hapticFeedback('medium');
      };
      
      recognition.onresult = (event: any) => {
        const results = event.results;
        const lastResult = results[results.length - 1];
        const transcript = lastResult[0].transcript;
        
        setSearchQuery(transcript);
        
        if (lastResult.isFinal) {
          // Auto-search when final result received
          setTimeout(() => {
            handleSearch(transcript);
          }, 300);
        }
      };
      
      recognition.onerror = (event: any) => {
        console.error('Voice search error:', event.error);
        setIsVoiceListening(false);
        telegram.hapticFeedback('error');
      };
      
      recognition.onend = () => {
        setIsVoiceListening(false);
      };
      
      voiceRecognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      console.error('Failed to start voice recognition:', error);
      telegram.hapticFeedback('error');
    }
  };

  // Handle selecting a search result - zoom to it and show spotlight animation (no duplicate marker)
  const handleSelectSearchResult = (well: Well) => {
    if (!mapInstanceRef.current) return;
    
    // Center map and zoom to medium level (16) to show facility + surrounding area
    mapInstanceRef.current.setCenter({ lat: well.latitude, lng: well.longitude });
    mapInstanceRef.current.setZoom(16);
    
    // Create temporary spotlight/pulse effect that fades after 3 seconds
    // This draws attention to the existing marker without adding a duplicate
    const spotlightCircle = new google.maps.Circle({
      center: { lat: well.latitude, lng: well.longitude },
      radius: 50, // 50 meters
      map: mapInstanceRef.current,
      fillColor: '#00ffff',
      fillOpacity: 0.4,
      strokeColor: '#00ffff',
      strokeWeight: 3,
      strokeOpacity: 0.8,
      zIndex: 3000,
    });
    
    // Animate the spotlight - pulse 3 times then fade out
    let pulseCount = 0;
    const maxPulses = 3;
    const pulseInterval = setInterval(() => {
      pulseCount++;
      // Pulse effect: expand and contract
      const baseRadius = 50;
      const expandedRadius = 80;
      const isExpanded = pulseCount % 2 === 1;
      spotlightCircle.setRadius(isExpanded ? expandedRadius : baseRadius);
      spotlightCircle.setOptions({
        fillOpacity: isExpanded ? 0.2 : 0.4,
        strokeOpacity: isExpanded ? 0.5 : 0.8,
      });
      
      if (pulseCount >= maxPulses * 2) {
        clearInterval(pulseInterval);
        // Fade out
        let opacity = 0.4;
        const fadeInterval = setInterval(() => {
          opacity -= 0.1;
          if (opacity <= 0) {
            clearInterval(fadeInterval);
            spotlightCircle.setMap(null);
          } else {
            spotlightCircle.setOptions({
              fillOpacity: opacity,
              strokeOpacity: opacity * 2,
            });
          }
        }, 100);
      }
    }, 400);
    
    // Auto-add to favorites for quick access later
    addFavorite(well);
    setToast({ open: true, message: `⭐ ${well.wellName} added to favorites!` });
    
    // Select the well and close search dialog
    setSelectedWell(well);
    setShowSearchDialog(false);
    telegram.hapticFeedback('medium');
  };

  // Helper: Calculate distance between two points in km (Haversine formula)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Helper: Find caliche routes that end near a destination (within 1km)
  const findNearbyCalichemRoutes = (destLat: number, destLng: number, maxDistanceKm: number = 1): any[] => {
    if (!calichemRoutesData || calichemRoutesData.length === 0) return [];
    
    return calichemRoutesData.filter((route: any) => {
      if (!route.landmarks || route.landmarks.length < 2) return false;
      
      // Get the last landmark (destination) of the route
      const sortedLandmarks = [...route.landmarks].sort((a: any, b: any) => a.sequence_order - b.sequence_order);
      const lastLandmark = sortedLandmarks[sortedLandmarks.length - 1];
      
      // Check distance to our destination
      const distance = calculateDistance(destLat, destLng, lastLandmark.latitude, lastLandmark.longitude);
      return distance <= maxDistanceKm;
    });
  };

  // Show route to selected well
  const showRouteToWell = async (well: Well, useCalichemWaypoints?: { lat: number; lng: number }[]) => {
    if (!mapInstanceRef.current || !window.google) return;
    
    setIsLoadingRoute(true);
    telegram.hapticFeedback('light');
    
    try {
      // Get user's current location
      const location = await NavigationService.getCurrentLocation();
      
      if (!location) {
        telegram.showAlert('📍 Location Access Required\n\nTo show route, please:\n1. Open Telegram Settings\n2. Allow location access for Mini Apps\n3. Or enable browser location permissions');
        setIsLoadingRoute(false);
        return;
      }
      
      setUserLocation(location);
      
      // Check for nearby caliche routes if we weren't given waypoints already
      // This helps users route via pavement first, then use known caliche roads
      if (!useCalichemWaypoints) {
        const nearbyRoutes = findNearbyCalichemRoutes(well.latitude, well.longitude, 1.5); // 1.5km radius
        if (nearbyRoutes.length > 0) {
          // Found a caliche route - offer to use it
          const route = nearbyRoutes[0]; // Use first matching route
          const sortedLandmarks = [...route.landmarks].sort((a: any, b: any) => a.sequence_order - b.sequence_order);
          
          // Show confirmation dialog
          const useCaliche = await new Promise<boolean>((resolve) => {
            telegram.showConfirm(
              `🛤️ Caliche Route Found!\n\n"${route.name}"\n\nUse this community route? It routes via pavement first, then guides you through the caliche roads.`,
              (confirmed) => resolve(confirmed)
            );
          });
          
          if (useCaliche) {
            // Extract waypoints from landmarks (skip first if it's start, use the turns)
            const waypoints = sortedLandmarks
              .filter((lm: any) => lm.landmark_type !== 'start' && lm.landmark_type !== 'end')
              .map((lm: any) => ({ lat: lm.latitude, lng: lm.longitude }));
            
            // Recurse with the waypoints
            setIsLoadingRoute(false);
            return showRouteToWell(well, waypoints);
          }
        }
      }
      
      // Create DirectionsService and DirectionsRenderer
      const directionsService = new google.maps.DirectionsService();
      
      // Clear previous route
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
      }
      clearNavigationMarkers();
      
      // Create new renderer with cyberpunk styling
      const hasCalichemWaypoints = useCalichemWaypoints && useCalichemWaypoints.length > 0;
      const directionsRenderer = new google.maps.DirectionsRenderer({
        map: mapInstanceRef.current,
        suppressMarkers: true, // We'll add custom markers
        polylineOptions: {
          // Orange for heavy haul, Green for caliche route, Cyan for normal
          strokeColor: hasCalichemWaypoints ? '#4ade80' : (heavyHaulMode ? '#f97316' : cyber.neonCyan),
          strokeWeight: hasCalichemWaypoints ? 7 : (heavyHaulMode ? 8 : 6),
          strokeOpacity: 0.9,
          geodesic: true,
        }
      });
      directionsRendererRef.current = directionsRenderer;
      
      // Build waypoints for DirectionsService if we have caliche route
      const googleWaypoints = useCalichemWaypoints?.slice(0, 23).map(wp => ({ // Google supports max 23 waypoints
        location: new google.maps.LatLng(wp.lat, wp.lng),
        stopover: false // Pass through, don't stop
      })) || [];
      
      // Request directions with heavy haul options if enabled
      // Heavy haul mode: avoids highways (for oversized loads that need permits),
      // avoids tolls (many don't accept commercial/oversized), and requests alternate routes
      const result = await directionsService.route({
        origin: { lat: location.lat, lng: location.lng },
        destination: { lat: well.latitude, lng: well.longitude },
        travelMode: google.maps.TravelMode.DRIVING,
        waypoints: googleWaypoints.length > 0 ? googleWaypoints : undefined,
        optimizeWaypoints: false, // Keep our specified order for caliche routes
        avoidHighways: heavyHaulMode, // Oversized loads often can't use highways without permits
        avoidTolls: heavyHaulMode, // Many toll roads don't accommodate oversized/hazmat
        provideRouteAlternatives: heavyHaulMode, // Get alternatives for manual selection
      });
      
      // Display the route (first route, or let user select if alternatives)
      directionsRenderer.setDirections(result);
      setIngressOrigin({ lat: location.lat, lng: location.lng });
      setIngressWaypoints(useCalichemWaypoints || []);
      // Add Material 3 markers for start/destination and caliche waypoints
      if (mapInstanceRef.current && result.routes?.[0]?.legs?.[0]) {
        const leg = result.routes[0].legs[0];
        const addNavMarker = (pos: google.maps.LatLng | google.maps.LatLngLiteral, type: LandmarkType, z: number) => {
          const icon = buildMaterialLandmarkIcon(type, {
            isStart: type === 'start',
            isDestination: type === 'destination',
            size: 64
          });
          const marker = new google.maps.Marker({
            position: pos,
            map: mapInstanceRef.current!,
            icon,
            zIndex: z
          });
          navigationMarkersRef.current.push(marker);
        };

        addNavMarker(leg.start_location, 'start', 7000);
        addNavMarker(leg.end_location, 'destination', 7000);

        if (useCalichemWaypoints && useCalichemWaypoints.length) {
          useCalichemWaypoints.forEach((wp, idx) => {
            addNavMarker({ lat: wp.lat, lng: wp.lng }, 'waypoint', 6990 - idx);
          });
        }
      }
      const firstInstruction = result.routes?.[0]?.legs?.[0]?.steps?.[0]?.instructions || '';
      const plainFirstStep = firstInstruction.replace(/<[^>]*>/g, '').trim();
      setIngressConnectorLabel(plainFirstStep || 'Main road');
      
      // Add user location marker - clickable to open vehicle selection menu
      if (userMarkerRef.current) {
        userMarkerRef.current.setMap(null);
      }
      
      const getUserIcon = () => {
        if (userVehicle === 'dot') {
          return {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: cyber.neonGreen,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          };
        }
        const svg = getVehicleSvg(userVehicle, cyber.neonGreen);
        const svgUrl = `data:image/svg+xml,${encodeURIComponent(svg)}`;
        const size = userVehicle === 'semi' || userVehicle === 'vacuum' ? 56 : userVehicle === 'rig' ? 48 : 48;
        const height = userVehicle === 'rig' ? 56 : userVehicle === 'car' ? 40 : 48;
        return {
          url: svgUrl,
          scaledSize: new google.maps.Size(size, height),
          anchor: new google.maps.Point(size / 2, height * 0.7),
        };
      };
      
      userMarkerRef.current = new google.maps.Marker({
        position: { lat: location.lat, lng: location.lng },
        map: mapInstanceRef.current,
        icon: getUserIcon(),
        title: userVehicle === 'dot' ? 'Your Location (tap to change vehicle)' : `${vehicleInfo[userVehicle].name}`,
        zIndex: 1000,
      });
      
      // Add click handler to open vehicle selection menu
      userMarkerRef.current.addListener('click', () => {
        setShowVehicleMenu(true);
        telegram.hapticFeedback('light');
      });
      
      // Extract route info
      const route = result.routes[0];
      const leg = route.legs[0];
      
      setRouteInfo({
        distance: leg.distance?.text || 'Unknown',
        duration: leg.duration?.text || 'Unknown',
        summary: hasCalichemWaypoints 
          ? `🛤️ Via Caliche Route` 
          : (route.summary || 'Route calculated'),
      });

      // Extract turn-by-turn steps for in-app navigation
      if (leg.steps) {
        const steps = leg.steps.map(step => ({
          instructions: step.instructions?.replace(/<[^>]*>/g, '') || '', // Strip HTML tags
          distance: step.distance?.text || '',
          duration: step.duration?.text || '',
          maneuver: step.maneuver || undefined,
        }));
        setNavigationSteps(steps);
        setNavigationDestination(well);
      }
      
      // Extract route path for elevation profile
      if (route.overview_path) {
        const pathCoords = route.overview_path.map((point: google.maps.LatLng) => ({
          lat: point.lat(),
          lng: point.lng()
        }));
        setElevationPath(pathCoords);
      }
      
      // Fit map to show entire route
      const bounds = new google.maps.LatLngBounds();
      bounds.extend({ lat: location.lat, lng: location.lng });
      bounds.extend({ lat: well.latitude, lng: well.longitude });
      mapInstanceRef.current.fitBounds(bounds, { top: 50, right: 80, bottom: 250, left: 20 });
      
      telegram.hapticFeedback('success');
      
      // Show confirmation toast
      if (hasCalichemWaypoints) {
        setToast({ open: true, message: '🛤️ Route via caliche road - pavement first!', severity: 'success' });
      }
      
    } catch (error) {
      console.error('[FavoritesMapView] Route error:', error);
      telegram.showAlert('❌ Could not calculate route.\n\nPlease try again.');
      setRouteInfo(null);
    } finally {
      setIsLoadingRoute(false);
    }
  };

  // Clear displayed route
  const clearRoute = () => {
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
      directionsRendererRef.current = null;
    }
    if (userMarkerRef.current) {
      userMarkerRef.current.setMap(null);
      userMarkerRef.current = null;
    }
    setRouteInfo(null);
    setUserLocation(null);
    setNavigationSteps([]);
    setCurrentStepIndex(0);
    setNavigationDestination(null);
    setIngressOrigin(null);
    setIngressWaypoints([]);
    setIngressConnectorLabel('Main road');
    telegram.hapticFeedback('light');
  };

  // Start turn-by-turn navigation mode
  const startNavigation = () => {
    if (navigationSteps.length === 0) {
      telegram.showAlert('📍 Please show route first before starting navigation.');
      return;
    }
    
    setIsNavigating(true);
    setCurrentStepIndex(0);
    telegram.hapticFeedback('success');
    
    // Start watching user's location for live updates
    if (navigator.geolocation) {
      navigationWatchId.current = navigator.geolocation.watchPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(newLocation);

          storeExternalFix({
            lat: newLocation.lat,
            lng: newLocation.lng,
            accuracy:
              typeof position.coords.accuracy === 'number'
                ? Math.max(position.coords.accuracy, 0)
                : null,
            timestamp: position.timestamp,
            source: 'browser-watch',
          });
          
          // Update user marker position
          if (userMarkerRef.current) {
            userMarkerRef.current.setPosition(newLocation);
          }
          
          // Center map on user during navigation
          if (mapInstanceRef.current && isNavigating) {
            mapInstanceRef.current.panTo(newLocation);
          }
        },
        (error) => {
          console.error('[Navigation] Location watch error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    }
    
    // Close the modal after successfully starting navigation
    setSelectedWell(null);
    
    // Show toast confirmation
    setToast({
      open: true,
      message: '🧭 Navigation started',
      severity: 'success'
    });
  };

  // Stop navigation mode
  const stopNavigation = () => {
    setIsNavigating(false);
    
    // Stop watching location
    if (navigationWatchId.current !== null) {
      navigator.geolocation.clearWatch(navigationWatchId.current);
      navigationWatchId.current = null;
    }
    clearNavigationMarkers();
    
    telegram.hapticFeedback('light');
  };

  const openRoutePhotosModal = async () => {
    setShowRoutePhotosModal(true);
    await loadGlobalRoutePhotos();
  };

  // Build reverse route back to connector road or to original origin using stored waypoints
  const buildBackRoute = async (mode: 'road' | 'start') => {
    if (!mapInstanceRef.current || !window.google) return;
    setIsLoadingRoute(true);
    try {
      const location = await NavigationService.getCurrentLocation();
      if (!location) {
        telegram.showAlert('📍 Location required to route back out. Please enable location and try again.');
        setIsLoadingRoute(false);
        return;
      }

      const origin = { lat: location.lat, lng: location.lng };
      let destination: { lat: number; lng: number } | null = null;
      let backWaypoints: Array<{ lat: number; lng: number }> = [];

      if (ingressWaypoints.length > 0) {
        if (mode === 'road') {
          destination = ingressWaypoints[0];
          backWaypoints = ingressWaypoints.slice(1).reverse();
        } else {
          destination = ingressOrigin || ingressWaypoints[0];
          backWaypoints = ingressWaypoints.slice().reverse();
        }
      } else if (ingressOrigin) {
        destination = ingressOrigin;
      } else if (navigationDestination) {
        destination = { lat: navigationDestination.latitude, lng: navigationDestination.longitude };
      }

      if (!destination) {
        telegram.showAlert('No back-out route available yet. Start a route first.');
        setIsLoadingRoute(false);
        return;
      }

      // Clear previous renderer
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
      }
      clearNavigationMarkers();

      const directionsService = new google.maps.DirectionsService();
      const directionsRenderer = new google.maps.DirectionsRenderer({
        map: mapInstanceRef.current,
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: '#22d3ee',
          strokeWeight: 7,
          strokeOpacity: 0.95,
        },
      });
      directionsRendererRef.current = directionsRenderer;

      const googleWaypoints = backWaypoints.slice(0, 23).map(wp => ({
        location: new google.maps.LatLng(wp.lat, wp.lng),
        stopover: false,
      }));

      const result = await directionsService.route({
        origin,
        destination,
        travelMode: google.maps.TravelMode.DRIVING,
        waypoints: googleWaypoints.length ? googleWaypoints : undefined,
        optimizeWaypoints: false,
      });

      directionsRenderer.setDirections(result);

      const route = result.routes[0];
      const leg = route.legs[0];

      // Markers for back route (current location to exit/start)
      if (mapInstanceRef.current) {
        const addNavMarker = (pos: google.maps.LatLng | google.maps.LatLngLiteral, type: LandmarkType, z: number) => {
          const icon = buildMaterialLandmarkIcon(type, {
            isStart: type === 'start',
            isDestination: type === 'destination',
            size: 64
          });
          const marker = new google.maps.Marker({
            position: pos,
            map: mapInstanceRef.current!,
            icon,
            zIndex: z
          });
          navigationMarkersRef.current.push(marker);
        };

        addNavMarker(origin, 'start', 7000);
        addNavMarker(destination, 'destination', 7000);
        backWaypoints.forEach((wp, idx) => addNavMarker(wp, 'waypoint', 6990 - idx));
      }

      setRouteInfo({
        distance: leg.distance?.text || 'Unknown',
        duration: leg.duration?.text || 'Unknown',
        summary: mode === 'road'
          ? `↩ Back to Road${ingressConnectorLabel ? ` (${ingressConnectorLabel})` : ''}`
          : '↩ Back to Start',
      });

      if (leg.steps) {
        const steps = leg.steps.map(step => ({
          instructions: step.instructions?.replace(/<[^>]*>/g, '') || '',
          distance: step.distance?.text || '',
          duration: step.duration?.text || '',
          maneuver: step.maneuver || undefined,
        }));
        setNavigationSteps(steps);
        setNavigationDestination(navigationDestination);
      }

      // Fit bounds to new route
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(origin);
      bounds.extend(destination);
      mapInstanceRef.current.fitBounds(bounds, { top: 50, right: 80, bottom: 250, left: 20 });

      telegram.hapticFeedback('success');
      setToast({
        open: true,
        message: mode === 'road' ? '↩ Back to road route ready' : '↩ Back to start route ready',
        severity: 'success',
      });
    } catch (error) {
      console.error('[FavoritesMapView] Back route error:', error);
      telegram.showAlert('❌ Could not build back-out route. Try again.');
    } finally {
      setIsLoadingRoute(false);
    }
  };

  // Navigate to next/previous step
  const nextStep = () => {
    if (currentStepIndex < navigationSteps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
      telegram.hapticFeedback('light');
    }
  };

  const prevStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      telegram.hapticFeedback('light');
    }
  };

  // Cleanup navigation watch on unmount
  useEffect(() => {
    return () => {
      if (navigationWatchId.current !== null) {
        navigator.geolocation.clearWatch(navigationWatchId.current);
      }
    };
  }, []);

  // Load caliche routes from API
  const loadCalichemRoutes = async () => {
    setIsLoadingRoutes(true);
    try {
      const userId = telegram.getUserId();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (userId) {
        headers['X-Telegram-User-Id'] = userId;
        headers['X-Telegram-Username'] = telegram.getUserName() || '';
      }

      const response = await fetch('/api/routes-v2?tab=public&limit=50', { headers });
      const data = await response.json();
      
      if (data.routes && Array.isArray(data.routes)) {
        const formattedRoutes = data.routes.map((r: any) => ({
          id: r.id,
          name: r.name,
          coordinates: Array.isArray(r.coordinates) ? r.coordinates.map((c: any) => 
            Array.isArray(c) ? { lat: c[0], lng: c[1] } : c
          ) : [],
          waypointCount: r.actual_waypoint_count || r.waypoint_count || 0,
          destinationName: r.destination_name,
          createdByUsername: r.created_by_username,
          forkCount: r.fork_count || 0,
        }));
        setCalicheRoutes(formattedRoutes);
      }
    } catch (error) {
      console.error('[FavoritesMapView] Failed to load caliche routes:', error);
    } finally {
      setIsLoadingRoutes(false);
    }
  };

  const distanceMiles = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Earth radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Draw route overlays on map
  const drawRouteOverlays = () => {
    const map = mapInstanceRef.current;
    if (!map || !showRouteOverlays || calicheRoutes.length === 0) return;

    // Clear existing overlays
    clearRouteOverlays();

    // Draw each route
    calicheRoutes.forEach((route, index) => {
      if (!route.coordinates || route.coordinates.length < 2) return;

      // Cyberpunk color palette for routes
      const colors = [
        { stroke: '#00f0ff', glow: '#00f0ff' }, // Cyan
        { stroke: '#f0f', glow: '#f0f' },       // Magenta
        { stroke: '#0ff', glow: '#0ff' },       // Aqua
        { stroke: '#ff00ff', glow: '#ff00ff' }, // Pink
        { stroke: '#00ff88', glow: '#00ff88' }, // Green
        { stroke: '#ffaa00', glow: '#ffaa00' }, // Orange
      ];
      const color = colors[index % colors.length];

      // Create polyline with cyberpunk glow effect
      const polyline = new google.maps.Polyline({
        path: route.coordinates,
        strokeColor: color.stroke,
        strokeOpacity: 0.9,
        strokeWeight: 4,
        map: map,
        zIndex: 500,
      });

      // Add click listener to show route info
      polyline.addListener('click', () => {
        telegram.hapticFeedback('light');
        const infoContent = `
          <div style="padding: 8px; min-width: 180px; font-family: 'Google Sans Flex', system-ui;">
            <div style="font-weight: 700; font-size: 14px; color: ${color.stroke}; margin-bottom: 4px;">
              🛣️ ${route.name}
            </div>
            ${route.destinationName ? `<div style="font-size: 12px; color: #666; margin-bottom: 4px;">🏁 To: ${route.destinationName}</div>` : ''}
            <div style="font-size: 11px; color: #888;">
              📍 ${route.waypointCount} waypoints
              ${route.forkCount > 0 ? ` • 🍴 ${route.forkCount} forks` : ''}
            </div>
            ${route.createdByUsername ? `<div style="font-size: 11px; color: #888; margin-top: 4px;">👤 @${route.createdByUsername}</div>` : ''}
          </div>
        `;
        
        const infoWindow = new google.maps.InfoWindow({
          content: infoContent,
          position: route.coordinates[Math.floor(route.coordinates.length / 2)],
        });
        infoWindow.open(map);
      });

      routePolylinesRef.current.push(polyline);

      // Add start marker
      if (route.coordinates.length > 0) {
        const startMarker = new google.maps.Marker({
          position: route.coordinates[0],
          map: map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: color.stroke,
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
          },
          title: `Start: ${route.name}`,
          zIndex: 501,
        });
        routeMarkersRef.current.push(startMarker);
      }

      // Add end marker (destination)
      if (route.coordinates.length > 1) {
        const endMarker = new google.maps.Marker({
          position: route.coordinates[route.coordinates.length - 1],
          map: map,
          icon: {
            path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
            scale: 6,
            fillColor: color.stroke,
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
            rotation: 0,
          },
          title: route.destinationName || `End: ${route.name}`,
          zIndex: 502,
        });
        routeMarkersRef.current.push(endMarker);
      }
    });
  };

  // Clear route overlays
  const clearRouteOverlays = () => {
    routePolylinesRef.current.forEach(p => p.setMap(null));
    routePolylinesRef.current = [];
    routeMarkersRef.current.forEach(m => m.setMap(null));
    routeMarkersRef.current = [];
  };

  // Toggle route overlays
  const toggleRouteOverlays = async () => {
    telegram.hapticFeedback('light');
    
    if (showRouteOverlays) {
      // Hide overlays
      clearRouteOverlays();
      setShowRouteOverlays(false);
    } else {
      // Show overlays - load if needed
      setShowRouteOverlays(true);
      if (calicheRoutes.length === 0) {
        await loadCalichemRoutes();
      }
    }
  };

  // Effect to draw/clear overlays when state changes
  useEffect(() => {
    if (showRouteOverlays && mapInstanceRef.current && calicheRoutes.length > 0) {
      drawRouteOverlays();
    } else if (!showRouteOverlays) {
      clearRouteOverlays();
    }
  }, [showRouteOverlays, calicheRoutes]);

  // Reset map state when dialog closes so it re-initializes on next open
  useEffect(() => {
    if (!open) {
      console.log('[FavoritesMapView] Modal closed, resetting map state');
      // Cleanup markers
      markersRef.current.forEach(m => m.setMap(null));
      markersRef.current = [];
      // Cleanup basin layer
      if (basinLayerCleanupRef.current) {
        basinLayerCleanupRef.current();
        basinLayerCleanupRef.current = null;
      }
      // Cleanup displayed route
      if (displayedRouteRef.current) {
        displayedRouteRef.current.setMap(null);
        displayedRouteRef.current = null;
      }
      routeWaypointsRef.current.forEach(m => m.setMap(null));
      routeWaypointsRef.current = [];
      // Cleanup directions renderer
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
        directionsRendererRef.current = null;
      }
      // Cleanup user marker
      if (userMarkerRef.current) {
        userMarkerRef.current.setMap(null);
        userMarkerRef.current = null;
      }
      // Cleanup route overlays
      clearRouteOverlays();
      setShowRouteOverlays(false);
      setCalicheRoutes([]);
      // Reset route state
      setRouteInfo(null);
      setUserLocation(null);
      // Reset map instance so it re-creates on next open
      mapInstanceRef.current = null;
      hasInitialBoundsRef.current = false;
      setMapLoaded(false);
      setContainerReady(false);
      return;
    }

    // Single unified initialization when dialog opens
    console.log('[FavoritesMapView] Dialog opened, starting initialization...');
    let cancelled = false;
    let initAttempts = 0;
    const maxInitAttempts = 100; // 10 seconds max

    const tryInitialize = () => {
      if (cancelled) return;
      initAttempts++;
      
      // Check if Google Maps API is available
      if (!window.google?.maps?.Map) {
        console.log(`[FavoritesMapView] Attempt ${initAttempts}: Google Maps not ready yet`);
        if (initAttempts < maxInitAttempts) {
          setTimeout(tryInitialize, 100);
        } else {
          console.error('[FavoritesMapView] ❌ Google Maps failed to load after 10 seconds');
        }
        return;
      }

      // Check if container ref is available
      if (!mapRef.current) {
        console.log(`[FavoritesMapView] Attempt ${initAttempts}: mapRef not available yet`);
        if (initAttempts < maxInitAttempts) {
          setTimeout(tryInitialize, 100);
        }
        return;
      }

      // Check container dimensions
      const width = mapRef.current.offsetWidth;
      const height = mapRef.current.offsetHeight;
      console.log(`[FavoritesMapView] Attempt ${initAttempts}: Container dimensions ${width}x${height}`);
      
      if (width === 0 || height === 0) {
        if (initAttempts < maxInitAttempts) {
          setTimeout(tryInitialize, 100);
        }
        return;
      }

      // Already initialized?
      if (mapInstanceRef.current) {
        console.log('[FavoritesMapView] Map already initialized, skipping');
        setMapLoaded(true);
        setContainerReady(true);
        return;
      }

      // All conditions met - initialize the map!
      console.log('[FavoritesMapView] 🚀 All conditions met, creating map...');
      
      const center = favorites.length > 0
        ? {
            lat: favorites.reduce((sum, w) => sum + w.latitude, 0) / favorites.length,
            lng: favorites.reduce((sum, w) => sum + w.longitude, 0) / favorites.length
          }
        : { lat: 32.5, lng: -104.5 };

      try {
        const map = new google.maps.Map(mapRef.current, {
          center,
          zoom: favorites.length === 1 ? 12 : 8,
          mapTypeId: 'hybrid',
          gestureHandling: 'greedy',
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          // Enable 3D tilt and rotation controls
          tilt: 0,
          heading: 0,
          rotateControl: true,
        });

        mapInstanceRef.current = map;
        setMapLoaded(true);
        setContainerReady(true);
        console.log('[FavoritesMapView] ✅ Map created successfully!');

        // Register zoom callback for hazard notifications
        setZoomCallback((lat: number, lng: number, zoom?: number) => {
          if (mapInstanceRef.current) {
            mapInstanceRef.current.panTo({ lat, lng });
            if (zoom) {
              mapInstanceRef.current.setZoom(zoom);
            }
          }
        });

        // Add right-click handler for adding route photos
        map.addListener('rightclick', (e: google.maps.MapMouseEvent) => {
          console.log('[FavoritesMapView] Right-click detected:', e);
          if (e.latLng) {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            // Use domEvent for reliable screen coordinates
            const domEvent = (e as any).domEvent;
            const mouseX = domEvent?.clientX || domEvent?.pageX || window.innerWidth / 2;
            const mouseY = domEvent?.clientY || domEvent?.pageY || window.innerHeight / 2;
            console.log('[FavoritesMapView] Opening context menu at:', { mouseX, mouseY, lat, lng });
            setContextMenu({ mouseX, mouseY, lat, lng });
          }
        });

        // Dismiss InfoWindow and reset mobile tap state when clicking on map
        map.addListener('click', () => {
          // Close InfoWindow tooltip when clicking on map
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
          }
          // Reset mobile tap state
          lastTappedWellIdRef.current = null;
        });

        // Initialize drawing manager (only if drawing library is loaded)
        try {
          if (google.maps.drawing && google.maps.drawing.DrawingManager) {
            const drawingManager = new google.maps.drawing.DrawingManager({
              drawingMode: null,
              drawingControl: false,
              polylineOptions: {
                strokeColor: '#60a5fa',
                strokeOpacity: 0.7,
                strokeWeight: 4,
                editable: true,
                draggable: true
              },
              markerOptions: {
                icon: 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png',
                draggable: true
              }
            });
            drawingManager.setMap(map);
            drawingManagerRef.current = drawingManager;
            
            google.maps.event.addListener(drawingManager, 'polylinecomplete', (polyline: google.maps.Polyline) => {
              polylineRef.current = polyline;
              drawingManager.setDrawingMode(null);
              setIsDrawing(false);
              
              // Extract points from the drawn polyline
              const path = polyline.getPath();
              const rawPoints: Array<{ lat: number; lng: number }> = [];
              for (let i = 0; i < path.getLength(); i++) {
                const point = path.getAt(i);
                rawPoints.push({ lat: point.lat(), lng: point.lng() });
              }
              
              // Store original count for user feedback
              setOriginalPointCount(rawPoints.length);
              
              // Simplify the polyline using Douglas-Peucker algorithm
              // Tolerance of ~10 meters helps clean up hand-drawn routes
              const simplified = simplifyPolyline(rawPoints, 0.0001);
              
              console.log(`[FavoritesMapView] Route simplified: ${rawPoints.length} → ${simplified.length} points`);
              
              // Update the polyline on the map with simplified path
              const simplifiedPath = simplified.map(p => new google.maps.LatLng(p.lat, p.lng));
              polyline.setPath(simplifiedPath);
              
              // Store simplified points and show save dialog
              setDrawnRoutePoints(simplified);
              setShowSaveRouteDialog(true);
              telegram.hapticFeedback('success');
            });
            console.log('[FavoritesMapView] ✅ Drawing manager initialized');
          } else {
            console.warn('[FavoritesMapView] Drawing library not loaded - route drawing disabled');
          }
        } catch (e) {
          console.warn('[FavoritesMapView] Drawing manager failed:', e);
        }

        // Add basin layer
        addBasinLayer(map).then((cleanup) => {
          basinLayerCleanupRef.current = cleanup;
        }).catch(() => {});

      } catch (error) {
        console.error('[FavoritesMapView] ❌ Map creation failed:', error);
      }
    };

    // Start trying after a brief delay for dialog animation
    const startTimer = setTimeout(tryInitialize, 200);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (basinLayerCleanupRef.current) {
        basinLayerCleanupRef.current();
        basinLayerCleanupRef.current = null;
      }
    };
  }, [open, favorites]);

  // Clean up displayed routes when well is deselected
  useEffect(() => {
    if (!selectedWell) {
      // Clear displayed route
      if (displayedRouteRef.current) {
        displayedRouteRef.current.setMap(null);
        displayedRouteRef.current = null;
      }
      routeWaypointsRef.current.forEach(m => m.setMap(null));
      routeWaypointsRef.current = [];
    }
  }, [selectedWell]);

  // Separate effect to manage markers when favorites or filters change
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || favorites.length === 0) {
      console.log('[FavoritesMapView] Marker effect: skipping - map not ready or no favorites. map:', !!mapInstanceRef.current, 'mapLoaded:', mapLoaded, 'fav count:', favorites.length);
      return;
    }

    console.log('[FavoritesMapView] 🎯 RENDERING MARKERS for', favorites.length, 'favorites', 'layer:', currentFacilityLayer);

    // Clear old markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    // Filter favorites based on current facility layer
    // This only filters within favorites - does NOT fetch from API
    const filteredFavorites = (currentFacilityLayer === 'all' || currentFacilityLayer === 'favorites')
      ? favorites 
      : favorites.filter(well => {
          const wellAny = well as any;
          const wellType = (wellAny.type || '').toLowerCase();
          const facilityTypeCode = getFacilityTypeCode(wellAny.type, wellAny.facilityType).toLowerCase();
          
          // Handle main types
          if (currentFacilityLayer === 'well') return wellType === 'well' || wellType === 'wells';
          if (currentFacilityLayer === 'swd') return wellType === 'swd' || wellType === 'swds';
          if (currentFacilityLayer === 'rig') return wellType === 'rig' || wellType === 'rigs';
          
          // For specific facility type codes, match the extracted code
          if (facilityTypeCode === currentFacilityLayer.toLowerCase()) return true;
          
          // Generic facility catch-all
          if (currentFacilityLayer === 'facility') return wellType === 'facility' && !['tb', 'fs', 'cs', 'gp', 'gs', 'cp', 'rf', 'tp', 'inj', 'plg', 'plc', 'plw', 'ggs', 'mgs', 'wh', 'bw', 'pit', 'bgt', 'wm', 'rfl', 'rm', 'ab', 'sc', 'ci'].includes(facilityTypeCode);
          
          return false;
        });

    // Create markers for filtered favorites with sane coordinate guards
    const newMarkers: google.maps.Marker[] = [];
    
    filteredFavorites.forEach((well, idx) => {
      try {
        const lat = Number(well.latitude);
        const lng = Number(well.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          console.warn('[FavoritesMapView] Skipping marker due to invalid coords', well.wellName, well.latitude, well.longitude);
          return;
        }

        // Get facility type code for icon (handles well, swd, rig, and facility subtypes)
        const wellAny = well as any;
        const typeCode = getFacilityTypeCode(wellAny.type, wellAny.facilityType);
        const isSelected = selectedWell?.id === well.id;
        
        console.log('[FavoritesMapView] Adding marker:', well.wellName, 'type:', wellAny.type, 'facilityType:', wellAny.facilityType, 'code:', typeCode);
        
        const marker = new google.maps.Marker({
          position: { lat, lng },
          map: mapInstanceRef.current,
          title: well.wellName,
          // Custom facility-type icon matching Quick Search buttons
          icon: createMarkerIcon(typeCode, isSelected, { cyber, darkMode }),
          // Force above overlays/data layers
          zIndex: isSelected ? 2000 : 1000 + idx,
          // Render as DOM overlay to avoid canvas compositing hiding pins
          optimized: false,
          // Start non-draggable, enable after long-press
          draggable: false,
          // Drop animation with staggered delay
          animation: google.maps.Animation.DROP,
        });

        // Stagger the drop animation for a cascading effect
        if (idx > 0) {
          marker.setAnimation(null); // Start without animation
          // Delay animation based on index for cascade effect
          setTimeout(() => {
            marker.setAnimation(google.maps.Animation.DROP);
          }, idx * 100); // 100ms delay between each pin
        }

        // Store original position for snap-back
        const originalPosition = { lat, lng };

        // Create InfoWindow for hover tooltip (shared instance created once)
        if (!infoWindowRef.current) {
          infoWindowRef.current = new google.maps.InfoWindow({
            disableAutoPan: true,
            pixelOffset: new google.maps.Size(0, -45),
          });

          infoWindowRef.current.addListener('closeclick', () => {
            hoverPinnedRef.current = false;
            hoveredMarkerRef.current = null;
            cancelPendingTooltipClose();
          });

          infoWindowRef.current.addListener('domready', () => {
            const tooltipElement = document.querySelector<HTMLDivElement>('[data-map-tooltip="true"]');
            if (!tooltipElement) {
              return;
            }

            const handleMouseEnter = () => {
              hoverPinnedRef.current = true;
              cancelPendingTooltipClose();
            };

            const handleMouseLeave = () => {
              hoverPinnedRef.current = false;
              scheduleTooltipClose(220);
            };

            tooltipElement.addEventListener('mouseenter', handleMouseEnter);
            tooltipElement.addEventListener('mouseleave', handleMouseLeave);

            const closeButton = tooltipElement.querySelector<HTMLButtonElement>('[data-tooltip-close="true"]');
            if (closeButton) {
              closeButton.addEventListener('click', () => {
                hoverPinnedRef.current = false;
                cancelPendingTooltipClose();
                infoWindowRef.current?.close();
              });
            }

            const copyButtons = tooltipElement.querySelectorAll<HTMLButtonElement>('[data-copy-value]');
            copyButtons.forEach((button) => {
              if (button.disabled) return;
              const copyText = button.getAttribute('data-copy-text') ?? '';
              const label = button.getAttribute('data-copy-label') ?? button.textContent ?? 'Copy';
              if (!copyText) return;

              button.addEventListener('click', async () => {
                let success = false;
                try {
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(copyText);
                    success = true;
                  } else {
                    const temp = document.createElement('textarea');
                    temp.value = copyText;
                    temp.setAttribute('readonly', '');
                    temp.style.position = 'absolute';
                    temp.style.left = '-9999px';
                    document.body.appendChild(temp);
                    temp.select();
                    success = document.execCommand('copy');
                    document.body.removeChild(temp);
                  }
                } catch (err) {
                  console.warn('[FavoritesMapView] Clipboard write failed', err);
                  success = false;
                }

                cancelPendingTooltipClose();

                if (success) {
                  button.textContent = 'Copied';
                  window.setTimeout(() => {
                    button.textContent = label;
                  }, 1400);
                } else {
                  button.textContent = 'Press ⌘C';
                  window.setTimeout(() => {
                    button.textContent = label;
                  }, 1600);
                }
              });
            });
          });
        }

        // Generate tooltip content with status color and copy actions
        const statusColors = getStatusColors(well.status);
        const accentColor = cyber ? getFacilityAccentColor(typeCode, cyber) : '#06b6d4';
        const buildTooltipContent = () => renderFacilityTooltip({
          well: well as unknown as TooltipWell,
          statusColors,
          accentColor,
          latitude: lat,
          longitude: lng,
          darkMode,
        });

        // Hover listeners for desktop - show tooltip
        if (!isTouchDevice) {
          marker.addListener('mouseover', () => {
            hoverPinnedRef.current = true;
            hoveredMarkerRef.current = marker;
            cancelPendingTooltipClose();
            if (infoWindowRef.current) {
              infoWindowRef.current.setContent(buildTooltipContent());
              infoWindowRef.current.open(mapInstanceRef.current, marker);
            }
          });

          marker.addListener('mouseout', () => {
            hoverPinnedRef.current = false;
            hoveredMarkerRef.current = null;
            scheduleTooltipClose(200);
          });
        }

        // Click handler - opens modal (with touch tap logic for mobile)
        marker.addListener('click', () => {
          telegram.hapticFeedback('light');
          
          // If in landmark drawing mode, place destination here instead of opening modal
          if (isDrawing && placeDestinationAtRef.current && well.latitude && well.longitude) {
            placeDestinationAtRef.current(well.latitude, well.longitude, well.wellName);
            setToast({
              open: true,
              message: `🏁 Destination set at ${well.wellName}`,
              severity: 'success'
            });
            return;
          }
          
          // On mobile: first tap shows tooltip, second tap opens modal
          if (isTouchDevice) {
            if (lastTappedWellIdRef.current === well.id) {
              // Second tap - open modal
              lastTappedWellIdRef.current = null;
              if (infoWindowRef.current) {
                infoWindowRef.current.close();
              }
              setModalWell(well);
              setWellModalOpen(true);
              setSelectedWell(well);
              onSelectWell(well);
            } else {
              // First tap - show tooltip and set selected well for zoom controls
              lastTappedWellIdRef.current = well.id;
              setSelectedWell(well); // Enable +/- zoom to center on this pin
              if (infoWindowRef.current) {
                infoWindowRef.current.setContent(buildTooltipContent());
                infoWindowRef.current.open(mapInstanceRef.current, marker);
              }
            }
          } else {
            // Desktop - direct to modal
            hoverPinnedRef.current = false;
            hoveredMarkerRef.current = null;
            setModalWell(well);
            setWellModalOpen(true);
            setSelectedWell(well);
            onSelectWell(well);
          }
        });

        // Right-click context menu for delete
        marker.addListener('rightclick', (e: google.maps.MapMouseEvent) => {
          telegram.hapticFeedback('medium');
          // Set marker context menu state - cast domEvent to MouseEvent for clientX/Y
          const mouseEvent = e.domEvent as MouseEvent;
          setMarkerContextMenu({
            well,
            x: mouseEvent?.clientX || window.innerWidth / 2,
            y: mouseEvent?.clientY || window.innerHeight / 2,
          });
        });

        // Long-press handler for touch/mouse down - enable drag after 600ms hold
        const handleLongPressStart = () => {
          const wellId = well.id;
          
          // Clear any existing timer
          const existingTimer = markerLongPressTimersRef.current.get(wellId);
          if (existingTimer) clearTimeout(existingTimer);
          
          // Start 600ms timer to enable dragging
          const timer = setTimeout(() => {
            marker.setDraggable(true);
            markerDragEnabledRef.current.set(wellId, true);
            telegram.hapticFeedback('heavy');
            setDraggingMarker({ well, x: window.innerWidth / 2, y: window.innerHeight / 2 });
          }, MARKER_LONG_PRESS_DURATION);
          
          markerLongPressTimersRef.current.set(wellId, timer);
        };
        
        const handleLongPressEnd = () => {
          const wellId = well.id;
          const timer = markerLongPressTimersRef.current.get(wellId);
          if (timer) {
            clearTimeout(timer);
            markerLongPressTimersRef.current.delete(wellId);
          }
          // Only keep draggable if drag was actually enabled
          if (!markerDragEnabledRef.current.get(wellId)) {
            marker.setDraggable(false);
          }
        };

        // Drag start - only allow if long-press timer completed
        marker.addListener('dragstart', () => {
          const wellId = well.id;
          if (!markerDragEnabledRef.current.get(wellId)) {
            // Long-press not yet triggered, cancel drag
            marker.setDraggable(false);
            setTimeout(() => marker.setDraggable(true), 50);
            return;
          }
          
          // Get screen position of marker
          const projection = mapInstanceRef.current?.getProjection();
          const pos = marker.getPosition();
          if (projection && pos) {
            const point = projection.fromLatLngToPoint(pos);
            if (point) {
              setDraggingMarker({ well, x: window.innerWidth / 2, y: window.innerHeight / 2 });
            }
          } else {
            setDraggingMarker({ well, x: window.innerWidth / 2, y: window.innerHeight / 2 });
          }
        });

        // Drag - position tracking handled by document-level listeners
        marker.addListener('drag', () => {
          // Position tracking handled by document mousemove/touchmove
        });

        // Drag end - check if over trash, otherwise snap back
        marker.addListener('dragend', () => {
          const wellId = well.id;
          markerDragEnabledRef.current.delete(wellId);
          
          // Use ref to get current value (avoids closure issue)
          if (isOverTrashRef.current) {
            // Remove from favorites
            removeFavorite(well.id);
            telegram.hapticFeedback('success');
            setToast({ open: true, message: `🗑️ Removed "${well.wellName}" from favorites` });
          } else {
            // Snap back to original position
            marker.setPosition(originalPosition);
          }
          setDraggingMarker(null);
          setIsOverTrash(false);
        });
        
        // Add long-press handlers using mousedown/touchstart
        marker.addListener('mousedown', handleLongPressStart);
        marker.addListener('touchstart', handleLongPressStart);
        marker.addListener('mouseup', handleLongPressEnd);
        marker.addListener('touchend', handleLongPressEnd);
        marker.addListener('mouseout', handleLongPressEnd);

        newMarkers.push(marker);
      } catch (e) {
        console.error('[FavoritesMapView] Error creating marker for', well.wellName, e);
      }
    });

    markersRef.current = newMarkers;
    console.log('[FavoritesMapView] ✅ Created', newMarkers.length, 'markers successfully (filtered from', favorites.length, 'favorites)');

    // Fit bounds ONLY on initial load, not when selecting wells
    if (newMarkers.length > 0 && !hasInitialBoundsRef.current) {
      const bounds = new google.maps.LatLngBounds();
      newMarkers.forEach(m => {
        const pos = m.getPosition();
        if (pos) bounds.extend(pos);
      });
      mapInstanceRef.current?.fitBounds(bounds);
      hasInitialBoundsRef.current = true;
      console.log('[FavoritesMapView] 📍 Initial bounds set');
    }
  }, [favorites, onSelectWell, mapLoaded, open, selectedWell, currentFacilityLayer]);

  // Render road hazard markers
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current) return;

    // Clear old hazard markers
    hazardMarkersRef.current.forEach(m => m.setMap(null));
    hazardMarkersRef.current = [];

    if (roadHazards.length === 0) return;

    console.log('[FavoritesMapView] 🚨 Rendering', roadHazards.length, 'road hazards');

    // Hazard icon configurations matching Material UI icons - with rounded backgrounds and clean design
    const hazardIcons: Record<string, { color: string; bgColor: string; icon: string }> = {
      crash: { color: '#ef4444', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="m20.56 3.34-1.93.59L17.2 4l-.71.71.4 2.46 1.91 3.24c.38.64.58 1.35.6 2.08.02.73-.15 1.45-.5 2.09L16.62 18H6.38l-1.58-2.81c-.35-.64-.52-1.36-.5-2.09.02-.73.22-1.44.6-2.08l1.91-3.24.4-2.46L6.5 4l-1.43-.07-1.93-.59A.998.998 0 0 0 2 4.32v3.09c0 .28.11.54.29.71l3.05 3.05-1.7 2.83c-.23.41-.36.87-.36 1.34 0 .6.2 1.18.57 1.64l.95 1.28.77 3.85c.09.47.5.82.98.82h9.9c.48 0 .89-.35.98-.82l.77-3.85.95-1.28c.37-.46.57-1.04.57-1.64 0-.47-.13-.93-.36-1.34l-1.7-2.83 3.05-3.05c.18-.17.29-.43.29-.71V4.32c0-.65-.62-1.13-1.25-.98z"/></svg>` },
      slowdown: { color: '#f59e0b', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="m20.38 8.57-1.23 1.85a8 8 0 0 1-.22 7.58H5.07A8 8 0 0 1 15.58 6.85l1.85-1.23A10 10 0 0 0 3.35 19a2 2 0 0 0 1.72 1h13.85a2 2 0 0 0 1.74-1 10 10 0 0 0-.27-10.44z"/><path d="M10.59 15.41a2 2 0 0 0 2.83 0l5.66-8.49-8.49 5.66a2 2 0 0 0 0 2.83z"/></svg>` },
      construction: { color: '#f97316', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="m13.7835 15.5145-1.4142-1.4142-7.778 7.778 1.4142 1.4142zm4.2426-1.4142-1.4142-1.4142-7.778 7.778 1.4142 1.4142zm2.8284-2.8284-1.4142-1.4142-7.778 7.778 1.4142 1.4142zM2 20h20v4H2v-4zM2.3 2.3l3.464 3.464 4.95-4.95A1.5 1.5 0 0 1 11.786.8L16.7 5.714a1.5 1.5 0 0 1 .014 2.072l-4.95 4.95 3.465 3.464a1.5 1.5 0 0 1 0 2.121l-1.414 1.414a1.5 1.5 0 0 1-2.121 0L8.23 16.271l-4.95 4.95a1.5 1.5 0 0 1-2.072-.014L.293 16.293a1.5 1.5 0 0 1 .014-2.072l4.95-4.95L2.3 6.343a1.5 1.5 0 0 1 0-2.121l1.414-1.414A1.5 1.5 0 0 1 5.836.808l3.465 3.464 4.95-4.95z"/></svg>` },
      lane_closure: { color: '#8b5cf6', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M18 18H6v-2h12v2zM18 13H6v-2h12v2zM18 8H6V6h12v2zM18 4H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-6.5 14H10v-2h1.5v2zm0-4H10v-2h1.5v2zm0-4H10V8h1.5v2zm2.5 8h-1.5v-2H14v2zm0-4h-1.5v-2H14v2zm0-4h-1.5V8H14v2z"/></svg>` },
      object_on_road: { color: '#eab308', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16v2h2v-2h-2zm0-6v4h2v-4h-2z"/></svg>` },
      flooded_road: { color: '#0ea5e9', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M17 16c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-5 4c.55 0 1.05-.2 1.43-.55.37.35.87.55 1.43.55s1.06-.2 1.43-.55c.37.35.87.55 1.43.55.56 0 1.06-.2 1.43-.55.38.35.88.55 1.43.55.55 0 1.05-.2 1.43-.55.38.35.88.55 1.43.55v-2c-.55 0-1.05-.2-1.43-.55-.38.35-.88.55-1.43.55s-1.05-.2-1.43-.55c-.37.35-.87.55-1.43.55s-1.06-.2-1.43-.55c-.37.35-.87.55-1.43.55s-1.06-.2-1.43-.55c-.38.35-.88.55-1.43.55v2c.55 0 1.05.2 1.43.55zM6 16c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm11-9H7V5c0-1.1.9-2 2-2h6c1.1 0 2 .9 2 2v2zm2 0c0-2.21-1.79-4-4-4H9C6.79 3 5 4.79 5 7H3.5C2.67 7 2 7.67 2 8.5V9h4.5c.83 0 1.5-.67 1.5-1.5V7h8v.5c0 .83.67 1.5 1.5 1.5H22v-.5c0-.83-.67-1.5-1.5-1.5H19z"/></svg>` },
      low_visibility: { color: '#6b7280', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>` },
      cop: { color: '#3b82f6', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>` },
      safety_inspector: { color: '#ec4899', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>` },
      hazard: { color: '#f59e0b', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>` },
      wreck: { color: '#ef4444', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="m20.56 3.34-1.93.59L17.2 4l-.71.71.4 2.46 1.91 3.24c.38.64.58 1.35.6 2.08.02.73-.15 1.45-.5 2.09L16.62 18H6.38l-1.58-2.81c-.35-.64-.52-1.36-.5-2.09.02-.73.22-1.44.6-2.08l1.91-3.24.4-2.46L6.5 4l-1.43-.07-1.93-.59A.998.998 0 0 0 2 4.32v3.09c0 .28.11.54.29.71l3.05 3.05-1.7 2.83c-.23.41-.36.87-.36 1.34 0 .6.2 1.18.57 1.64l.95 1.28.77 3.85c.09.47.5.82.98.82h9.9c.48 0 .89-.35.98-.82l.77-3.85.95-1.28c.37-.46.57-1.04.57-1.64 0-.47-.13-.93-.36-1.34l-1.7-2.83 3.05-3.05c.18-.17.29-.43.29-.71V4.32c0-.65-.62-1.13-1.25-.98z"/></svg>` },
      roadblock: { color: '#8b5cf6', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/></svg>` },
      fire: { color: '#f97316', bgColor: '#1f1f1f', icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/></svg>` }
    };

    roadHazards.forEach((hazard, idx) => {
      // Calculate net votes for cops
      const stillThereVotes = hazard.still_there_votes || 0;
      const goneVotes = hazard.gone_votes || 0;
      const netVotes = stillThereVotes - goneVotes;
      const isGhostCop = hazard.type === 'cop' && netVotes <= -3;
      
      // Use ghost icon if cop has -3 or more net downvotes
      let config = hazardIcons[hazard.type] || hazardIcons.hazard;
      
      if (isGhostCop) {
        // White ghost icon for theme awareness
        const ghostColor = darkMode ? '#ffffff' : '#1f1f1f';
        config = {
          color: ghostColor,
          bgColor: darkMode ? '#374151' : '#f3f4f6',
          icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c-4 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h2l1.5-1.5h5L16 21h2v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-4-4-8-4zM9 13c-.83 0-1.5-.67-1.5-1.5S8.17 10 9 10s1.5.67 1.5 1.5S9.83 13 9 13zm6 0c-.83 0-1.5-.67-1.5-1.5S14.17 10 15 10s1.5.67 1.5 1.5S15.83 13 15 13z"/></svg>`
        };
      }
      
      // Extract SVG path content so safety inspector and other hazards keep their unique silhouettes
      const iconSvgContentMatch = config.icon.match(/<svg[^>]*>([\s\S]*?)<\/svg>/i);
      const rawIconContent = iconSvgContentMatch ? iconSvgContentMatch[1] : config.icon;
      const sanitizedIconContent = rawIconContent
        .replace(/fill="currentColor"/g, '')
        .replace(/fill='currentColor'/g, '');

      const iconSvg = `data:image/svg+xml;base64,${btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
          <circle cx="28" cy="28" r="26" fill="${config.bgColor}" stroke="${config.color}" stroke-width="1.5"/>
          <g transform="translate(16, 16)" fill="${config.color}">
            ${sanitizedIconContent}
          </g>
        </svg>
      `)}`;

      const marker = new google.maps.Marker({
        position: { lat: hazard.latitude, lng: hazard.longitude },
        map: mapInstanceRef.current,
        title: isGhostCop 
          ? `👻 Ghost Checkpoint - ${new Date(hazard.created_at).toLocaleTimeString()}`
          : `${hazard.type.charAt(0).toUpperCase() + hazard.type.slice(1).replace(/_/g, ' ')} - Reported ${new Date(hazard.created_at).toLocaleTimeString()}`,
        icon: {
          url: iconSvg,
          scaledSize: new google.maps.Size(56, 56),
          anchor: new google.maps.Point(28, 28),
        },
        zIndex: 3000 + idx,
        optimized: false,
      });

      // Add click listener for info window
      marker.addListener('click', async () => {
        telegram.hapticFeedback('light');
        const expiresAt = new Date(hazard.expires_at);
        const minutesLeft = Math.max(0, Math.round((expiresAt.getTime() - Date.now()) / 60000));
        
        // Check if user already voted on this hazard (all types support voting)
        const userId = telegram.getUserId() || 'anonymous';
        let hasVoted = false;
        
        try {
          const voteCheckRes = await fetch(`/api/hazards/vote/check?hazardId=${hazard.id}&userId=${userId}`);
          const voteCheckData = await voteCheckRes.json();
          hasVoted = voteCheckData.hasVoted || false;
        } catch (error) {
          console.error('[FavoritesMapView] Error checking vote status:', error);
        }
        
        // Build info window content - match well popup styling
        let content = `
          <div style="
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 12px;
            min-width: 200px;
            background: rgba(17, 24, 39, 0.95);
            border-radius: 8px;
            border: 1px solid ${config.color}40;
          ">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <div style="
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background: ${config.color};
                box-shadow: 0 0 8px ${config.color};
                flex-shrink: 0;
              "></div>
              <span style="
                color: #fff;
                font-weight: 600;
                font-size: 15px;
                text-transform: capitalize;
              ">${isGhostCop ? '👻 Ghost Checkpoint' : hazard.type.replace(/_/g, ' ')}</span>
            </div>
            <div style="
              color: #9ca3af;
              font-size: 12px;
              padding-left: 20px;
              margin-bottom: 4px;
            ">Expires in ~${minutesLeft} min</div>`;
        
        // Add voting UI only if user hasn't voted yet
        if (!hasVoted) {
          const voteDisplay = `
            <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb;">
              <div style="display: flex; justify-content: center; gap: 8px; margin-bottom: 8px;">
                <span style="font-size: 14px;">👍 ${stillThereVotes}</span>
                <span style="font-size: 14px; color: #9ca3af;">|</span>
                <span style="font-size: 14px;">👎 ${goneVotes}</span>
              </div>
              ${hazard.type === 'cop' ? `
              <div style="font-size: 12px; color: ${netVotes >= 0 ? '#10b981' : '#ef4444'}; font-weight: 600; margin-bottom: 8px;">
                Net: ${netVotes > 0 ? '+' : ''}${netVotes}
              </div>` : `
              <div style="font-size: 11px; color: #9ca3af; margin-bottom: 8px;">
                ${3 - goneVotes} more "gone" votes to remove
              </div>`}`;
          
          content += voteDisplay + `
              <div style="display: flex; gap: 8px; justify-content: center;">
                <button id="upvote-btn" style="padding: 6px 16px; background: #10b981; color: white; border: none; border-radius: 6px; font-size: 13px; cursor: pointer; font-weight: 600;">
                  👍 Still There
                </button>
                <button id="downvote-btn" style="padding: 6px 16px; background: #ef4444; color: white; border: none; border-radius: 6px; font-size: 13px; cursor: pointer; font-weight: 600;">
                  👎 Gone
                </button>
              </div>
            </div>`;
        }
        
        content += '</div>';
        
        const infoWindow = new google.maps.InfoWindow({ content });
        infoWindow.open(mapInstanceRef.current, marker);
        
        // Add vote button click handlers for all hazard types
        if (!hasVoted) {
          google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
            const upvoteBtn = document.getElementById('upvote-btn');
            const downvoteBtn = document.getElementById('downvote-btn');
            
            upvoteBtn?.addEventListener('click', async () => {
              telegram.hapticFeedback('medium');
              if (!userLocation) return;
              
              // Close info window immediately
              infoWindow.close();
              
              // Show immediate feedback
              setToast({ open: true, message: '👍 Voting...', severity: 'info' });
              
              const result = await roadHazardsService.voteOnHazard(
                hazard.id,
                'still_there',
                userId,
                userLocation.lat,
                userLocation.lng
              );
              
              if (result.success) {
                setToast({ open: true, message: '👍 Voted! Thanks for the update.', severity: 'success' });
                // Refresh hazards to show updated votes and lock out further voting
                await roadHazardsService.fetchHazards();
              } else {
                setToast({ open: true, message: result.message || 'Vote failed', severity: 'error' });
              }
            });
            
            downvoteBtn?.addEventListener('click', async () => {
              telegram.hapticFeedback('medium');
              if (!userLocation) return;
              
              // Close info window immediately
              infoWindow.close();
              
              // Show immediate feedback
              setToast({ open: true, message: '👎 Voting...', severity: 'info' });
              
              const result = await roadHazardsService.voteOnHazard(
                hazard.id,
                'gone',
                userId,
                userLocation.lat,
                userLocation.lng
              );
              
              if (result.success) {
                const message = result.hazardRemoved 
                  ? '✅ Hazard removed! Thanks for the update.'
                  : ((result as any).ghostMode 
                    ? '👻 Cop marked as gone! Now showing as ghost checkpoint.'
                    : '👎 Voted! Thanks for the update.');
                setToast({ open: true, message, severity: 'success' });
                // Refresh hazards to show updated votes, ghost state, or removal, and lock out further voting
                await roadHazardsService.fetchHazards();
              } else {
                setToast({ open: true, message: result.message || 'Vote failed', severity: 'error' });
              }
            });
          });
        }
      });

      hazardMarkersRef.current.push(marker);
    });

    console.log('[FavoritesMapView] ✅ Created', hazardMarkersRef.current.length, 'hazard markers');
  }, [roadHazards, mapLoaded]);

  // Poll for community facilities and subscribe to updates
  useEffect(() => {
    if (!open) return;
    
    // Start polling for community facilities
    communityFacilitiesService.startPolling(30000); // Poll every 30 seconds
    
    // Subscribe to facility updates
    const unsubscribe = communityFacilitiesService.subscribe((facilities, newFacilities) => {
      console.log('[FavoritesMapView] 📍 Community facilities updated:', facilities.length);
      setCommunityFacilities(facilities);
      
      // Show toast for new facilities
      if (newFacilities.length > 0) {
        setToast({
          open: true,
          message: `🆕 ${newFacilities.length} new facility${newFacilities.length > 1 ? 'ies' : ''} added!`,
          severity: 'info'
        });
      }
    });
    
    return () => {
      unsubscribe();
      communityFacilitiesService.stopPolling();
    };
  }, [open]);

  // Render community facility markers
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current) return;

    // Clear old markers
    communityFacilityMarkersRef.current.forEach(m => m.setMap(null));
    communityFacilityMarkersRef.current = [];

    if (communityFacilities.length === 0) return;

    console.log('[FavoritesMapView] 📍 Rendering', communityFacilities.length, 'community facilities');

    communityFacilities.forEach((facility, idx) => {
      // Icon color based on facility type
      const typeColors: Record<string, string> = {
        well: '#10b981',
        wl: '#10b981',
        swd: '#3b82f6',
        rig: '#f59e0b',
        pit: '#8b5cf6',
        battery: '#ec4899',
        tank: '#06b6d4',
        // Food vendors - warm appetizing colors
        taco: '#f97316', // Orange
        food: '#ec4899', // Pink
        cafe: '#eab308', // Yellow
        other: '#6b7280',
      };
      const facilityTypeLower = facility.facility_type.toLowerCase();
      const color = typeColors[facilityTypeLower] || '#22c55e';
      
      // Get the appropriate SVG path for this facility type
      const typeCode = getFacilityTypeCode(facilityTypeLower);
      const svgPath = FACILITY_SVG_PATHS[typeCode] || FACILITY_SVG_PATHS['default'];
      
      // Check if this is a food vendor - use special marker
      const isFoodVendor = ['taco', 'food', 'cafe'].includes(typeCode);
      
      // Create marker with appropriate icon
      const marker = new google.maps.Marker({
        position: { lat: facility.latitude, lng: facility.longitude },
        map: mapInstanceRef.current,
        title: `${isFoodVendor ? '🌮' : '⭐'} ${facility.name} (Community)`,
        icon: {
          path: svgPath,
          fillColor: color,
          fillOpacity: 0.95,
          strokeColor: '#ffffff',
          strokeWeight: isFoodVendor ? 2.5 : 2,
          scale: isFoodVendor ? 1.5 : 1.2, // Slightly larger for food vendors
          anchor: new google.maps.Point(12, 12),
        },
        zIndex: isFoodVendor ? 3000 + idx : 2000 + idx, // Food vendors on top
        optimized: false,
      });

      // Add click listener for info window
      marker.addListener('click', () => {
        telegram.hapticFeedback('light');
        
        // Parse notes for food vendor details
        const notes = facility.notes || '';
        const menuMatch = notes.match(/🍽️ Menu: (.+)/);
        const hoursMatch = notes.match(/⏰ Hours: (.+)/);
        const phoneMatch = notes.match(/📞 Phone: (.+)/);
        const paymentMatch = notes.match(/💵 Cash: (\w+) \| 💳 Card: (\w+)/);
        
        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div style="padding: 12px; min-width: 220px; background: ${darkMode ? '#1f2937' : '#ffffff'}; border-radius: 8px;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <span style="font-size: 24px;">${typeCode === 'taco' ? '🌮' : typeCode === 'food' ? '🍔' : typeCode === 'cafe' ? '☕' : '⭐'}</span>
                <div style="font-weight: bold; color: ${color}; font-size: 16px;">
                  ${facility.name}
                </div>
              </div>
              ${isFoodVendor && menuMatch ? `<div style="font-size: 14px; color: ${darkMode ? '#e5e7eb' : '#374151'}; margin-bottom: 6px; font-weight: 500;">
                🍽️ ${menuMatch[1]}
              </div>` : ''}
              ${isFoodVendor && hoursMatch ? `<div style="font-size: 13px; color: ${darkMode ? '#9ca3af' : '#6b7280'}; margin-bottom: 4px;">
                ⏰ ${hoursMatch[1]}
              </div>` : ''}
              ${isFoodVendor && phoneMatch ? `<div style="font-size: 13px; color: ${darkMode ? '#60a5fa' : '#2563eb'}; margin-bottom: 4px;">
                <a href="tel:${phoneMatch[1].replace(/[^0-9]/g, '')}" style="color: inherit; text-decoration: none;">📞 ${phoneMatch[1]}</a>
              </div>` : ''}
              ${isFoodVendor && paymentMatch ? `<div style="font-size: 12px; color: ${darkMode ? '#9ca3af' : '#6b7280'}; margin-bottom: 4px;">
                💵 Cash: ${paymentMatch[1]} | 💳 Card: ${paymentMatch[2]}
              </div>` : ''}
              ${!isFoodVendor ? `<div style="font-size: 13px; color: ${darkMode ? '#9ca3af' : '#6b7280'}; margin-bottom: 4px;">
                Type: ${facility.facility_type.toUpperCase()}
              </div>` : ''}
              ${facility.operator && !isFoodVendor ? `<div style="font-size: 13px; color: ${darkMode ? '#9ca3af' : '#6b7280'}; margin-bottom: 4px;">
                Operator: ${facility.operator}
              </div>` : ''}
              ${facility.api_number && !isFoodVendor ? `<div style="font-size: 13px; color: ${darkMode ? '#9ca3af' : '#6b7280'}; margin-bottom: 4px;">
                API: ${facility.api_number}
              </div>` : ''}
              ${facility.county ? `<div style="font-size: 13px; color: ${darkMode ? '#9ca3af' : '#6b7280'}; margin-bottom: 4px;">
                📍 ${facility.county} County
              </div>` : ''}
              <div style="font-size: 11px; color: ${darkMode ? '#6b7280' : '#9ca3af'}; margin-top: 8px; padding-top: 8px; border-top: 1px solid ${darkMode ? '#374151' : '#e5e7eb'};">
                ${isFoodVendor ? '🌮 Food Vendor' : 'Community Added'} • ${new Date(facility.created_at).toLocaleDateString()}
              </div>
            </div>
          `,
        });
        infoWindow.open(mapInstanceRef.current, marker);
      });

      communityFacilityMarkersRef.current.push(marker);
    });

    console.log('[FavoritesMapView] ✅ Created', communityFacilityMarkersRef.current.length, 'community facility markers');
  }, [communityFacilities, mapLoaded, darkMode]);

  // Render search result markers with pulsing animation
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current) return;

    // Clear existing search result markers
    searchResultMarkersRef.current.forEach(m => m.setMap(null));
    searchResultMarkersRef.current = [];

    if (searchResultMarkers.length === 0) return;

    console.log('[FavoritesMapView] 🔍 Rendering', searchResultMarkers.length, 'search result markers');

    searchResultMarkers.forEach((facility, idx) => {
      // Get facility type code and colors
      const wellAny = facility as any;
      const typeCode = getFacilityTypeCode(wellAny.type, wellAny.facilityType);
      
      // Create custom icon with pulsing effect (different color to indicate from search)
      // Use a bright cyan/magenta glow to distinguish from favorites
      const searchMarkerIcon = createSearchMarkerIcon(typeCode);
      
      // Create marker
      const marker = new google.maps.Marker({
        position: { lat: facility.latitude, lng: facility.longitude },
        map: mapInstanceRef.current,
        title: `🔍 ${facility.wellName} (from search)`,
        icon: searchMarkerIcon,
        zIndex: 2500 + idx, // Above community facilities
        optimized: false,
        animation: google.maps.Animation.DROP,
      });

      // Apply pulsing animation via DOM manipulation
      // This creates a pulsing effect on the marker element
      const applyPulseAnimation = () => {
        // Wait for marker to be added to DOM
        setTimeout(() => {
          const markerImg = marker.getIcon() as google.maps.Icon;
          if (markerImg && markerImg.url) {
            // Find the marker's img element and apply animation
            const markerDiv = document.querySelector(`img[src="${markerImg.url}"]`) as HTMLElement;
            if (markerDiv) {
              markerDiv.style.animation = 'searchMarkerPulse 2s ease-in-out infinite';
            }
          }
        }, 100);
      };
      applyPulseAnimation();

      // Add click listener for info window
      marker.addListener('click', () => {
        telegram.hapticFeedback('light');
        
        // Open well modal
        setModalWell(facility);
        setWellModalOpen(true);
        setSelectedWell(facility);
        onSelectWell(facility);
      });

      searchResultMarkersRef.current.push(marker);
    });

    console.log('[FavoritesMapView] ✅ Created', searchResultMarkersRef.current.length, 'search result markers');
  }, [searchResultMarkers, mapLoaded]);

  // Fetch OCD Activity data (incidents, pits, frac) when layer is toggled on
  /* useEffect(() => {
    if (!showOcdActivity || !mapLoaded) {
      // Clear markers when layer is turned off
      ocdMarkersRef.current.forEach(m => m.setMap(null));
      ocdMarkersRef.current = [];
      return;
    }

    const fetchOcdData = async () => {
      setIsLoadingOcdActivity(true);
      console.log('[FavoritesMapView] 🏗️ Fetching OCD activity data...');
      
      try {
        // Get map center for location-based filtering
        const map = mapInstanceRef.current;
        const center = map?.getCenter();
        const lat = center?.lat() || 32.5;  // Default to NM
        const lng = center?.lng() || -103.5;
        
        // Fetch all three data types in parallel
        const [incidentsRes, pitsRes, fracRes] = await Promise.all([
          fetch(`/api/ocd/incidents?lat=${lat}&lng=${lng}&radius=100&days=180`),
          fetch(`/api/ocd/pits?lat=${lat}&lng=${lng}&radius=100`),
          fetch(`/api/ocd/frac?lat=${lat}&lng=${lng}&radius=100&days=180`),
        ]);

        const [incidentsData, pitsData, fracData] = await Promise.all([
          incidentsRes.json(),
          pitsRes.json(),
          fracRes.json(),
        ]);

        console.log('[FavoritesMapView] 🏗️ OCD data fetched:', {
          incidents: incidentsData.count || 0,
          pits: pitsData.count || 0,
          frac: fracData.count || 0,
        });

        setOcdActivityData({
          incidents: incidentsData.incidents || [],
          pits: pitsData.pits || [],
          fracActivity: fracData.fracActivity || [],
        });
      } catch (error) {
        console.error('[FavoritesMapView] Error fetching OCD data:', error);
      } finally {
        setIsLoadingOcdActivity(false);
      }
    };

    fetchOcdData();
  }, [showOcdActivity, mapLoaded]); */

  // Render OCD Activity markers when data changes
  /* useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !showOcdActivity) return;

    // Clear old markers
    ocdMarkersRef.current.forEach(m => m.setMap(null));
    ocdMarkersRef.current = [];

    const { incidents, pits, fracActivity } = ocdActivityData;
    console.log('[FavoritesMapView] 🏗️ Rendering OCD markers:', { incidents: incidents.length, pits: pits.length, frac: fracActivity.length });

    // Incident markers - severity-based coloring
    incidents.forEach((incident: any, idx: number) => {
      if (!incident.latitude || !incident.longitude) return;
      
      // Severity colors: Critical=Red, Major=Orange, Moderate=Yellow, Minor=Gray
      const severityColors: Record<string, string> = {
        Critical: '#ef4444',
        Major: '#f97316',
        Moderate: '#facc15',
        Minor: '#6b7280',
      };
      const color = severityColors[incident.severity] || '#f97316';
      
      // Type symbols
      const typeSymbols: Record<string, string> = {
        Spill: '🛢️',
        Fire: '🔥',
        Blowout: '💥',
        'H2S Release': '☠️',
        Injury: '🚑',
      };
      const symbol = typeSymbols[incident.incidentType] || '⚠️';

      const marker = new google.maps.Marker({
        position: { lat: incident.latitude, lng: incident.longitude },
        map: mapInstanceRef.current,
        title: `${incident.incidentType}: ${incident.siteName || 'Unknown'}`,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 14,
          fillColor: color,
          fillOpacity: 0.6,
          strokeColor: color,
          strokeWeight: 2,
        },
        label: {
          text: symbol,
          fontSize: '14px',
        },
        zIndex: 4000 + idx,
      });

      marker.addListener('click', () => {
        telegram.hapticFeedback('light');
        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div style="padding: 10px; max-width: 250px;">
              <div style="font-size: 20px; margin-bottom: 4px;">${symbol}</div>
              <div style="font-weight: bold; color: ${color};">${incident.incidentType}</div>
              <div style="font-size: 13px; margin-top: 4px;">${incident.siteName || 'Unknown Site'}</div>
              ${incident.operator ? `<div style="font-size: 12px; color: #666;">Operator: ${incident.operator}</div>` : ''}
              ${incident.incidentDate ? `<div style="font-size: 12px; color: #666;">Date: ${new Date(incident.incidentDate).toLocaleDateString()}</div>` : ''}
              ${incident.material ? `<div style="font-size: 12px; color: #666;">Material: ${incident.material}</div>` : ''}
              ${incident.estimatedVolume ? `<div style="font-size: 12px; color: #666;">Volume: ${incident.estimatedVolume} ${incident.volumeUnit || 'bbls'}</div>` : ''}
              <div style="font-size: 11px; color: #999; margin-top: 6px; padding: 4px; background: ${color}20; border-radius: 4px;">
                ${incident.severity || 'Unknown'} severity
                ${incident.resultedInFire ? ' • 🔥 Fire' : ''}
                ${incident.resultedInInjury ? ' • 🚑 Injury' : ''}
                ${incident.reachedWatercourse ? ' • 💧 Water Impact' : ''}
              </div>
            </div>
          `,
        });
        infoWindow.open(mapInstanceRef.current, marker);
      });

      ocdMarkersRef.current.push(marker);
    });

    // Pit markers - blue/teal for water-related
    pits.forEach((pit: any, idx: number) => {
      if (!pit.latitude || !pit.longitude) return;
      
      const pitColors: Record<string, string> = {
        'Frac Pit': '#0ea5e9',
        'Produced Water Pit': '#06b6d4',
        'Emergency Pit': '#f97316',
        'Drilling Pit': '#6b7280',
      };
      const color = pitColors[pit.pitType] || '#0ea5e9';

      const marker = new google.maps.Marker({
        position: { lat: pit.latitude, lng: pit.longitude },
        map: mapInstanceRef.current,
        title: `${pit.pitType}: ${pit.name || 'Unknown'}`,
        icon: {
          path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
          scale: 8,
          fillColor: color,
          fillOpacity: 0.6,
          strokeColor: color,
          strokeWeight: 2,
          rotation: 180,
        },
        zIndex: 3500 + idx,
      });

      marker.addListener('click', () => {
        telegram.hapticFeedback('light');
        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div style="padding: 10px; max-width: 250px;">
              <div style="font-weight: bold; color: ${color};">🏊 ${pit.pitType}</div>
              <div style="font-size: 13px; margin-top: 4px;">${pit.name || 'Unnamed Pit'}</div>
              ${pit.operator ? `<div style="font-size: 12px; color: #666;">Operator: ${pit.operator}</div>` : ''}
              ${pit.wellName ? `<div style="font-size: 12px; color: #666;">Well: ${pit.wellName}</div>` : ''}
              ${pit.capacity ? `<div style="font-size: 12px; color: #666;">Capacity: ${pit.capacity.toLocaleString()} bbls</div>` : ''}
              ${pit.status ? `<div style="font-size: 11px; color: #999; margin-top: 4px;">Status: ${pit.status}</div>` : ''}
            </div>
          `,
        });
        infoWindow.open(mapInstanceRef.current, marker);
      });

      ocdMarkersRef.current.push(marker);
    });

    // Frac Activity markers - purple for completion activity
    fracActivity.forEach((frac: any, idx: number) => {
      if (!frac.latitude || !frac.longitude) return;

      const marker = new google.maps.Marker({
        position: { lat: frac.latitude, lng: frac.longitude },
        map: mapInstanceRef.current,
        title: `Frac: ${frac.wellName || frac.apiNumber}`,
        icon: {
          path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 8,
          fillColor: '#a855f7',
          fillOpacity: 0.6,
          strokeColor: '#a855f7',
          strokeWeight: 2,
        },
        zIndex: 3600 + idx,
      });

      marker.addListener('click', () => {
        telegram.hapticFeedback('light');
        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div style="padding: 10px; max-width: 250px;">
              <div style="font-weight: bold; color: #a855f7;">💧 Frac Job</div>
              <div style="font-size: 13px; margin-top: 4px;">${frac.wellName || frac.apiNumber}</div>
              ${frac.operator ? `<div style="font-size: 12px; color: #666;">Operator: ${frac.operator}</div>` : ''}
              ${frac.fracStartDate ? `<div style="font-size: 12px; color: #666;">Started: ${new Date(frac.fracStartDate).toLocaleDateString()}</div>` : ''}
              ${frac.totalWaterVolume ? `<div style="font-size: 12px; color: #666;">Water: ${frac.totalWaterVolume.toLocaleString()} bbls</div>` : ''}
              ${frac.stages ? `<div style="font-size: 12px; color: #666;">Stages: ${frac.stages}</div>` : ''}
              ${frac.waterSource ? `<div style="font-size: 11px; color: #999; margin-top: 4px;">Source: ${frac.waterSource}</div>` : ''}
            </div>
          `,
        });
        infoWindow.open(mapInstanceRef.current, marker);
      });

      ocdMarkersRef.current.push(marker);
    });

    console.log('[FavoritesMapView] ✅ Created', ocdMarkersRef.current.length, 'OCD activity markers');
  }, [ocdActivityData, showOcdActivity, mapLoaded]); */

  // Fetch Caliche Routes when layer is toggled on
  useEffect(() => {
    if (!showCalichemRoutes || !mapLoaded) {
      // Clear markers when layer turned off
      calichemRoutePolylinesRef.current.forEach(p => p.setMap(null));
      calichemRoutePolylinesRef.current = [];
      calichemRouteMarkersRef.current.forEach(m => m.setMap(null));
      calichemRouteMarkersRef.current = [];
      return;
    }

    const fetchCalichemRoutes = async () => {
      setIsLoadingCalichemRoutes(true);
      console.log('[FavoritesMapView] 🛤️ Fetching Caliche Routes...');
      
      try {
        // Fetch all public landmark routes
        const response = await fetch('/api/landmark-routes/all');
        if (!response.ok) throw new Error('Failed to fetch routes');
        
        const data = await response.json();
        console.log('[FavoritesMapView] 🛤️ Fetched', data.routes?.length || 0, 'Caliche Routes');
        setCalichemRoutesData(data.routes || []);
      } catch (error) {
        console.error('[FavoritesMapView] Error fetching Caliche Routes:', error);
        setToast({ open: true, message: 'Failed to load Caliche Routes', severity: 'error' });
      } finally {
        setIsLoadingCalichemRoutes(false);
      }
    };

    fetchCalichemRoutes();
  }, [showCalichemRoutes, mapLoaded]);

  // Render Caliche Routes on map when data changes
  useEffect(() => {
    if (!mapLoaded || !mapInstanceRef.current || !showCalichemRoutes) return;

    // Clear old polylines and markers
    calichemRoutePolylinesRef.current.forEach(p => p.setMap(null));
    calichemRoutePolylinesRef.current = [];
    calichemRouteMarkersRef.current.forEach(m => m.setMap(null));
    calichemRouteMarkersRef.current = [];

    console.log('[FavoritesMapView] 🛤️ Rendering', calichemRoutesData.length, 'Caliche Routes');

    // Surface type colors
    const surfaceColors: Record<string, string> = {
      paved: '#22c55e',    // Green
      caliche: '#f59e0b',  // Amber/Orange
      dirt: '#a16207',     // Brown
    };

    calichemRoutesData.forEach((route: any) => {
      if (!route.landmarks || route.landmarks.length < 2) return;
      
      const color = surfaceColors[route.surface_type] || '#3b82f6';
      const landmarks = route.landmarks.sort((a: any, b: any) => a.sequence_order - b.sequence_order);
      
      // Draw polyline connecting landmarks
      const path = landmarks.map((l: any) => ({ lat: l.latitude, lng: l.longitude }));
      
      const polyline = new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: color,
        strokeOpacity: 0.8,
        strokeWeight: 4,
        map: mapInstanceRef.current,
        zIndex: 4000,
      });

      // Get destination coordinates for navigation
      const destLandmark = landmarks.find((l: any) => l.landmark_type === 'destination') || landmarks[landmarks.length - 1];
      const destLat = destLandmark?.latitude || landmarks[landmarks.length - 1].latitude;
      const destLng = destLandmark?.longitude || landmarks[landmarks.length - 1].longitude;
      const routeIdForNav = route.route_id;

      // Add click listener to polyline
      polyline.addListener('click', () => {
        telegram.hapticFeedback('light');
        const infoWindow = new google.maps.InfoWindow({
          position: path[Math.floor(path.length / 2)],
          content: `
            <div style="padding: 14px; max-width: 300px; background: #1a1a2e; color: #fff; border-radius: 10px;">
              <div style="font-weight: bold; font-size: 16px; color: ${color}; margin-bottom: 4px;">🛤️ ${route.name}</div>
              ${route.description ? `<div style="font-size: 13px; margin-bottom: 8px; color: #ccc;">${route.description}</div>` : ''}
              <div style="display: flex; gap: 12px; font-size: 12px; color: #999; margin-bottom: 8px;">
                ${route.difficulty ? `<span>⚡ ${route.difficulty}</span>` : ''}
                ${route.surface_type ? `<span>🛣️ ${route.surface_type}</span>` : ''}
                ${route.total_distance_meters ? `<span>📏 ${(route.total_distance_meters / 1609.34).toFixed(1)} mi</span>` : ''}
              </div>
              <div style="font-size: 11px; color: #666; margin-bottom: 10px;">
                👤 ${route.created_by_username || 'Anonymous'} • 👍 ${route.upvotes || 0}
              </div>
              <div style="display: flex; flex-direction: column; gap: 6px;">
                <button onclick="window.dispatchEvent(new CustomEvent('navigateRoute', {detail: {routeId: '${routeIdForNav}'}}))" style="display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px; background: linear-gradient(135deg, #22c55e, #16a34a); color: #fff; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; font-size: 13px;">
                  🧭 Open in ZiaWellNav
                </button>
                <div style="display: flex; gap: 6px;">
                  <button onclick="window.open('https://maps.apple.com/?daddr=${destLat},${destLng}&dirflg=d', '_blank')" style="flex: 1; display: flex; align-items: center; justify-content: center; gap: 4px; padding: 8px; background: #374151; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 12px;">
                    🍎 Apple Maps
                  </button>
                  <button onclick="window.open('https://www.google.com/maps/dir/?api=1&destination=${destLat},${destLng}&travelmode=driving', '_blank')" style="flex: 1; display: flex; align-items: center; justify-content: center; gap: 4px; padding: 8px; background: #374151; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 12px;">
                    🗺️ Google Maps
                  </button>
                </div>
              </div>
            </div>
          `,
        });
        infoWindow.open(mapInstanceRef.current);
      });

      calichemRoutePolylinesRef.current.push(polyline);

      // Add markers for all landmarks with offset positioning to avoid blocking polyline
      landmarks.forEach((landmark: any, idx: number) => {
        const isStart = landmark.landmark_type === 'start';
        const isDestination = landmark.landmark_type === 'destination';
        const lmType = (landmark.landmark_type as LandmarkType) || 'waypoint';
        
        // Alternate offset direction: even indices north, odd indices south
        const offsetDirection = idx % 2 === 0 ? 'north' : 'south';
        const offsetPos = calculateOffsetPosition(
          landmark.latitude,
          landmark.longitude,
          offsetDirection,
          40 // 40 meters offset
        );
        
        const markerIcon = buildMaterialLandmarkIcon(
          lmType,
          { isStart, isDestination, size: 60 }
        );
        
        const marker = new google.maps.Marker({
          position: { lat: offsetPos.lat, lng: offsetPos.lng },
          map: mapInstanceRef.current,
          title: `${(LANDMARK_TYPES[lmType] || LANDMARK_TYPES.waypoint).label}: ${landmark.name || ''}`,
          icon: markerIcon,
          zIndex: 4100 + idx,
        });

        calichemRouteMarkersRef.current.push(marker);
      });
    });

    console.log('[FavoritesMapView] ✅ Rendered', calichemRoutePolylinesRef.current.length, 'route polylines');
  }, [calichemRoutesData, showCalichemRoutes, mapLoaded]);

  // Handle navigateRoute event from Caliche Routes infoWindow
  useEffect(() => {
    const handleNavigateRoute = async (event: Event) => {
      const { routeId } = (event as CustomEvent).detail;
      console.log('[FavoritesMapView] 🧭 Navigate route requested:', routeId);
      
      // Find the route data
      const route = calichemRoutesData.find((r: any) => r.route_id === routeId);
      if (!route || !route.landmarks || route.landmarks.length < 2) {
        setToast({ open: true, message: 'Route not found', severity: 'error' });
        return;
      }
      
      // Get the sorted landmarks for turn-by-turn
      const landmarks = route.landmarks.sort((a: any, b: any) => a.sequence_order - b.sequence_order);
      const firstLandmark = landmarks[0];
      
      // Enable GPS tracking if not already enabled
      if (!gpsState.personalLocationEnabled) {
        console.log('[FavoritesMapView] 🛰️ Enabling GPS for navigation');
        gpsState.togglePersonalLocation();
      }
      
      // Pan map to first landmark and set appropriate zoom
      if (mapInstanceRef.current && firstLandmark) {
        const map = mapInstanceRef.current;
        map.panTo({ lat: firstLandmark.latitude, lng: firstLandmark.longitude });
        map.setZoom(16); // Close-up zoom for navigation
        console.log('[FavoritesMapView] 🗺️ Panned to first landmark:', firstLandmark.name);
      }
      
      // Open the route detail panel to start turn-by-turn navigation
      setSelectedCalichemRoute(route);
      setSelectedCalichemRouteLandmarks(landmarks);
      setShowCalichemRouteDetail(true);
      
      telegram.hapticFeedback('medium');
      setToast({ open: true, message: `🧭 Starting navigation: ${route.name}`, severity: 'success' });
    };

    window.addEventListener('navigateRoute', handleNavigateRoute);
    return () => window.removeEventListener('navigateRoute', handleNavigateRoute);
  }, [calichemRoutesData, gpsState]);

  // Fetch location for weather overlay on map load (separate from user's actual GPS location)
  useEffect(() => {
    if (!mapLoaded || weatherLocation) return;
    
    // Try to get initial location for weather
    const fetchWeatherLocation = async () => {
      try {
        // Try Telegram's location API first
        const location = await telegram.requestLocation();
        const coords = { lat: location.latitude, lng: location.longitude };
        setWeatherLocation(coords);
        setUserLocation(coords); // Also set actual user location since we got real GPS
        console.log('[FavoritesMapView] 🌤️ Got GPS location for weather:', location);
      } catch {
        // Fallback: use map center for WEATHER ONLY (not userLocation!)
        const map = mapInstanceRef.current;
        if (map) {
          const center = map.getCenter();
          if (center) {
            setWeatherLocation({ lat: center.lat(), lng: center.lng() });
            // DO NOT set userLocation to map center - this was causing "My Location" to show wrong position!
            console.log('[FavoritesMapView] 🌤️ Using map center for weather (GPS unavailable)');
          }
        }
      }
    };
    
    fetchWeatherLocation();
  }, [mapLoaded, weatherLocation]);

  // Handle map style changes
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    
    const map = mapInstanceRef.current;
    
    // Apply base map type and styles based on selection
    switch (mapStyle) {
      case 'night':
        map.setMapTypeId('roadmap');
        map.setOptions({ styles: withRoadLabelEmphasis(NIGHT_MODE_STYLE) });
        break;
      case 'highcontrast':
        map.setMapTypeId('roadmap');
        map.setOptions({ styles: withRoadLabelEmphasis(HIGH_CONTRAST_STYLE) });
        break;
      case 'cyber':
        map.setMapTypeId('roadmap');
        map.setOptions({ styles: withRoadLabelEmphasis(darkMode ? DARK_CYBER_STYLE : LIGHT_CYBER_STYLE) });
        break;
      case 'terrain':
        map.setMapTypeId('terrain');
        map.setOptions({ styles: withRoadLabelEmphasis() }); // Clear custom styles but keep label boost
        break;
      case 'satellite':
        map.setMapTypeId('satellite');
        map.setOptions({ styles: withRoadLabelEmphasis() });
        break;
      case 'hybrid':
        map.setMapTypeId('hybrid');
        map.setOptions({ styles: withRoadLabelEmphasis() });
        break;
      case 'desert':
        map.setMapTypeId('roadmap');
        map.setOptions({ styles: withRoadLabelEmphasis(DESERT_STYLE) });
        break;
      case 'roads':
        map.setMapTypeId('roadmap');
        map.setOptions({ styles: withRoadLabelEmphasis(ROADS_ONLY_STYLE) });
        break;
      case 'topo':
        map.setMapTypeId('terrain');
        map.setOptions({ styles: withRoadLabelEmphasis(TOPO_STYLE) });
        break;
    }
  }, [mapStyle, darkMode]);

  // Initialize Drawing Canvas (Leaflet) when modal opens
  useEffect(() => {
    if (!showDrawingCanvas || !leafletCanvasRef.current) return;

    console.log('Drawing Canvas: Initializing Leaflet map');

    // Wait for DOM to be ready
    const timer = setTimeout(() => {
      try {
        // Verify ref is still valid
        if (!leafletCanvasRef.current) {
          console.warn('Drawing Canvas: ref not available');
          return;
        }

        // Calculate center from current well or favorites
        const centerWell = selectedWell || (favorites.length > 0 ? favorites[0] : null);
        const center = centerWell
          ? { lat: centerWell.latitude, lng: centerWell.longitude }
          : { lat: 32.5, lng: -104.5 };

        console.log('Drawing Canvas: Initializing at', center);

        // Initialize Leaflet map
        leafletMapService.initializeMap(leafletCanvasRef.current, center, 12);

        // Add selected well marker
        if (selectedWell) {
          leafletMapService.addWellMarker(
            selectedWell.latitude,
            selectedWell.longitude,
            selectedWell.wellName || 'Target Well',
            () => { /* marker click */ }
          );
        }

        // Set up drawing completion
        leafletMapService.setOnDrawComplete((coordinates) => {
          console.log('Drawing completed:', coordinates);
          setPendingRouteCoordinates(coordinates);
          // Could auto-save here or show a dialog
        });

        console.log('Drawing Canvas: Initialization complete');
      } catch (error) {
        console.error('Drawing Canvas: Initialization error:', error);
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      // Cleanup when modal closes
      try {
        leafletMapService.destroy();
      } catch (e) {
        console.error('Drawing Canvas: Cleanup error:', e);
      }
    };
  }, [showDrawingCanvas, selectedWell, favorites]);



  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{
          sx: {
            zIndex: Z_INDEX.MODAL_CONTAINER,
            backgroundColor: cyber.bgPrimary,
            backgroundImage: 'none'
          }
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Map Container - Use neutral background to avoid cyber styling interference */}
        <Box sx={{ flex: 1, position: 'relative', backgroundColor: darkMode ? '#1a1a2e' : '#ffffff' }}>
          {!mapLoaded && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: cyber.gradientCard,
              zIndex: Z_INDEX.LOADING_SPINNER,
              pointerEvents: 'none'
            }}>
              {/* Cyberpunk spinner with glow effect */}
              <Box sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3
              }}>
                {/* Outer glow ring */}
                <Box sx={{
                  position: 'absolute',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${cyber.neonBlue}50 0%, transparent 70%)`,
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { transform: 'scale(1)', opacity: 0.5 },
                    '50%': { transform: 'scale(1.2)', opacity: 1 }
                  }
                }} />
                {/* Main spinner */}
                <CyberSpinner size={56} />
              </Box>
            </Box>
          )}
          <div 
            ref={mapRef} 
            style={{ 
              width: '100%', 
              height: '100%',
              position: 'relative',
              // Isolate map from any CSS effects that could cause visual artifacts
              isolation: 'isolate',
              contain: 'layout paint',
            }} 
          />

          {/* ========== Laser Route Overlay - Cyberpunk animation for caliche routes ========== */}
          {showCalichemRoutes && calichemRoutesData.length > 0 && (
            <LaserRouteOverlay
              map={mapInstanceRef.current}
              routes={calichemRoutesData}
              visible={showCalichemRoutes}
              onRouteSelect={setLaserRouteSelection}
            />
          )}

          {/* ========== Weather Overlay - Flash Flood & Wind Warnings ========== */}
          {showWeatherOverlay && userLocation && (
            <WeatherOverlay
              latitude={userLocation.lat}
              longitude={userLocation.lng}
              darkMode={darkMode}
              expanded={weatherExpanded}
              onToggleExpanded={() => setWeatherExpanded(!weatherExpanded)}
            />
          )}

          {/* ========== Pollen Widget - AQI now shown directly in FAB ========== */}
          {showPollen && userLocation && (
            <Box sx={{ 
              position: 'absolute', 
              top: 88, 
              right: 16, 
              zIndex: 1050,
              display: 'flex',
              gap: 1,
            }}>
              <PollenAlertWidget
                latitude={userLocation.lat}
                longitude={userLocation.lng}
                darkMode={darkMode}
              />
            </Box>
          )}

          {/* ========== Laser Route Info Panel - Shows when clicking distance badge ========== */}
          {laserRouteSelection && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1100,
                backgroundColor: darkMode ? 'rgba(10, 10, 20, 0.95)' : 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(12px)',
                borderRadius: 3,
                border: `1px solid ${darkMode ? 'rgba(0, 255, 136, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
                boxShadow: darkMode 
                  ? '0 8px 32px rgba(0, 255, 136, 0.2), 0 4px 16px rgba(0, 0, 0, 0.4)' 
                  : '0 8px 32px rgba(0, 0, 0, 0.15)',
                padding: 2,
                minWidth: 280,
                maxWidth: 'calc(100% - 32px)',
              }}
            >
              {/* Close button */}
              <IconButton
                size="small"
                onClick={() => setLaserRouteSelection(null)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    color: darkMode ? '#00ff88' : '#1976d2',
                    backgroundColor: darkMode ? 'rgba(0, 255, 136, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              {/* Route name */}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: darkMode ? '#00ff88' : '#1976d2',
                  mb: 0.5,
                  pr: 4, // Space for close button
                  fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                }}
              >
                {laserRouteSelection.name}
              </Typography>

              {/* Route info row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <RouteIcon sx={{ fontSize: 16 }} />
                  {laserRouteSelection.distanceMiles.toFixed(2)} mi
                </Typography>
                <Chip
                  label={laserRouteSelection.surfaceType || 'caliche'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    backgroundColor: darkMode ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.15)',
                    color: darkMode ? '#fbbf24' : '#d97706',
                    border: `1px solid ${darkMode ? 'rgba(245, 158, 11, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                  }}
                >
                  {laserRouteSelection.waypoints.length} waypoints
                </Typography>
              </Box>

              {/* Navigate button */}
              <Button
                fullWidth
                variant="contained"
                startIcon={<NavigationIcon />}
                onClick={() => {
                  if (!userLocation) {
                    setToast({ open: true, message: 'Getting your location...', severity: 'info' });
                    return;
                  }
                  
                  // Get waypoints excluding the last one (which becomes destination)
                  const waypoints = laserRouteSelection.waypoints.slice(0, -1);
                  const destination = laserRouteSelection.waypoints[laserRouteSelection.waypoints.length - 1];
                  
                  if (!destination) {
                    setToast({ open: true, message: 'Invalid route data', severity: 'error' });
                    return;
                  }
                  
                  // Use NavigationService with waypoints
                  if (waypoints.length > 0) {
                    NavigationService.openGoogleMapsWithWaypoints(
                      waypoints.map(w => ({ lat: w.lat, lng: w.lng })),
                      { lat: destination.lat, lng: destination.lng }
                    );
                  } else {
                    // Single point - just navigate to destination directly
                    NavigationService.openGoogleMapsWithWaypoint(
                      { lat: destination.lat, lng: destination.lng },
                      { lat: destination.lat, lng: destination.lng }
                    );
                  }
                  
                  setToast({ 
                    open: true, 
                    message: `🗺️ Opening ${laserRouteSelection.name} in Google Maps`, 
                    severity: 'success' 
                  });
                }}
                sx={{
                  background: darkMode 
                    ? 'linear-gradient(135deg, #00ff88 0%, #00cc6a 100%)' 
                    : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: darkMode ? '#000' : '#fff',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  py: 1.2,
                  boxShadow: darkMode 
                    ? '0 4px 16px rgba(0, 255, 136, 0.3)' 
                    : '0 4px 16px rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    background: darkMode 
                      ? 'linear-gradient(135deg, #00ff99 0%, #00dd77 100%)' 
                      : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: darkMode 
                      ? '0 6px 24px rgba(0, 255, 136, 0.4)' 
                      : '0 6px 24px rgba(0, 0, 0, 0.25)',
                  }
                }}
              >
                Navigate Route
              </Button>
            </Box>
          )}


          {/* ========== Nearby Services Modal - Gas, Food, Lodging ========== */}
          {userLocation && (
            <NearbyServicesWidget
              latitude={userLocation.lat}
              longitude={userLocation.lng}
              darkMode={darkMode}
              open={showNearbyServices}
              onClose={() => setShowNearbyServices(false)}
            />
          )}

          {/* ========== Wind Visualization - Animated Particles ========== */}
          {showWindOnMap && windData && windData.speed >= 5 && (
            <WindVisualization
              windData={windData}
              isVisible={showWindOnMap}
              intensity={windData.speed >= 30 ? 'high' : windData.speed >= 15 ? 'medium' : 'low'}
            />
          )}

          {/* ========== Truck Stops Layer - Diesel, Parking, Showers ========== */}
          <TruckStopsLayer
            map={mapInstanceRef.current}
            userLocation={userLocation}
            visible={showTruckStops}
            darkMode={darkMode}
            onTruckStopCountChange={setTruckStopCount}
            onLoadingChange={setIsLoadingTruckStops}
          />

          {/* ========== Elevation Profile Widget - Route Grade Analysis ========== */}
          {showElevationWidget && elevationPath.length >= 2 && (
            <ElevationWidget
              path={elevationPath}
              darkMode={darkMode}
              onClose={() => setShowElevationWidget(false)}
            />
          )}

          {/* ========== Street View Modal - Location Preview ========== */}
          <StreetViewModal
            isOpen={showStreetViewModal}
            onClose={() => {
              setShowStreetViewModal(false);
              setStreetViewLocation(null);
            }}
            lat={streetViewLocation?.lat || 0}
            lng={streetViewLocation?.lng || 0}
            title={streetViewLocation?.title || 'Street View Preview'}
          />

          {/* ========== Offline Cache Manager - Download Maps for Low Signal Areas ========== */}
          <OfflineCacheManager
            open={showOfflineCacheManager}
            onClose={() => setShowOfflineCacheManager(false)}
            darkMode={darkMode}
          />

          {/* ========== Road Hazard Dialog - Report Hazards & Cops ========== */}
          <RoadHazardDialog
            open={showRoadHazardDialog}
            onClose={() => setShowRoadHazardDialog(false)}
            currentLocation={userLocation}
            onHazardReported={(hazard) => {
              console.log('[FavoritesMapView] 🚨 Hazard reported:', hazard);
              // Immediately broadcast the hazard to all users via snackbar notification
              showHazardNotification(hazard);
              setToast({
                open: true,
                message: hazard.type === 'cop' 
                  ? '👮 Cop reported! Thanks for helping the community.' 
                  : '⚠️ Hazard reported! Drive safe.',
                severity: 'success'
              });
            }}
          />

          {/* ========== Well Details Modal - Replaces Drawer ========== */}
          <WellDetailsModal
            well={modalWell}
            open={wellModalOpen}
            onClose={() => {
              setWellModalOpen(false);
              setModalWell(null);
            }}
            darkMode={darkMode}
            onShowRoute={showRouteToWell}
            onNavigate={startNavigation}
            onStreetView={(lat, lng, title) => {
              setStreetViewLocation({ lat, lng, title });
              setShowStreetViewModal(true);
            }}
            onClearRoute={clearRoute}
            routeInfo={routeInfo}
            isLoadingRoute={isLoadingRoute}
            navigationSteps={navigationSteps}
            landmarkWaypoints={selectedCalichemRouteLandmarks.length > 1
              ? selectedCalichemRouteLandmarks
                  .filter((_, idx, arr) => idx !== 0 && idx !== arr.length - 1)
                  .map(lm => ({ lat: lm.latitude, lng: lm.longitude }))
              : undefined}
            userVehicle={userVehicle}
            onVehicleChange={(vehicle: typeof userVehicle) => setUserVehicle(vehicle)}
            onCopyApi={(api) => {
              navigator.clipboard.writeText(api).then(() => {
                telegram.hapticFeedback('success');
                setToast({ open: true, message: `API ${api} copied!` });
              });
            }}
          />

          {/* ========== Turn-by-Turn Navigation Overlay ========== */}
          {isNavigating && navigationSteps.length > 0 && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: Z_INDEX.CONTROLS + 100,
              pointerEvents: 'none',
            }}>
              {/* Navigation Header Bar */}
              <Box sx={{
                background: darkMode 
                  ? 'linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.9) 100%)'
                  : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.9) 100%)',
                backdropFilter: 'blur(20px)',
                borderBottom: `2px solid ${cyber.neonGreen}`,
                p: 2,
                pointerEvents: 'auto',
              }}>
                {/* Top row: Exit button & destination */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Button
                    onClick={stopNavigation}
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      border: `2px solid ${cyber.neonRed}`,
                      color: cyber.neonRed,
                      fontWeight: 700,
                      '&:hover': {
                        backgroundColor: `${cyber.neonRed}20`,
                      }
                    }}
                  >
                    <CloseIcon sx={{ mr: 0.5 }} /> Exit
                  </Button>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ color: cyber.textMuted, fontSize: '0.7rem', textTransform: 'uppercase' }}>
                      Navigating to
                    </Typography>
                    <Typography sx={{ color: cyber.textPrimary, fontWeight: 700, fontSize: '0.9rem' }}>
                      {navigationDestination?.wellName || 'Destination'}
                    </Typography>
                  </Box>
                </Box>

                {/* Back-out actions */}
                {(ingressWaypoints.length > 0 || ingressOrigin) && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => buildBackRoute('road')}
                      sx={{
                        background: `linear-gradient(135deg, ${cyber.neonCyan} 0%, ${cyber.neonGreen} 100%)`,
                        color: '#000',
                        fontWeight: 800,
                        borderRadius: 2,
                        textTransform: 'none',
                        boxShadow: `0 0 16px ${cyber.neonCyan}40`,
                        '&:hover': { background: `linear-gradient(135deg, ${cyber.neonGreen} 0%, ${cyber.neonCyan} 100%)` },
                      }}
                    >
                      ↩ Back to Road {ingressConnectorLabel ? `(${ingressConnectorLabel})` : ''}
                    </Button>
                    {ingressOrigin && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => buildBackRoute('start')}
                        sx={{
                          borderColor: cyber.neonCyan,
                          color: cyber.neonCyan,
                          fontWeight: 700,
                          borderRadius: 2,
                          textTransform: 'none',
                          '&:hover': { borderColor: cyber.neonGreen, color: cyber.neonGreen },
                        }}
                      >
                        ↩ Back to Start
                      </Button>
                    )}
                  </Box>
                )}

                {/* Current Step Card */}
                <Box sx={{
                  background: `linear-gradient(135deg, ${cyber.neonGreen}15 0%, ${cyber.neonCyan}10 100%)`,
                  border: `2px solid ${cyber.neonGreen}`,
                  borderRadius: 3,
                  p: 2.5,
                  boxShadow: `0 0 30px ${cyber.neonGreen}30`,
                }}>
                  {/* Maneuver Icon */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      backgroundColor: cyber.neonGreen,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {(() => {
                        const maneuver = navigationSteps[currentStepIndex]?.maneuver || '';
                        if (maneuver.includes('left')) return <TurnLeftIcon sx={{ fontSize: 48, color: '#000' }} />;
                        if (maneuver.includes('right')) return <TurnRightIcon sx={{ fontSize: 48, color: '#000' }} />;
                        if (maneuver.includes('uturn')) return <UTurnLeftIcon sx={{ fontSize: 48, color: '#000' }} />;
                        if (maneuver.includes('roundabout')) return <RoundaboutLeftIcon sx={{ fontSize: 36, color: '#000' }} />;
                        if (maneuver.includes('merge')) return <MergeIcon sx={{ fontSize: 36, color: '#000' }} />;
                        if (maneuver.includes('fork')) {
                          const forkDirection: 'left' | 'right' | 'split' =
                            maneuver.includes('left') ? 'left' : maneuver.includes('right') ? 'right' : 'split';
                          return <ForkBadge direction={forkDirection} />;
                        }
                        return <StraightIcon sx={{ fontSize: 36, color: '#000' }} />;
                      })()}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{
                        color: cyber.textPrimary,
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        lineHeight: 1.3,
                        mb: 1,
                      }}>
                        {navigationSteps[currentStepIndex]?.instructions || 'Continue on route'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography sx={{ 
                          color: cyber.neonGreen, 
                          fontWeight: 700, 
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                        }}>
                          {navigationSteps[currentStepIndex]?.distance}
                        </Typography>
                        <Typography sx={{ 
                          color: cyber.neonCyan, 
                          fontWeight: 600, 
                          fontSize: '0.9rem',
                          fontFamily: 'monospace',
                        }}>
                          {navigationSteps[currentStepIndex]?.duration}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Step Counter & Navigation Controls */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  mt: 2,
                }}>
                  <IconButton
                    onClick={prevStep}
                    disabled={currentStepIndex === 0}
                    sx={{
                      width: 48,
                      height: 48,
                      border: `2px solid ${currentStepIndex === 0 ? cyber.borderDefault : cyber.neonCyan}`,
                      color: currentStepIndex === 0 ? cyber.textMuted : cyber.neonCyan,
                      borderRadius: 2,
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography sx={{ 
                    color: cyber.textSecondary, 
                    fontWeight: 600,
                    fontSize: '0.85rem',
                  }}>
                    Step {currentStepIndex + 1} of {navigationSteps.length}
                  </Typography>
                  <IconButton
                    onClick={nextStep}
                    disabled={currentStepIndex >= navigationSteps.length - 1}
                    sx={{
                      width: 48,
                      height: 48,
                      border: `2px solid ${currentStepIndex >= navigationSteps.length - 1 ? cyber.borderDefault : cyber.neonCyan}`,
                      color: currentStepIndex >= navigationSteps.length - 1 ? cyber.textMuted : cyber.neonCyan,
                      borderRadius: 2,
                    }}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Box>

                {/* Total Route Info */}
                {routeInfo && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    gap: 3,
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${cyber.borderDefault}`,
                  }}>
                    {/* Heavy Haul Mode Indicator */}
                    {heavyHaulMode && (
                      <Chip
                        icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                        label="HEAVY HAUL"
                        size="small"
                        sx={{
                          backgroundColor: '#f9731630',
                          color: '#f97316',
                          border: '1px solid #f97316',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { boxShadow: '0 0 5px #f9731640' },
                            '50%': { boxShadow: '0 0 15px #f9731680' },
                          },
                        }}
                      />
                    )}
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ color: cyber.textMuted, fontSize: '0.65rem', textTransform: 'uppercase' }}>Total</Typography>
                      <Typography sx={{ color: heavyHaulMode ? '#f97316' : cyber.neonGreen, fontWeight: 700, fontFamily: 'monospace' }}>
                        {routeInfo.distance}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ color: cyber.textMuted, fontSize: '0.65rem', textTransform: 'uppercase' }}>ETA</Typography>
                      <Typography sx={{ color: heavyHaulMode ? '#f97316' : cyber.neonBlue, fontWeight: 700, fontFamily: 'monospace' }}>
                        {routeInfo.duration}
                      </Typography>
                    </Box>
                    {/* Elevation Profile Button */}
                    {elevationPath.length >= 2 && (
                      <Tooltip title="Show Elevation Profile">
                        <IconButton
                          size="small"
                          onClick={() => setShowElevationWidget(!showElevationWidget)}
                          sx={{
                            color: showElevationWidget ? cyber.neonGreen : cyber.textMuted,
                            border: `1px solid ${showElevationWidget ? cyber.neonGreen : cyber.borderDefault}`,
                            '&:hover': {
                              backgroundColor: `${cyber.neonGreen}20`,
                            },
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 17l4-4 4 4 4-8 6 8" />
                          </svg>
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* ========== MapFabManager - 4-Zone FAB System ========== */}
          <MapFabManager
            visible={mapLoaded}
            darkMode={darkMode}
            highContrast={highContrast}
            bottomOffset={0}
            mapStyle={mapStyle}
            onCycleMapStyle={() => {
              setMapStyle(prev => {
                const styles: MapStyleType[] = ['cyber', 'satellite', 'terrain', 'night', 'highcontrast', 'hybrid', 'desert', 'roads', 'topo'];
                const currentIdx = styles.indexOf(prev);
                return styles[(currentIdx + 1) % styles.length];
              });
              telegram.hapticFeedback('light');
            }}
            onZoomIn={() => {
              const map = mapInstanceRef.current;
              if (map) {
                // If a facility is selected, zoom all the way in and center on it
                if (selectedWell) {
                  map.setCenter({ lat: selectedWell.latitude, lng: selectedWell.longitude });
                  map.setZoom(20);
                  telegram.hapticFeedback('success');
                } else {
                  const zoom = map.getZoom() || 10;
                  map.setZoom(Math.min(zoom + 1, 21));
                  telegram.hapticFeedback('light');
                }
              }
            }}
            onZoomOut={() => {
              const map = mapInstanceRef.current;
              if (map) {
                // If a facility is selected, zoom out while keeping it centered
                if (selectedWell) {
                  map.setCenter({ lat: selectedWell.latitude, lng: selectedWell.longitude });
                  const zoom = map.getZoom() || 10;
                  map.setZoom(Math.max(zoom - 1, 1));
                  telegram.hapticFeedback('light');
                } else {
                  const zoom = map.getZoom() || 10;
                  map.setZoom(Math.max(zoom - 1, 1));
                  telegram.hapticFeedback('light');
                }
              }
            }}
            onClose={onClose}
            onSearch={() => {
              setShowSearchDialog(true);
              telegram.hapticFeedback('light');
            }}
            onMyLocation={async () => {
              telegram.hapticFeedback('medium');
              const map = mapInstanceRef.current;
              if (!map) return;
              
              // Helper function to center and zoom to location
              const centerOnLocation = (lat: number, lng: number, accuracy?: number) => {
                const targetZoom = Math.max(18, map.getZoom() ?? 0);

                // Prefer moveCamera for smooth combined pan/zoom when available
                if (typeof (map as google.maps.Map & { moveCamera?: (options: google.maps.CameraOptions) => void }).moveCamera === 'function') {
                  (map as google.maps.Map & { moveCamera: (options: google.maps.CameraOptions) => void }).moveCamera({
                    center: { lat, lng },
                    zoom: targetZoom,
                  });
                } else {
                  map.panTo({ lat, lng });
                  if ((map.getZoom() ?? 0) !== targetZoom) {
                    // Schedule zoom on next frame to avoid canceling the pan animation
                    requestAnimationFrame(() => map.setZoom(targetZoom));
                  }
                }
                setUserLocation({ lat, lng }); // Update actual user location
                // Compute and cycle search radius 1-10 miles
                const nextRadius = myLocationRadiusMiles === null || myLocationRadiusMiles >= 10 ? 1 : myLocationRadiusMiles + 1;
                setMyLocationRadiusMiles(nextRadius);
                // Filter favorites within radius and highlight them
                if (favorites.length > 0 && nextRadius > 0) {
                  const nearby = favorites.filter(f =>
                    distanceMiles(lat, lng, f.latitude, f.longitude) <= nextRadius
                  );
                  if (nearby.length > 0) {
                    setToast({ open: true, message: `📍 Showing facilities within ${nextRadius} mi (${nearby.length} found)` });
                  } else {
                    setToast({ open: true, message: `📍 No facilities within ${nextRadius} mi` });
                  }
                }
                telegram.hapticFeedback('success');
                // Show accuracy warning if GPS is weak (> 500 meters)
                if (accuracy && accuracy > 500) {
                  setToast({ open: true, message: `📍 Low GPS accuracy (~${Math.round(accuracy)}m) - location may be approximate` });
                } else {
                  setToast({ open: true, message: `📍 Centered on your location` });
                }
              };
              
              // ALWAYS fetch fresh GPS - don't use cached userLocation which may be wrong/stale
              // This fixes the bug where "My Location" showed wrong position from map center fallback
              
              // Show loading indicator
              setToast({ open: true, message: '📍 Getting your location...' });
              
              try {
                const result = await requestPreciseLocation({
                  desiredAccuracyMeters: 20,
                  refinementTimeoutMs: 8000,
                  allowTelegramFallback: true,
                  allowLastKnownFallback: false,
                });

                if (!result.fix) {
                  telegram.hapticFeedback('error');
                  const reason = result.reason ?? 'Unable to get location. Please check GPS permissions and try again.';
                  setToast({ open: true, message: reason, severity: 'error' });
                  return;
                }

                centerOnLocation(result.fix.lat, result.fix.lng, result.fix.accuracy ?? undefined);
                telegram.hapticFeedback('success');

                if (!result.fulfilled && typeof result.fix.accuracy === 'number') {
                  setToast({ open: true, message: `📍 Location ±${Math.round(result.fix.accuracy)}m. Move for a stronger fix if needed.` });
                } else if (result.usedFallback) {
                  setToast({ open: true, message: '📍 Using fallback location source' });
                } else {
                  setToast({ open: true, message: '📍 Centered on your location' });
                }
              } catch (error) {
                telegram.hapticFeedback('error');
                console.error('High-precision location failed:', error);
                const message = error instanceof Error ? error.message : 'Unable to get location';
                setToast({ open: true, message, severity: 'error' });
              }
            }}
            voiceStatus={voiceFabStatus}
            onVoiceClick={() => {
              toggleVoiceSearch();
            }}
            // onManualSearchClick={() => {
            //   setSearchMode('facilities');
            //   setSearchQuery('');
            //   setShowSearchDialog(true);
            //   telegram.hapticFeedback('light');
            // }}
            aiSpeedDial={<AISpeedDial />}
            personalLocationEnabled={gpsState.personalLocationEnabled}
            crewSharingEnabled={gpsState.crewSharingEnabled}
            locationStatus={gpsState.locationStatus}
            onTogglePersonalLocation={gpsState.togglePersonalLocation}
            onToggleCrewSharing={gpsState.toggleCrewSharing}
            onRequestLocation={gpsState.requestLocation}
            isCrewMember={gpsState.isCrewMember}
            isCrewOwner={gpsState.isCrewOwner}
            crewName={gpsState.crewName ?? undefined}
            onSettings={() => {
              if (!useNewFabManager) {
                telegram.hapticFeedback('light');
                setShowSettingsMenu(prev => !prev);
              }
            }}
            onOpenOfflineCache={() => {
              setShowOfflineCacheManager(true);
            }}
            onToggleDarkMode={toggleDarkMode}
            onToggleHighContrast={toggleHighContrast}
            soundEnabled={soundEnabled}
            onToggleSound={() => setSoundEnabled(prev => !prev)}
            showWeather={showWeatherOverlay}
            onToggleWeather={() => {
              setShowWeatherOverlay(!showWeatherOverlay);
              telegram.hapticFeedback('light');
            }}
            // Temperature FAB - shows current temp directly
            temperature={temperature}
            temperatureColor={temperatureColor}
            feelsLike={feelsLike}
            // UV Index FAB - shows UV level directly
            uvIndex={uvIndex}
            uvColor={uvColor}
            // Wind FAB - shows wind speed directly AND controls weather overlay (CAUTION GUSTY notification)
            showWind={showWindOnMap}
            windSpeed={windData?.speed ?? null}
            onToggleWind={() => {
              // Toggle both wind particles and the weather overlay (CAUTION GUSTY bar)
              setShowWindOnMap(!showWindOnMap);
              setShowWeatherOverlay(!showWeatherOverlay);
              telegram.hapticFeedback('light');
            }}
            // Rain/Precipitation FAB - critical for flash flood warnings
            precipProbability={precipProbability}
            precipColor={precipColor}
            thunderstormProbability={thunderstormProbability}
            // Air Quality FAB - shows AQI number directly in FAB
            showAirQuality={showAirQuality}
            aqiValue={aqiValue}
            aqiColor={aqiColor}
            onToggleAirQuality={() => {
              setShowAirQuality(!showAirQuality);
              telegram.hapticFeedback('light');
            }}
            // Pollen FAB - shows pollen index directly in FAB
            showPollen={showPollen}
            pollenIndex={pollenIndex}
            pollenColor={pollenColor}
            onTogglePollen={() => {
              setShowPollen(!showPollen);
              telegram.hapticFeedback('light');
            }}
            // Nearby Services FAB
            showNearbyServices={showNearbyServices}
            onToggleNearbyServices={() => {
              setShowNearbyServices(!showNearbyServices);
              telegram.hapticFeedback('light');
            }}
            // Elevation FAB
            showElevation={showElevationWidget}
            onToggleElevation={() => {
              setShowElevationWidget(!showElevationWidget);
              telegram.hapticFeedback('light');
            }}
            hasElevationPath={elevationPath.length >= 2}
            showRoutes={showRouteOverlays}
            isLoadingRoutes={isLoadingRoutes}
            onToggleRoutes={toggleRouteOverlays}
            routeCount={calicheRoutes.length}
            isLandmarkMode={isDrawing}
            onToggleLandmarkMode={() => {
              setIsDrawing(!isDrawing);
              telegram.hapticFeedback('light');
            }}
            currentFacilityLayer={currentFacilityLayer}
            onCycleFacilityLayer={() => {
              setCurrentFacilityLayer(prev => {
                const idx = FACILITY_LAYERS.indexOf(prev);
                const next = FACILITY_LAYERS[(idx + 1) % FACILITY_LAYERS.length];
                telegram.hapticFeedback('medium');
                return next;
              });
            }}
            // Truck Stops FAB - toggle for truck-friendly fuel stops
            showTruckStops={showTruckStops}
            onToggleTruckStops={() => {
              setShowTruckStops(!showTruckStops);
              telegram.hapticFeedback('light');
            }}
            truckStopCount={truckStopCount}
            isLoadingTruckStops={isLoadingTruckStops}
            // Vehicle FAB - quick access to change location marker
            currentVehicle={userVehicle}
            onOpenVehicleMenu={() => {
              setShowVehicleMenu(true);
              telegram.hapticFeedback('light');
            }}
            // Heavy Haul FAB - oversized/hazmat routing mode
            heavyHaulMode={heavyHaulMode}
            onToggleHeavyHaul={() => {
              setHeavyHaulMode(!heavyHaulMode);
              telegram.hapticFeedback('medium');
              setToast({
                open: true,
                message: !heavyHaulMode 
                  ? '🚛 Heavy Haul Mode ON - Routes optimized for oversized loads' 
                  : '🚗 Standard routing enabled',
                severity: !heavyHaulMode ? 'warning' : 'info'
              });
              // Clear existing route so user recalculates with new mode
              if (directionsRendererRef.current) {
                directionsRendererRef.current.setMap(null);
                directionsRendererRef.current = null;
              }
              setRouteInfo(null);
            }}
            // OCD Activity FAB - incidents, pits, frac activity layer - COMMENTED OUT
            // showOcdActivity={showOcdActivity}
            // onToggleOcdActivity={async () => {
            //   // Check premium feature access for OCD layers
            //   if (!showOcdActivity) {
            //     const hasAccess = await featureAccess.checkAccessWithPrompt('ocdLayers');
            //     if (!hasAccess) {
            //       console.log('🔒 [OCD] OCD Layers locked - trial expired');
            //       return;
            //     }
            //   }
            //   setShowOcdActivity(!showOcdActivity);
            //   telegram.hapticFeedback('medium');
            //   setToast({
            //     open: true,
            //     message: !showOcdActivity 
            //       ? '🏗️ OCD Activity Layer ON - Showing incidents, pits, and frac jobs' 
            //       : '🏗️ OCD Activity Layer OFF',
            //     severity: !showOcdActivity ? 'info' : 'success'
            //   });
            // }}
            // ocdActivityCount={ocdActivityData.incidents.length + ocdActivityData.pits.length + ocdActivityData.fracActivity.length}
            // isLoadingOcdActivity={isLoadingOcdActivity}
            // Caliche Routes FAB - community dirt road routes layer
            showCalichemRoutes={showCalichemRoutes}
            onToggleCalichemRoutes={() => {
              setShowCalichemRoutes(!showCalichemRoutes);
              telegram.hapticFeedback('medium');
              setToast({
                open: true,
                message: !showCalichemRoutes 
                  ? '🛤️ Caliche Routes ON - Showing community dirt road routes' 
                  : '🛤️ Caliche Routes OFF',
                severity: !showCalichemRoutes ? 'info' : 'success'
              });
            }}
            calichemRoutesCount={calichemRoutesData.length}
            isLoadingCalichemRoutes={isLoadingCalichemRoutes}
            onOpenRoutePhotos={openRoutePhotosModal}
            routePhotosCount={routePhotoFeed.length}
            // Community Facilities and Add Facility
            onToggleCommunityFacilities={() => {
              telegram.hapticFeedback('light');
              setToast({
                open: true,
                message: 'Community Facilities dialog opening soon!',
                severity: 'info'
              });
            }}
            communityFacilitiesCount={0}
            onToggleAddFacility={async () => {
              telegram.hapticFeedback('medium');
              setToast({
                open: true,
                message: '📍 Getting your location...',
                severity: 'info',
              });

              try {
                const result = await requestPreciseLocation({
                  desiredAccuracyMeters: 25,
                  refinementTimeoutMs: 8000,
                  allowLastKnownFallback: true,
                  allowTelegramFallback: true,
                });

                if (result.fix) {
                  setAddFacilityLocation({ lat: result.fix.lat, lng: result.fix.lng });
                  if (!result.fulfilled && typeof result.fix.accuracy === 'number') {
                    setToast({
                      open: true,
                      message: `⚠️ GPS accuracy ~${Math.round(result.fix.accuracy)}m. Adjust pin if needed.`,
                      severity: 'warning',
                    });
                  } else {
                    setToast({
                      open: true,
                      message: '📍 Location locked. Opening Add Facility…',
                      severity: 'success',
                    });
                  }
                } else {
                  setAddFacilityLocation(NM_FALLBACK_LOCATION);
                  setToast({
                    open: true,
                    message: '⚠️ Using NM center. Adjust location manually.',
                    severity: 'warning',
                  });
                }
              } catch (error) {
                console.error('Precise location unavailable for add facility:', error);
                setAddFacilityLocation(NM_FALLBACK_LOCATION);
                setToast({
                  open: true,
                  message: '⚠️ Location unavailable. Using NM center.',
                  severity: 'warning',
                });
              } finally {
                setShowAddFacilityForm(true);
              }
            }}
            // Road hazard, cop, restroom, and taco truck reporting FABs
            poopPinCount={activePoopPinCount}
            tacoPinCount={activeTacoPinCount}
            onReportHazard={() => {
              telegram.hapticFeedback('medium');
              setShowRoadHazardDialog(true);
            }}
            onReportCop={async () => {
              telegram.hapticFeedback('medium');
              setToast({ open: true, message: '📍 Getting your location...', severity: 'info' });

              const result = await requestPreciseLocation({
                desiredAccuracyMeters: 25,
                refinementTimeoutMs: 6000,
              });

              if (!result.fix) {
                setToast({
                  open: true,
                  message: '❌ Unable to get GPS fix. Enable location services and try again.',
                  severity: 'error',
                });
                return;
              }

              if (!result.fulfilled && result.fix.accuracy !== null) {
                setToast({
                  open: true,
                  message: `⚠️ GPS accuracy ~${Math.round(result.fix.accuracy)}m. Pin could be off slightly.`,
                  severity: 'warning',
                });
              }

              try {
                const hazard = await roadHazardsService.reportHazard(
                  'cop',
                  result.fix.lat,
                  result.fix.lng,
                  'Cop spotted',
                  telegram.getUserId() || undefined,
                  telegram.getUser()?.first_name || 'Anonymous'
                );

                if (hazard) {
                  showHazardNotification(hazard);
                  onHazardReported?.(hazard);
                  setUserLocation({ lat: result.fix.lat, lng: result.fix.lng });
                  setToast({
                    open: true,
                    message: '🚔 Cop reported! Thanks for helping the community.',
                    severity: 'success',
                  });
                  telegram.hapticFeedback('success');
                } else {
                  setToast({
                    open: true,
                    message: '❌ Failed to report cop. Try again.',
                    severity: 'error',
                  });
                }
              } catch (error) {
                console.error('[FavoritesMapView] Error reporting cop:', error);
                setToast({
                  open: true,
                  message: '❌ Failed to report cop. Try again.',
                  severity: 'error',
                });
              }
            }}
            onReportSafetyGuy={async () => {
              telegram.hapticFeedback('medium');
              setToast({ open: true, message: '📍 Getting your location...', severity: 'info' });

              const result = await requestPreciseLocation({
                desiredAccuracyMeters: 25,
                refinementTimeoutMs: 6000,
              });

              if (!result.fix) {
                setToast({
                  open: true,
                  message: '❌ Unable to get GPS fix. Enable location services and try again.',
                  severity: 'error',
                });
                return;
              }

              if (!result.fulfilled && result.fix.accuracy !== null) {
                setToast({
                  open: true,
                  message: `⚠️ GPS accuracy ~${Math.round(result.fix.accuracy)}m. Pin could be off slightly.`,
                  severity: 'warning',
                });
              }

              try {
                const hazard = await roadHazardsService.reportHazard(
                  'safety_inspector',
                  result.fix.lat,
                  result.fix.lng,
                  'Safety inspector spotted',
                  telegram.getUserId() || undefined,
                  telegram.getUser()?.first_name || 'Anonymous'
                );

                if (hazard) {
                  showHazardNotification(hazard);
                  onHazardReported?.(hazard);
                  setUserLocation({ lat: result.fix.lat, lng: result.fix.lng });
                  setToast({
                    open: true,
                    message: '👷 Safety Inspector reported! Thanks for helping the community.',
                    severity: 'success',
                  });
                  telegram.hapticFeedback('success');
                } else {
                  setToast({
                    open: true,
                    message: '❌ Failed to report safety inspector. Try again.',
                    severity: 'error',
                  });
                }
              } catch (error) {
                console.error('[FavoritesMapView] Error reporting safety inspector:', error);
                setToast({
                  open: true,
                  message: '❌ Failed to report safety inspector. Try again.',
                  severity: 'error',
                });
              }
            }}
            onReportPoop={async () => {
              telegram.hapticFeedback('medium');
              setToast({ open: true, message: '📍 Getting your location...', severity: 'info' });
              const result = await requestPreciseLocation({
                desiredAccuracyMeters: 20,
                refinementTimeoutMs: 7000,
                maxLastKnownAgeMs: 45000,
              });

              if (!result.fix) {
                setToast({
                  open: true,
                  message: '❌ Unable to get GPS fix. Enable location services and try again.',
                  severity: 'error',
                });
                return;
              }

              if (result.usedFallback && result.reason) {
                setToast({ open: true, message: `⚠️ ${result.reason}`, severity: 'warning' });
              } else if (!result.fulfilled && result.fix.accuracy !== null) {
                setToast({
                  open: true,
                  message: `⚠️ GPS accuracy ~${Math.round(result.fix.accuracy)}m. Pin may be slightly off.`,
                  severity: 'warning',
                });
              }

              try {
                const coords = { lat: result.fix.lat, lng: result.fix.lng };
                setUserLocation(coords);
                mapInstanceRef.current?.panTo(coords);

                const pin = await poopPinService.reportPin(
                  coords.lat,
                  coords.lng,
                  telegram.getUserId() || undefined,
                  telegram.getUser()?.first_name || 'Anonymous'
                );

                setPoopPins(prev => [pin, ...prev.filter(p => p.id !== pin.id)]);
                setToast({
                  open: true,
                  message: '💩 Restroom marked! Active for 14 days.',
                  severity: 'success',
                });
                telegram.hapticFeedback('success');
                await fetchPoopPins();
              } catch (error) {
                console.error('[FavoritesMapView] Error reporting poop pin:', error);
                const message = error instanceof Error ? error.message : 'Failed to drop restroom pin. Try again.';
                setToast({
                  open: true,
                  message: `❌ ${message}`,
                  severity: 'error',
                });
              }
            }}
            onReportTaco={async () => {
              telegram.hapticFeedback('medium');
              setToast({ open: true, message: '📍 Getting your location...', severity: 'info' });
              const result = await requestPreciseLocation({
                desiredAccuracyMeters: 20,
                refinementTimeoutMs: 7000,
                maxLastKnownAgeMs: 45000,
              });

              if (!result.fix) {
                setToast({
                  open: true,
                  message: '❌ Unable to get GPS fix. Enable location services and try again.',
                  severity: 'error',
                });
                return;
              }

              if (result.usedFallback && result.reason) {
                setToast({ open: true, message: `⚠️ ${result.reason}`, severity: 'warning' });
              } else if (!result.fulfilled && result.fix.accuracy !== null) {
                setToast({
                  open: true,
                  message: `⚠️ GPS accuracy ~${Math.round(result.fix.accuracy)}m. Pin may be slightly off.`,
                  severity: 'warning',
                });
              }

              try {
                const coords = { lat: result.fix.lat, lng: result.fix.lng };
                setUserLocation(coords);
                mapInstanceRef.current?.panTo(coords);

                const pin = await tacoPinService.reportPin(
                  coords.lat,
                  coords.lng,
                  telegram.getUserId() || undefined,
                  telegram.getUser()?.first_name || 'Anonymous'
                );

                setTacoPins(prev => [pin, ...prev.filter(p => p.id !== pin.id)]);
                setToast({
                  open: true,
                  message: '🌮 Taco truck pinned! Active for 14 days.',
                  severity: 'success',
                });
                telegram.hapticFeedback('success');
                await fetchTacoPins();
              } catch (error) {
                console.error('[FavoritesMapView] Error reporting taco pin:', error);
                const message = error instanceof Error ? error.message : 'Failed to drop taco pin. Try again.';
                setToast({
                  open: true,
                  message: `❌ ${message}`,
                  severity: 'error',
                });
              }
            }}
            // D-pad map panning
            onPan={(direction) => {
              const map = mapInstanceRef.current;
              if (!map) return;
              
              // Pan by ~100 pixels worth of map distance
              const panAmount = 150;
              
              switch (direction) {
                case 'up':
                  map.panBy(0, -panAmount);
                  break;
                case 'down':
                  map.panBy(0, panAmount);
                  break;
                case 'left':
                  map.panBy(-panAmount, 0);
                  break;
                case 'right':
                  map.panBy(panAmount, 0);
                  break;
              }
            }}
          />

          {/* Zoom buttons moved to FAB taskbar (zoomIn/zoomOut in right column) */}

          {/* ========== OLD FAB STRUCTURE ========== */}
          {/* Wrapped in conditional - set useNewFabManager = false to revert */}
          {!useNewFabManager && (
            <>
              {/* Map Controls - Top Center - Theme-aware */}
              <Box sx={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: Z_INDEX.CONTROLS,
                display: 'flex',
                gap: 1.5,
                background: cyber.gradientCard,
                backdropFilter: 'blur(12px)',
                border: `2px solid ${cyber.neonBlue}`,
                borderRadius: 3,
                p: 1.5,
                boxShadow: cyber.shadowElevated
              }}>

                {/* Landmark Route Button - Opens landmark panel */}
                <Tooltip title={isDrawing ? 'Close landmark menu' : 'Build landmark route'}>
              <IconButton
                onClick={() => {
                  setIsDrawing(!isDrawing);
                  telegram.hapticFeedback('light');
                }}
                sx={{
                  width: 56,
                  height: 56,
                  color: isDrawing ? cyber.neonCyan : cyber.textMuted,
                  backgroundColor: isDrawing ? `${cyber.neonCyan}30` : 'transparent',
                  border: `2px solid ${isDrawing ? cyber.neonCyan : cyber.borderDefault}`,
                  borderRadius: 2,
                  boxShadow: isDrawing ? (darkMode ? `0 0 15px ${cyber.neonCyan}60` : `0 0 8px ${cyber.neonCyan}40`) : 'none',
                  '&:hover': {
                    backgroundColor: `${cyber.neonCyan}20`,
                    borderColor: cyber.neonCyan
                  }
                }}
              >
                <FlagIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Home Button - Top Left */}
          <Tooltip title="Go Home" placement="right">
            <IconButton
              onClick={() => { telegram.hapticFeedback('light'); onClose(); }}
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: Z_INDEX.CONTROLS,
                width: 56,
                height: 56,
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(12px)',
                color: cyber.neonCyan,
                border: `2px solid ${cyber.neonCyan}`,
                borderRadius: 2,
                boxShadow: `0 0 20px ${cyber.neonCyan}40`,
                '&:hover': {
                  backgroundColor: `${cyber.neonCyan}20`,
                  boxShadow: `0 0 25px ${cyber.neonCyan}60`,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <HomeIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>

          {/* Heavy Haul / Hazmat Mode Toggle - Top Right */}
          <Tooltip 
            title={heavyHaulMode 
              ? "🚛 Heavy Haul Mode ON - Routes avoid highways & tolls for oversized loads" 
              : "Enable Heavy Haul Mode for semis, oversized loads, and heavy equipment"
            } 
            placement="left"
          >
            <IconButton
              onClick={() => {
                setHeavyHaulMode(!heavyHaulMode);
                telegram.hapticFeedback('medium');
                setToast({
                  open: true,
                  message: !heavyHaulMode 
                    ? '🚛 Heavy Haul Mode ON - Routes optimized for oversized loads' 
                    : '🚗 Standard routing enabled',
                  severity: !heavyHaulMode ? 'warning' : 'info'
                });
                // Clear existing route so user recalculates with new mode
                if (directionsRendererRef.current) {
                  directionsRendererRef.current.setMap(null);
                  directionsRendererRef.current = null;
                }
                setRouteInfo(null);
              }}
              sx={{
                position: 'absolute',
                top: 16, // Top right
                right: 16,
                zIndex: Z_INDEX.CONTROLS,
                width: 56,
                height: 56,
                backgroundColor: heavyHaulMode ? '#f97316' : cyber.bgSecondary, // Orange when active
                color: heavyHaulMode ? '#000000' : '#f97316',
                border: `2px solid #f97316`,
                borderRadius: 2,
                boxShadow: heavyHaulMode 
                  ? `0 0 25px #f9731680, 0 0 50px #f9731640` 
                  : `0 0 15px #f9731630`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: heavyHaulMode ? '#ea580c' : `#f9731620`,
                  boxShadow: `0 0 30px #f9731680`,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>

          {/* My Location FAB - Vehicle icon to zoom to current position - Bottom of right FAB stack - COMMENTED OUT
          <Tooltip title={`Go to my location (${vehicleInfo[userVehicle].name})`} placement="left">
            <IconButton
              onClick={async () => {
                telegram.hapticFeedback('medium');
                const map = mapInstanceRef.current;
                if (!map) return;
                
                try {
                  const result = await requestPreciseLocation({
                    desiredAccuracyMeters: 25,
                    refinementTimeoutMs: 6000,
                    allowTelegramFallback: true,
                    allowLastKnownFallback: true,
                  });

                  if (!result.fix) {
                    telegram.hapticFeedback('error');
                    const message = result.reason ?? 'Unable to get location';
                    setToast({ open: true, message, severity: 'error' });
                    return;
                  }

                  map.panTo({ lat: result.fix.lat, lng: result.fix.lng });
                  map.setZoom(16);
                  telegram.hapticFeedback('success');

                  if (!result.fulfilled && typeof result.fix.accuracy === 'number') {
                    setToast({ open: true, message: `📍 Location ±${Math.round(result.fix.accuracy)}m. Move for better GPS if needed.` });
                  } else {
                    setToast({ open: true, message: `📍 Centered on your location` });
                  }
                } catch (error) {
                  telegram.hapticFeedback('error');
                  console.error('Vehicle recenter precise location error:', error);
                  const message = error instanceof Error ? error.message : 'Unable to get location';
                  setToast({ open: true, message, severity: 'error' });
                }
              }}
              sx={{
                position: 'absolute',
                bottom: 24,
                right: 16,
                zIndex: Z_INDEX.CONTROLS,
                width: 56,
                height: 56,
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                color: cyber.neonGreen,
                border: `2px solid ${cyber.neonGreen}`,
                borderRadius: 2,
                boxShadow: `0 0 15px ${cyber.neonGreen}40`,
                '&:hover': {
                  backgroundColor: `${cyber.neonGreen}20`,
                  boxShadow: `0 0 25px ${cyber.neonGreen}60`,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              {getVehicleMuiIcon(userVehicle, 28)}
            </IconButton>
          </Tooltip>
          */}

          {/* Premium/Money FAB - Green with $ icon - 2nd from bottom */}
          <Tooltip title="Premium Features" placement="left">
            <IconButton
              onClick={() => {
                telegram.hapticFeedback('medium');
                // TODO: Open premium features dialog
                console.log('Premium features clicked');
              }}
              sx={{
                position: 'absolute',
                bottom: 88,
                right: 16,
                zIndex: Z_INDEX.CONTROLS,
                width: 56,
                height: 56,
                borderRadius: 2,
                backgroundColor: cyber.neonGreen,
                color: '#fff',
                border: `2px solid ${cyber.neonGreen}`,
                boxShadow: `0 4px 20px ${cyber.neonGreen}60`,
                '&:hover': {
                  backgroundColor: '#059669',
                  boxShadow: `0 6px 30px ${cyber.neonGreen}80`,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <AttachMoneyIcon sx={{ fontSize: 32 }} />
            </IconButton>
          </Tooltip>

          {/* Add Route Photo FAB - Dark Gray Camera - 3rd from bottom */}
          <Tooltip 
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>📍 Add Route Photo</Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                  Press P or right-click map
                </Typography>
              </Box>
            }
            placement="left"
          >
            <IconButton
              onClick={() => {
                const map = mapInstanceRef.current;
                if (map) {
                  const center = map.getCenter();
                  if (center) {
                    setRoutePhotoLocation({ lat: center.lat(), lng: center.lng() });
                    setShowCommunityRoutes(true);
                    telegram.hapticFeedback('light');
                  }
                }
              }}
              sx={{
                position: 'absolute',
                bottom: 152,
                right: 16,
                zIndex: Z_INDEX.CONTROLS,
                width: 56,
                height: 56,
                borderRadius: 2,
                backgroundColor: 'rgba(55, 65, 81, 0.95)',
                color: '#fff',
                border: '2px solid rgba(75, 85, 99, 0.8)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
                '&:hover': {
                  backgroundColor: 'rgba(75, 85, 99, 1)',
                  boxShadow: '0 6px 25px rgba(0, 0, 0, 0.5)',
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <AddAPhotoIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>

          {/* Settings FAB - 4th from bottom - Opens mini FABs at bottom */}
          <Tooltip title={showSettingsMenu ? "Close Settings" : "Settings"} placement="left">
            <IconButton
              onClick={() => {
                telegram.hapticFeedback('light');
                setShowSettingsMenu(prev => !prev);
              }}
              sx={{
                position: 'absolute',
                bottom: 216,
                right: 16,
                zIndex: Z_INDEX.CONTROLS,
                width: 56,
                height: 56,
                backgroundColor: showSettingsMenu ? `${cyber.neonPurple}30` : 'rgba(15, 23, 42, 0.95)',
                color: cyber.neonPurple,
                border: `2px solid ${cyber.neonPurple}`,
                borderRadius: 2,
                boxShadow: showSettingsMenu ? `0 0 25px ${cyber.neonPurple}60` : `0 0 15px ${cyber.neonPurple}40`,
                transform: showSettingsMenu ? 'rotate(90deg)' : 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: `${cyber.neonPurple}20`,
                  boxShadow: `0 0 25px ${cyber.neonPurple}60`,
                  transform: showSettingsMenu ? 'rotate(90deg) scale(1.05)' : 'scale(1.05)',
                },
                '&:active': {
                  transform: showSettingsMenu ? 'rotate(90deg) scale(0.95)' : 'scale(0.95)',
                },
              }}
            >
              <SettingsIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>

          {/* Settings Mini FABs - Fixed at bottom of screen (like HomeSpeedDial) */}
          {showSettingsMenu && (
            <>
              {/* Backdrop to close menu */}
              <Box
                onClick={() => setShowSettingsMenu(false)}
                sx={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 1199,
                  backgroundColor: 'transparent',
                }}
              />
              
              {/* Mini FABs row - isolated to prevent glow effects bleeding onto map */}
              <Box
                sx={{
                  position: 'fixed',
                  bottom: 100,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 1,
                  zIndex: 1200,
                  isolation: 'isolate', // Prevent glow effects from bleeding onto map
                  animation: 'slideUp 0.2s ease-out',
                  '@keyframes slideUp': {
                    from: { opacity: 0, transform: 'translateX(-50%) translateY(20px)' },
                    to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
                  },
                }}
              >
                {/* Offline Cache */}
                <Tooltip title="Offline Maps" arrow placement="top">
                  <IconButton
                    onClick={() => {
                      telegram.hapticFeedback('light');
                      setShowOfflineCacheManager(true);
                      setShowSettingsMenu(false);
                    }}
                    sx={{
                      width: 52,
                      height: 52,
                      background: `linear-gradient(135deg, ${cyber.neonCyan}20 0%, ${cyber.neonCyan}40 100%)`,
                      border: `3px solid ${cyber.neonCyan}`,
                      boxShadow: `0 0 15px ${cyber.neonCyan}60`,
                      color: cyber.neonCyan,
                      animation: 'popIn 0.2s ease-out 0s both',
                      '@keyframes popIn': {
                        from: { opacity: 0, transform: 'scale(0.5)' },
                        to: { opacity: 1, transform: 'scale(1)' },
                      },
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: `0 0 25px ${cyber.neonCyan}80`,
                      },
                    }}
                  >
                    <CloudDownloadIcon />
                  </IconButton>
                </Tooltip>

                {/* Dark/Light Mode Toggle */}
                <Tooltip title={darkMode ? "Light Mode" : "Dark Mode"} arrow placement="top">
                  <IconButton
                    onClick={() => {
                      telegram.hapticFeedback('light');
                      toggleDarkMode();
                      setShowSettingsMenu(false);
                    }}
                    sx={{
                      width: 52,
                      height: 52,
                      background: `linear-gradient(135deg, ${cyber.neonYellow}20 0%, ${cyber.neonYellow}40 100%)`,
                      border: `3px solid ${cyber.neonYellow}`,
                      boxShadow: `0 0 15px ${cyber.neonYellow}60`,
                      color: cyber.neonYellow,
                      animation: 'popIn 0.2s ease-out 0.05s both',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: `0 0 25px ${cyber.neonYellow}80`,
                      },
                    }}
                  >
                    {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                  </IconButton>
                </Tooltip>

                {/* High Contrast Toggle */}
                <Tooltip title={highContrast ? "Normal Contrast" : "High Contrast"} arrow placement="top">
                  <IconButton
                    onClick={() => {
                      telegram.hapticFeedback('light');
                      toggleHighContrast();
                      setShowSettingsMenu(false);
                    }}
                    sx={{
                      width: 52,
                      height: 52,
                      background: highContrast 
                        ? `linear-gradient(135deg, ${cyber.neonGreen}20 0%, ${cyber.neonGreen}40 100%)`
                        : `linear-gradient(135deg, rgba(100,100,100,0.2) 0%, rgba(100,100,100,0.4) 100%)`,
                      border: `3px solid ${highContrast ? cyber.neonGreen : '#6b7280'}`,
                      boxShadow: `0 0 15px ${highContrast ? cyber.neonGreen : '#6b7280'}60`,
                      color: highContrast ? cyber.neonGreen : '#6b7280',
                      animation: 'popIn 0.2s ease-out 0.1s both',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: `0 0 25px ${highContrast ? cyber.neonGreen : '#6b7280'}80`,
                      },
                    }}
                  >
                    <ContrastIcon />
                  </IconButton>
                </Tooltip>

                {/* Sound Toggle */}
                <Tooltip title={soundEnabled ? "Mute Sounds" : "Enable Sounds"} arrow placement="top">
                  <IconButton
                    onClick={() => {
                      telegram.hapticFeedback('light');
                      setSoundEnabled(!soundEnabled);
                      setShowSettingsMenu(false);
                    }}
                    sx={{
                      width: 52,
                      height: 52,
                      background: soundEnabled 
                        ? `linear-gradient(135deg, ${cyber.neonBlue}20 0%, ${cyber.neonBlue}40 100%)`
                        : `linear-gradient(135deg, rgba(100,100,100,0.2) 0%, rgba(100,100,100,0.4) 100%)`,
                      border: `3px solid ${soundEnabled ? cyber.neonBlue : '#6b7280'}`,
                      boxShadow: `0 0 15px ${soundEnabled ? cyber.neonBlue : '#6b7280'}60`,
                      color: soundEnabled ? cyber.neonBlue : '#6b7280',
                      animation: 'popIn 0.2s ease-out 0.15s both',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        boxShadow: `0 0 25px ${soundEnabled ? cyber.neonBlue : '#6b7280'}80`,
                      },
                    }}
                  >
                    {soundEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}

          {/* Custom Cyberpunk Zoom Controls - Right Side (Large for Gloves) */}
          <Box sx={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: Z_INDEX.CONTROLS,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}>
            {/* Reset Map View - Clears UI and returns to default view */}
            <Tooltip title="Reset Map View" placement="left">
              <IconButton
                onClick={() => {
                  telegram.hapticFeedback('medium');
                  // Reset all UI states
                  setSelectedWell(null);
                  setShowSearchDialog(false);
                  setSearchQuery('');
                  setSearchResults([]);
                  setRouteInfo(null);
                  if (directionsRendererRef.current) {
                    directionsRendererRef.current.setMap(null);
                    directionsRendererRef.current = null;
                  }
                  // Reset map to default view
                  const map = mapInstanceRef.current;
                  if (map) {
                    map.setCenter({ lat: 34.5, lng: -106.0 });
                    map.setZoom(7);
                  }
                  setToast({ open: true, message: '🏠 Map view reset', severity: 'info' });
                }}
                sx={{
                  width: 56,
                  height: 56,
                  backgroundColor: 'rgba(15, 23, 42, 0.9)',
                  backdropFilter: 'blur(12px)',
                  border: `2px solid ${cyber.neonBlue}`,
                  borderRadius: 2,
                  color: cyber.neonBlue,
                  boxShadow: `0 0 15px ${cyber.neonBlue}40, inset 0 0 10px ${cyber.neonBlue}15`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(15, 23, 42, 0.95)',
                    borderColor: cyber.neonCyan,
                    color: cyber.neonCyan,
                    boxShadow: `0 0 25px ${cyber.neonCyan}60, inset 0 0 15px ${cyber.neonCyan}25`,
                    transform: 'scale(1.05)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                  },
                }}
              >
                <ExploreIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </Tooltip>

            {/* Recenter to Basin View - Centers user in their basin at readable zoom */}
            <Tooltip title="Recenter to Basin" placement="left">
              <IconButton
                onClick={async () => {
                  const map = mapInstanceRef.current;
                  if (!map) return;

                  try {
                    const result = await requestPreciseLocation({
                      desiredAccuracyMeters: 200,
                      refinementTimeoutMs: 5000,
                      allowLastKnownFallback: true,
                      allowTelegramFallback: true,
                    });

                    if (!result.fix) {
                      throw new Error(result.reason ?? 'Location unavailable');
                    }

                    map.setCenter({ lat: result.fix.lat, lng: result.fix.lng });
                    map.setZoom(9);
                    telegram.hapticFeedback('success');
                  } catch (error) {
                    map.setCenter({ lat: 34.5, lng: -106.0 });
                    map.setZoom(8);
                    telegram.hapticFeedback('light');
                    const message = error instanceof Error ? error.message : 'Location unavailable';
                    setToast({ open: true, message: `📍 ${message}. Showing New Mexico overview.` });
                  }
                }}
                sx={{
                  width: 56,
                  height: 56,
                  backgroundColor: 'rgba(15, 23, 42, 0.9)',
                  backdropFilter: 'blur(12px)',
                  border: `2px solid #f97316`,
                  borderRadius: 2,
                  color: '#f97316',
                  boxShadow: `0 0 15px #f9731640, inset 0 0 10px #f9731615`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(15, 23, 42, 0.95)',
                    borderColor: '#fb923c',
                    color: '#fb923c',
                    boxShadow: `0 0 25px #fb923c60, inset 0 0 15px #fb923c25`,
                    transform: 'scale(1.05)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)',
                    boxShadow: `0 0 30px #22c55e70, inset 0 0 20px #22c55e30`,
                    borderColor: '#22c55e',
                    color: '#22c55e',
                  },
                }}
              >
                <CenterFocusStrongIcon sx={{ fontSize: 28 }} />
              </IconButton>
            </Tooltip>

            {/* Zoom In */}
            <IconButton
              onClick={() => {
                const map = mapInstanceRef.current;
                if (map) {
                  // If a facility is selected, zoom all the way in and center on it
                  if (selectedWell) {
                    map.setCenter({ lat: selectedWell.latitude, lng: selectedWell.longitude });
                    map.setZoom(20);
                    telegram.hapticFeedback('success');
                  } else {
                    const zoom = map.getZoom() || 10;
                    map.setZoom(Math.min(zoom + 1, 21));
                    telegram.hapticFeedback('light');
                  }
                }
              }}
              sx={{
                width: 56,
                height: 56,
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(12px)',
                border: `2px solid ${cyber.neonCyan}`,
                borderRadius: 2,
                color: cyber.neonCyan,
                boxShadow: `0 0 15px ${cyber.neonCyan}40, inset 0 0 10px ${cyber.neonCyan}15`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  borderColor: cyber.neonBlue,
                  color: cyber.neonBlue,
                  boxShadow: `0 0 25px ${cyber.neonBlue}60, inset 0 0 15px ${cyber.neonBlue}25`,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                  boxShadow: `0 0 30px ${cyber.neonGreen}70, inset 0 0 20px ${cyber.neonGreen}30`,
                  borderColor: cyber.neonGreen,
                  color: cyber.neonGreen,
                },
              }}
            >
              <AddIcon sx={{ fontSize: 32, fontWeight: 700 }} />
            </IconButton>

            {/* Zoom Out */}
            <IconButton
              onClick={() => {
                const map = mapInstanceRef.current;
                if (map) {
                  const zoom = map.getZoom() || 10;
                  map.setZoom(Math.max(zoom - 1, 1));
                  telegram.hapticFeedback('light');
                }
              }}
              sx={{
                width: 56,
                height: 56,
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(12px)',
                border: `2px solid ${cyber.neonCyan}`,
                borderRadius: 2,
                color: cyber.neonCyan,
                boxShadow: `0 0 15px ${cyber.neonCyan}40, inset 0 0 10px ${cyber.neonCyan}15`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  borderColor: cyber.neonBlue,
                  color: cyber.neonBlue,
                  boxShadow: `0 0 25px ${cyber.neonBlue}60, inset 0 0 15px ${cyber.neonBlue}25`,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                  boxShadow: `0 0 30px ${cyber.neonGreen}70, inset 0 0 20px ${cyber.neonGreen}30`,
                  borderColor: cyber.neonGreen,
                  color: cyber.neonGreen,
                },
              }}
            >
              <RemoveIcon sx={{ fontSize: 32, fontWeight: 700 }} />
            </IconButton>

            {/* Map Style Toggle */}
            <IconButton
              onClick={() => {
                setMapStyle(prev => {
                  const styles: MapStyleType[] = ['cyber', 'satellite', 'terrain', 'night', 'highcontrast', 'hybrid', 'desert', 'roads', 'topo'];
                  const currentIdx = styles.indexOf(prev);
                  return styles[(currentIdx + 1) % styles.length];
                });
                telegram.hapticFeedback('light');
              }}
              sx={{
                width: 56,
                height: 56,
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(12px)',
                border: `2px solid ${cyber.neonPurple}`,
                borderRadius: 2,
                color: cyber.neonPurple,
                boxShadow: `0 0 15px ${cyber.neonPurple}40, inset 0 0 10px ${cyber.neonPurple}15`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  borderColor: cyber.neonPink,
                  color: cyber.neonPink,
                  boxShadow: `0 0 25px ${cyber.neonPink}60, inset 0 0 15px ${cyber.neonPink}25`,
                  transform: 'scale(1.05)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                  boxShadow: `0 0 30px ${cyber.neonGreen}70, inset 0 0 20px ${cyber.neonGreen}30`,
                },
              }}
            >
              <LayersIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Box>

          {/* Caliche Routes Slider - Bottom Center-Right */}
          <Tooltip title={showRouteOverlays ? 'Hide Caliche Routes' : 'Show Caliche Routes'} placement="top">
            <Box
              onClick={() => !isLoadingRoutes && toggleRouteOverlays()}
              sx={{
                position: 'absolute',
                bottom: 24,
                right: 180,
                zIndex: Z_INDEX.CONTROLS,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(12px)',
                border: `2px solid ${showRouteOverlays ? cyber.neonCyan : cyber.neonOrange}`,
                borderRadius: 3,
                cursor: isLoadingRoutes ? 'not-allowed' : 'pointer',
                opacity: isLoadingRoutes ? 0.5 : 1,
                transition: 'all 0.3s ease',
                boxShadow: showRouteOverlays 
                  ? `0 0 20px ${cyber.neonCyan}60`
                  : `0 0 15px ${cyber.neonOrange}40`,
                '&:hover': {
                  borderColor: cyber.neonCyan,
                  boxShadow: `0 0 25px ${cyber.neonCyan}60`,
                },
              }}
            >
              {/* Route Icon */}
              {isLoadingRoutes ? (
                <CyberSpinner size={20} />
              ) : (
                <RouteIcon sx={{ fontSize: 22, color: showRouteOverlays ? cyber.neonCyan : cyber.textMuted }} />
              )}
              
              {/* Horizontal Slider Track */}
              <Box
                sx={{
                  width: 48,
                  height: 24,
                  borderRadius: 12,
                  backgroundColor: showRouteOverlays ? `${cyber.neonCyan}30` : 'rgba(100, 116, 139, 0.3)',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${showRouteOverlays ? cyber.neonCyan : 'rgba(100, 116, 139, 0.5)'}`,
                }}
              >
                {/* Slider Thumb */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 2,
                    left: showRouteOverlays ? 24 : 2,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: showRouteOverlays ? cyber.neonCyan : 'rgba(100, 116, 139, 0.8)',
                    boxShadow: showRouteOverlays 
                      ? `0 0 10px ${cyber.neonCyan}80` 
                      : '0 2px 4px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s ease',
                  }}
                />
              </Box>
              
              {/* Label */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: showRouteOverlays ? cyber.neonCyan : cyber.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  minWidth: 50,
                }}
              >
                Routes
              </Typography>
            </Box>
          </Tooltip>

          {/* Route Overlay Count Badge */}
          {showRouteOverlays && calicheRoutes.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                right: 80,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: Z_INDEX.CONTROLS,
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                backdropFilter: 'blur(12px)',
                border: `1px solid ${cyber.neonCyan}`,
                borderRadius: 2,
                px: 1.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <RouteIcon sx={{ fontSize: 16, color: cyber.neonCyan }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: cyber.neonCyan }}>
                {calicheRoutes.length} routes
              </Typography>
            </Box>
          )}
            </>
          )}

          {/* Route Creation FAB - Bottom Right */}
          <RouteCreationFAB
            mapCenter={mapInstanceRef.current ? (() => {
              const center = mapInstanceRef.current?.getCenter();
              return center ? { lat: center.lat(), lng: center.lng() } : null;
            })() : null}
            onRouteCreated={(routeId) => {
              console.log('Route created:', routeId);
              // Could refresh routes list or show notification
            }}
            darkMode={darkMode}
            highContrast={highContrast}
            facilities={favorites}
          />

          {/* Landmark FAB - Build routes with drag-drop landmarks */}
          <LandmarkFAB
            map={mapInstanceRef.current}
            facilityId={selectedWell?.api || nearestFacilityForRoute?.api || nearestFacilityForRoute?.id}
            facilityName={selectedWell?.wellName || nearestFacilityForRoute?.name}
            darkMode={darkMode}
            highContrast={highContrast}
            isOpen={isDrawing}
            onOpenChange={setIsDrawing}
            showFAB={false}  // FAB is now in toolbar, don't show floating FAB
            onPlaceDestinationAt={placeDestinationAtRef}
            onFirstLandmarkPlaced={(lat, lng) => {
              // Find nearest facility to auto-name the route
              console.log('[FavoritesMapView] Finding nearest facility to:', lat, lng);
              
              let nearestFacility: { id: string; api: string; name: string } | null = null;
              let minDistance = Infinity;
              
              // Check all favorites
              favorites.forEach(facility => {
                if (facility.latitude && facility.longitude) {
                  const distance = Math.sqrt(
                    Math.pow(lat - facility.latitude, 2) + 
                    Math.pow(lng - facility.longitude, 2)
                  );
                  
                  if (distance < minDistance) {
                    minDistance = distance;
                    nearestFacility = {
                      id: facility.id || '',
                      api: facility.api || '',  // Store API number explicitly
                      name: facility.wellName || 'Unknown'
                    };
                  }
                }
              });
              
              if (nearestFacility) {
                console.log('[FavoritesMapView] Found nearest facility:', nearestFacility);
                setNearestFacilityForRoute(nearestFacility);
              }
            }}
            onRouteCreated={async (routeId) => {
              console.log('Landmark route created:', routeId);
              setIsDrawing(false);  // Close panel after route created
              setNearestFacilityForRoute(null);  // Clear for next route
              // Notify parent to refresh caliche routes list
              if (onRouteCreated) {
                onRouteCreated();
              }
              // Auto-enable Caliche Routes layer to show the new route
              if (!showCalichemRoutes) {
                setShowCalichemRoutes(true);
              } else {
                // Refresh routes data to include the new route
                try {
                  const response = await fetch('/api/landmark-routes/all');
                  if (response.ok) {
                    const data = await response.json();
                    setCalichemRoutesData(data.routes || []);
                  }
                } catch (e) {
                  console.error('Failed to refresh routes:', e);
                }
              }
              setToast({
                open: true,
                message: '🛤️ Route saved! Toggle Caliche Routes layer to see all routes.',
                severity: 'success'
              });
            }}
            onRouteCancelled={() => {
              console.log('Landmark route cancelled');
              setIsDrawing(false);  // Close panel on cancel
              setNearestFacilityForRoute(null);  // Clear for next route
            }}
          />

          {/* Marker drag-to-trash overlay */}
          {draggingMarker && (
            <>
              {/* Trash drop zone - bottom center */}
              <Box
                ref={trashZoneRef}
                sx={{
                  position: 'fixed',
                  bottom: 100,
                  left: '50%',
                  width: 80,
                  height: 80,
                  zIndex: Z_INDEX.MODAL_CONTAINER + 15,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: `3px dashed ${isOverTrash ? '#ef4444' : cyber.textMuted}`,
                  background: isOverTrash 
                    ? 'rgba(239, 68, 68, 0.3)' 
                    : 'rgba(15, 23, 42, 0.7)',
                  backdropFilter: 'blur(8px)',
                  transition: 'all 0.2s ease',
                  transform: `translateX(-50%) scale(${isOverTrash ? 1.1 : 1})`,
                }}
              >
                <DeleteIcon
                  sx={{
                    fontSize: 36,
                    color: isOverTrash ? '#ef4444' : cyber.textMuted,
                    transition: 'color 0.2s ease',
                  }}
                />
              </Box>

              {/* Screen overlay to capture all events */}
              <Box
                sx={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: Z_INDEX.MODAL_CONTAINER + 10,
                  background: 'rgba(0,0,0,0.3)',
                  pointerEvents: 'none',
                }}
              />
            </>
          )}

          {/* Bottom-left container removed (favorites pill) */}
        </Box>

        {/* Drawing Canvas Modal (Leaflet) */}
        {showDrawingCanvas && (
          <Dialog
            open={showDrawingCanvas}
            onClose={() => setShowDrawingCanvas(false)}
            fullScreen
            PaperProps={{
              sx: {
                zIndex: Z_INDEX.MODAL_CONTAINER,
                backgroundColor: cyber.bgPrimary,
                backgroundImage: 'none'
              }
            }}
          >
            <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Leaflet Map Container */}
              <Box sx={{ flex: 1, position: 'relative', backgroundColor: cyber.bgPrimary }}>
                <div ref={leafletCanvasRef} style={{ width: '100%', height: '100%' }} />

              {/* Close Button */}
              <IconButton
                onClick={() => setShowDrawingCanvas(false)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: Z_INDEX.CONTROLS,
                  backgroundColor: 'rgba(15, 23, 42, 0.9)',
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(16, 185, 129, 0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(30, 41, 59, 0.95)',
                    borderColor: 'rgba(16, 185, 129, 0.5)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Instructions */}
              <Box sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: Z_INDEX.CONTROLS,
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '2px solid rgba(16, 185, 129, 0.3)',
                borderRadius: 2,
                p: 2,
                maxWidth: '300px'
              }}>
                <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 700, mb: 1 }}>
                  🎨 Drawing Canvas
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>
                  Use the polyline tool (top-left) to draw routes
                </Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                  Routes are saved to the database for all users viewing this facility
                </Typography>
              </Box>

              {/* Route Save Dialog */}
              {pendingRouteCoordinates && (
                <Dialog
                  open={!!pendingRouteCoordinates}
                  onClose={() => setPendingRouteCoordinates(null)}
                  PaperProps={{
                    sx: {
                      zIndex: Z_INDEX.MODAL_CONTAINER + 1,
                      backgroundColor: 'rgba(15, 23, 42, 0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '2px solid rgba(96, 165, 250, 0.3)',
                      borderRadius: 3
                    }
                  }}
                >
                  <Box sx={{ p: 3, minWidth: 350 }}>
                    <Typography variant="h6" sx={{ color: '#60a5fa', fontWeight: 700, mb: 2 }}>
                      Save Route
                    </Typography>
                    <TextField
                      fullWidth
                      label="Route Name"
                      value={routeName}
                      onChange={(e) => setRouteName(e.target.value)}
                      placeholder="e.g., Access Road to Antelope Ridge"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          color: '#f1f5f9',
                          '& fieldset': { borderColor: 'rgba(96, 165, 250, 0.3)' },
                          '&:hover fieldset': { borderColor: 'rgba(96, 165, 250, 0.5)' },
                          '&.Mui-focused fieldset': { borderColor: '#60a5fa' }
                        },
                        '& .MuiInputBase-input::placeholder': { color: 'rgba(148, 163, 184, 0.5)' }
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Description (Optional)"
                      multiline
                      rows={3}
                      placeholder="Add notes about this route..."
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          color: '#f1f5f9',
                          '& fieldset': { borderColor: 'rgba(96, 165, 250, 0.3)' },
                          '&:hover fieldset': { borderColor: 'rgba(96, 165, 250, 0.5)' },
                          '&.Mui-focused fieldset': { borderColor: '#60a5fa' }
                        },
                        '& .MuiInputBase-input::placeholder': { color: 'rgba(148, 163, 184, 0.5)' }
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setPendingRouteCoordinates(null)}
                        sx={{
                          flex: 1,
                          color: '#94a3b8',
                          borderColor: 'rgba(96, 165, 250, 0.3)',
                          '&:hover': { borderColor: 'rgba(96, 165, 250, 0.5)', backgroundColor: 'rgba(96, 165, 250, 0.1)' }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={async () => {
                          if (!routeName.trim()) {
                            alert('Please enter a route name');
                            return;
                          }
                          try {
                            const response = await fetch('/api/routes/create', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                facilityId: selectedWell?.id,
                                name: routeName.trim(),
                                coordinates: pendingRouteCoordinates,
                              })
                            });
                            const data = await response.json();
                            if (data.success) {
                              setPendingRouteCoordinates(null);
                              setRouteName('');
                              // Refresh routes list
                              leafletMapService.setOnDrawComplete((coordinates) => {
                                setPendingRouteCoordinates(coordinates);
                              });
                            } else {
                              alert(`Failed to save route: ${data.error}`);
                            }
                          } catch (error) {
                            console.error('Error saving route:', error);
                            alert('Error saving route');
                          }
                        }}
                        sx={{
                          flex: 1,
                          backgroundColor: '#10b981',
                          color: 'white',
                          '&:hover': { backgroundColor: '#059669' }
                        }}
                      >
                        Save Route
                      </Button>
                    </Box>
                  </Box>
                </Dialog>
              )}
            </Box>
          </Box>
        </Dialog>
        )}

        {/* Photo Uploader Modal */}
        {selectedWell && (
          <PhotoUploader
            open={showPhotoUploader}
            onClose={() => setShowPhotoUploader(false)}
            facilityId={selectedWell.api || selectedWell.id}
            facilityType={(selectedWell as any).type || 'well'}
            onSuccess={() => {
              // Photo refresh handled by RoutePhotosGallery
            }}
          />
        )}

        {addFacilityLocation && (
          <AddFacilityForm
            open={showAddFacilityForm}
            onClose={() => {
              setShowAddFacilityForm(false);
              setAddFacilityLocation(null);
            }}
            latitude={addFacilityLocation.lat}
            longitude={addFacilityLocation.lng}
            onSuccess={() => {
              setToast({ open: true, message: 'Facility submitted! We will review and publish soon.', severity: 'success' });
            }}
            onViewExistingFacility={(facility) => {
              setShowAddFacilityForm(false);
              setAddFacilityLocation(null);
              if (facility.api) {
                setSearchQuery(facility.api);
                setShowSearchDialog(true);
                handleSearch(facility.api);
              }
            }}
            onViewOnMap={(lat, lng, facilityName) => {
              setShowAddFacilityForm(false);
              setAddFacilityLocation(null);
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setCenter({ lat, lng });
                mapInstanceRef.current.setZoom(18);
              }
              const tempWell: Well = {
                id: `new-facility-${Date.now()}`,
                api: '',
                wellName: facilityName,
                latitude: lat,
                longitude: lng,
                county: '',
                operator: '',
                status: 'active',
                basin: '',
                type: 'facility',
              };
              setSelectedWell(tempWell);
            }}
            onAddToFavorites={(facility) => {
              const favoriteWell: Well = {
                id: facility.id,
                api: facility.api || '',
                wellName: facility.name,
                latitude: facility.latitude,
                longitude: facility.longitude,
                county: facility.county || '',
                operator: facility.operator || '',
                status: 'active',
                basin: '',
                type: facility.facilityType,
              };
              addFavorite(favoriteWell);
              setToast({ open: true, message: `⭐ ${facility.name} added to favorites!` });
            }}
            onOpenCommunityFacilities={() => {
              telegram.hapticFeedback('light');
              setToast({
                open: true,
                message: 'Community Facilities dialog opening soon!',
                severity: 'info',
              });
            }}
          />
        )}
      </Box>
    </Dialog>

      {/* Global Route Photos Modal */}
      <Dialog
        open={showRoutePhotosModal}
        onClose={() => setShowRoutePhotosModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cyber.bgSecondary,
            border: `1px solid ${cyber.neonPurple}`,
            boxShadow: `0 0 30px ${cyber.neonPurple}40`,
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ color: cyber.textPrimary, fontWeight: 700 }}>
          Route Photos
          <Button
            size="small"
            onClick={loadGlobalRoutePhotos}
            disabled={isLoadingRoutePhotos}
            sx={{ ml: 2, textTransform: 'none', color: cyber.neonPurple }}
          >
            Refresh
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingRoutePhotos ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : routePhotoFeed.length === 0 ? (
            <Typography sx={{ color: cyber.textMuted, textAlign: 'center', py: 3 }}>
              No route photos nearby yet. Add one by clicking on a facility icon on the map and adding a route photo!
            </Typography>
          ) : (
            <ImageList variant="masonry" cols={3} gap={12}>
              {routePhotoFeed.map((photo) => (
                <ImageListItem key={photo.id} sx={{ cursor: 'pointer' }} onClick={() => window.open(photo.fullUrl, '_blank')}>
                  <img
                    src={photo.thumbnailUrl}
                    alt={photo.description || photo.facility_id}
                    loading="lazy"
                    style={{ borderRadius: 8, border: `1px solid ${cyber.borderDefault}` }}
                  />
                  <ImageListItemBar
                    title={photo.description || 'Route photo'}
                    subtitle={photo.facility_id}
                    position="below"
                    sx={{
                      '.MuiImageListItemBar-title': { color: cyber.textPrimary, fontSize: '0.9rem', fontWeight: 700 },
                      '.MuiImageListItemBar-subtitle': { color: cyber.textMuted, fontSize: '0.75rem' },
                    }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRoutePhotosModal(false)} sx={{ color: cyber.textMuted }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Selection Menu */}
      <Dialog
        open={showVehicleMenu}
        onClose={() => setShowVehicleMenu(false)}
        PaperProps={{
          sx: {
            backgroundColor: cyber.bgSecondary,
            border: `1px solid ${cyber.neonGreen}`,
            borderRadius: 3,
            boxShadow: `0 0 30px ${cyber.neonGreen}40`,
            maxWidth: 360,
            width: '90%'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: cyber.neonGreen, 
          textAlign: 'center',
          borderBottom: `1px solid ${cyber.borderDefault}`,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1
        }}>
          <DirectionsCarIcon sx={{ fontSize: 28 }} />
          Choose Your Vehicle
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: 1.5,
            mt: 1
          }}>
            {(Object.keys(vehicleInfo) as VehicleType[]).map((type) => (
              <Button
                key={type}
                variant={userVehicle === type ? 'contained' : 'outlined'}
                onClick={() => {
                  setUserVehicle(type);
                  setShowVehicleMenu(false);
                  telegram.hapticFeedback('success');
                  setToast({ 
                    open: true, 
                    message: `${vehicleInfo[type].name} selected!` 
                  });
                }}
                sx={{
                  flexDirection: 'column',
                  py: 1.5,
                  px: 1,
                  minHeight: 85,
                  backgroundColor: userVehicle === type ? cyber.neonGreen : 'transparent',
                  borderColor: userVehicle === type ? cyber.neonGreen : cyber.borderDefault,
                  color: userVehicle === type ? cyber.bgPrimary : cyber.textPrimary,
                  '&:hover': {
                    backgroundColor: userVehicle === type ? cyber.neonGreen : `${cyber.neonGreen}20`,
                    borderColor: cyber.neonGreen,
                    boxShadow: `0 0 15px ${cyber.neonGreen}40`
                  }
                }}
              >
                <Box sx={{ mb: 0.5 }}>
                  {getVehicleMuiIcon(type, 32, userVehicle === type)}
                </Box>
                <Typography sx={{ 
                  fontSize: '0.65rem', 
                  textTransform: 'none',
                  fontWeight: userVehicle === type ? 600 : 400,
                  lineHeight: 1.2
                }}>
                  {vehicleInfo[type].name}
                </Typography>
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'center', 
          borderTop: `1px solid ${cyber.borderDefault}`,
          pt: 1.5,
          pb: 2
        }}>
          <Button 
            onClick={() => setShowVehicleMenu(false)}
            sx={{ color: cyber.textSecondary }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast for copy feedback */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2000}
        onClose={() => setToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '60px', zIndex: Z_INDEX.NOTIFICATIONS }}
      >
        <Alert severity="success" sx={{ backgroundColor: 'rgba(34, 197, 94, 0.9)', color: 'white' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Weather Warning Notifications (high UV, extreme temp, high wind) */}
      <Snackbar
        open={weatherWarning.open}
        autoHideDuration={8000}
        onClose={() => setWeatherWarning(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '80px !important', zIndex: Z_INDEX.NOTIFICATIONS }}
      >
        <Alert 
          severity={weatherWarning.severity} 
          variant="filled"
          onClose={() => setWeatherWarning(prev => ({ ...prev, open: false }))}
          sx={{ 
            width: '100%',
            fontSize: '0.95rem',
            fontWeight: 600,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}
        >
          {weatherWarning.message}
        </Alert>
      </Snackbar>

      {/* Right-click Context Menu for adding route photos */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              backgroundColor: cyber.bgSecondary,
              border: `1px solid ${cyber.borderDefault}`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              borderRadius: 2
            }
          }
        }}
      >
        <MenuItem
          onClick={() => {
            if (contextMenu) {
              setRoutePhotoLocation({ lat: contextMenu.lat, lng: contextMenu.lng });
              setShowCommunityRoutes(true);
              telegram.hapticFeedback('light');
            }
            setContextMenu(null);
          }}
          sx={{ color: cyber.textPrimary }}
        >
          <ListItemIcon>
            <AddAPhotoIcon sx={{ color: cyber.neonBlue }} />
          </ListItemIcon>
          <ListItemText primary="📍 Add Route Photo Here" />
        </MenuItem>
      </Menu>

      {/* Right-click Context Menu for markers - Delete from favorites */}
      <Menu
        open={markerContextMenu !== null}
        onClose={() => setMarkerContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          markerContextMenu !== null
            ? { top: markerContextMenu.y, left: markerContextMenu.x }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              backgroundColor: cyber.bgSecondary,
              border: `1px solid ${cyber.neonRed}40`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              borderRadius: 2
            }
          }
        }}
      >
        {markerContextMenu && (
          <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${cyber.borderDefault}` }}>
            <Typography sx={{ color: cyber.neonBlue, fontWeight: 700, fontSize: '0.85rem' }}>
              {markerContextMenu.well.wellName}
            </Typography>
            <Typography sx={{ color: cyber.textMuted, fontSize: '0.7rem' }}>
              {markerContextMenu.well.type || 'Well'}
            </Typography>
          </Box>
        )}
        <MenuItem
          onClick={() => {
            if (markerContextMenu) {
              removeFavorite(markerContextMenu.well.id);
              telegram.hapticFeedback('success');
              setToast({ open: true, message: `🗑️ Removed "${markerContextMenu.well.wellName}" from favorites` });
            }
            setMarkerContextMenu(null);
          }}
          sx={{ color: cyber.neonRed }}
        >
          <ListItemIcon>
            <DeleteIcon sx={{ color: cyber.neonRed }} />
          </ListItemIcon>
          <ListItemText primary="Remove from Favorites" />
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (markerContextMenu) {
              // Select the well and start in-app navigation
              setSelectedWell(markerContextMenu.well);
              await showRouteToWell(markerContextMenu.well);
              setTimeout(() => {
                if (navigationSteps.length > 0) {
                  startNavigation();
                }
              }, 500);
              telegram.hapticFeedback('light');
            }
            setMarkerContextMenu(null);
          }}
          sx={{ color: cyber.textPrimary }}
        >
          <ListItemIcon>
            <NavigationIcon sx={{ color: cyber.neonGreen }} />
          </ListItemIcon>
          <ListItemText primary="Navigate (In-App)" />
        </MenuItem>
      </Menu>

      {/* Save Drawn Route Dialog */}
      <Dialog
        open={showSaveRouteDialog}
        onClose={() => {
          if (!isSavingRoute) {
            setShowSaveRouteDialog(false);
            setRouteName('');
            setRouteDescription('');
            // Remove the drawn polyline from the map
            if (polylineRef.current) {
              polylineRef.current.setMap(null);
              polylineRef.current = null;
            }
          }
        }}
        PaperProps={{
          sx: {
            backgroundColor: cyber.bgSecondary,
            backgroundImage: 'none',
            border: `2px solid ${cyber.neonBlue}40`,
            borderRadius: 3,
            boxShadow: cyber.shadowElevated,
            minWidth: 380
          }
        }}
      >
        <DialogTitle sx={{ color: cyber.neonBlue, fontWeight: 700, pb: 1 }}>
          💾 Save Your Route
        </DialogTitle>
        <DialogContent>
          {/* Simplification feedback */}
          <Box sx={{ 
            mb: 2, 
            p: 1.5, 
            borderRadius: 2, 
            backgroundColor: `${cyber.neonGreen}15`,
            border: `1px solid ${cyber.neonGreen}40`
          }}>
            <Typography variant="body2" sx={{ color: cyber.neonGreen, fontWeight: 600 }}>
              ✨ Route optimized: {originalPointCount} → {drawnRoutePoints.length} points
            </Typography>
            <Typography variant="caption" sx={{ color: cyber.textMuted }}>
              Cleaned up over-clicked areas automatically
            </Typography>
          </Box>

          <TextField
            autoFocus
            fullWidth
            label="Route Name"
            value={routeName}
            onChange={(e) => setRouteName(e.target.value)}
            placeholder="e.g., Access Road from Highway 285"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: cyber.textPrimary,
                '& fieldset': { borderColor: cyber.borderDefault },
                '&:hover fieldset': { borderColor: `${cyber.neonBlue}80` },
                '&.Mui-focused fieldset': { borderColor: cyber.neonBlue }
              },
              '& .MuiInputLabel-root': { color: cyber.textMuted },
              '& .MuiInputBase-input::placeholder': { color: cyber.textMuted }
            }}
          />
          
          <TextField
            fullWidth
            label="Description (Optional)"
            value={routeDescription}
            onChange={(e) => setRouteDescription(e.target.value)}
            multiline
            rows={2}
            placeholder="e.g., Turn left at the cattle guard, follow the dirt road..."
            sx={{
              '& .MuiOutlinedInput-root': {
                color: cyber.textPrimary,
                '& fieldset': { borderColor: cyber.borderDefault },
                '&:hover fieldset': { borderColor: `${cyber.neonBlue}80` },
                '&.Mui-focused fieldset': { borderColor: cyber.neonBlue }
              },
              '& .MuiInputLabel-root': { color: cyber.textMuted },
              '& .MuiInputBase-input::placeholder': { color: cyber.textMuted }
            }}
          />
          
          {selectedWell && (
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: cyber.textMuted }}>
              This route will be saved for: <strong style={{ color: cyber.neonCyan }}>{selectedWell.wellName || selectedWell.id}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => {
              setShowSaveRouteDialog(false);
              setRouteName('');
              setRouteDescription('');
              // Remove the drawn polyline
              if (polylineRef.current) {
                polylineRef.current.setMap(null);
                polylineRef.current = null;
              }
            }}
            disabled={isSavingRoute}
            sx={{ 
              color: cyber.textMuted,
              '&:hover': { backgroundColor: `${cyber.neonRed}20`, color: cyber.neonRed }
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            disabled={!routeName.trim() || isSavingRoute}
            onClick={async () => {
              if (!routeName.trim() || !selectedWell) return;
              
              setIsSavingRoute(true);
              try {
                // Convert to the format expected by the API
                const coordinates = drawnRoutePoints.map(p => [p.lat, p.lng] as [number, number]);
                
                const response = await fetch('/api/routes-v2', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    facilityId: selectedWell.api || selectedWell.id,
                    name: routeName.trim(),
                    description: routeDescription.trim() || null,
                    coordinates,
                    isPublic: true // Make route visible to all users
                  })
                });
                
                const data = await response.json();
                
                if (data.success || data.id) {
                  telegram.hapticFeedback('success');
                  setToast({ open: true, message: `✅ Route "${routeName}" saved!` });
                  setShowSaveRouteDialog(false);
                  setRouteName('');
                  setRouteDescription('');
                  setDrawnRoutePoints([]);
                  
                  // Keep the polyline on map as a visual confirmation
                  if (polylineRef.current) {
                    polylineRef.current.setOptions({
                      strokeColor: cyber.neonGreen,
                      strokeOpacity: 0.9
                    });
                  }
                } else {
                  throw new Error(data.error || 'Failed to save route');
                }
              } catch (error) {
                console.error('Error saving route:', error);
                telegram.hapticFeedback('error');
                setToast({ open: true, message: `❌ Error: ${error instanceof Error ? error.message : 'Failed to save'}` });
              } finally {
                setIsSavingRoute(false);
              }
            }}
            sx={{
              backgroundColor: cyber.neonGreen,
              color: '#000',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#22c55e' },
              '&:disabled': { backgroundColor: cyber.borderDefault, color: cyber.textMuted }
            }}
          >
            {isSavingRoute ? 'Saving...' : 'Save Route'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Search Facilities Dialog - Same size as NearbyServicesWidget */}
      <Dialog
        open={showSearchDialog}
        onClose={() => {
          setShowSearchDialog(false);
          setSearchQuery('');
          setSearchResults([]);
          setSearchError(null);
          setAddressPredictions([]);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? 'rgba(17, 24, 39, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: 3,
            border: `1px solid ${darkMode ? 'rgba(0, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
            maxHeight: '80vh',
            minHeight: '60vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          pb: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SearchIcon sx={{ color: '#06b6d4', fontSize: 28 }} />
            <Typography variant="h5" sx={{ color: darkMode ? '#fff' : '#111', fontWeight: 600 }}>
              {searchMode === 'facilities' ? 'Search Facilities' : 'Search Address'}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => {
              setShowSearchDialog(false);
              setSearchQuery('');
              setSearchResults([]);
              setSearchError(null);
              setAddressPredictions([]);
            }} 
            sx={{ color: darkMode ? '#9ca3af' : '#666' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, overflowY: 'auto' }}>
          {/* Search Mode Toggle */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              label="Facilities"
              onClick={() => {
                setSearchMode('facilities');
                setAddressPredictions([]);
              }}
              sx={{
                backgroundColor: searchMode === 'facilities' ? cyber.neonCyan : 'transparent',
                color: searchMode === 'facilities' ? '#000' : cyber.textMuted,
                border: `1px solid ${searchMode === 'facilities' ? cyber.neonCyan : cyber.borderDefault}`,
                fontWeight: 600,
                '&:hover': { backgroundColor: searchMode === 'facilities' ? cyber.neonCyan : `${cyber.neonCyan}20` }
              }}
            />
            <Chip
              icon={<PlaceIcon sx={{ fontSize: 16 }} />}
              label="Address"
              onClick={() => {
                setSearchMode('address');
                setSearchResults([]);
              }}
              sx={{
                backgroundColor: searchMode === 'address' ? '#f59e0b' : 'transparent',
                color: searchMode === 'address' ? '#000' : cyber.textMuted,
                border: `1px solid ${searchMode === 'address' ? '#f59e0b' : cyber.borderDefault}`,
                fontWeight: 600,
                '&:hover': { backgroundColor: searchMode === 'address' ? '#f59e0b' : '#f59e0b20' }
              }}
            />
          </Box>
          
          <TextField
            id="search-facilities-input"
            autoFocus
            fullWidth
            placeholder={searchMode === 'facilities' 
              ? "Search wells, SWDs, rigs, operators..." 
              : "Enter address, city, or location..."}
            value={searchQuery}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchMode === 'facilities') {
                handleSearch(searchQuery);
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searchMode === 'facilities' ? (
                    <SearchIcon sx={{ color: cyber.textMuted }} />
                  ) : (
                    <PlaceIcon sx={{ color: '#f59e0b' }} />
                  )}
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {(isSearching || isLoadingAddresses) && (
                    <CircularProgress size={20} sx={{ color: cyber.neonCyan }} />
                  )}
                  {/* Voice search button */}
                  {!(isSearching || isLoadingAddresses) && (
                    <IconButton
                      onClick={toggleVoiceSearch}
                      sx={{
                        color: isVoiceListening ? cyber.neonPink : cyber.textMuted,
                        animation: isVoiceListening ? 'pulse 1s infinite' : 'none',
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.15)' },
                          '100%': { transform: 'scale(1)' }
                        },
                        '&:hover': { color: cyber.neonCyan }
                      }}
                      size="small"
                    >
                      {isVoiceListening ? <MicIcon /> : <MicOffIcon />}
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{ 
              mt: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: cyber.bgPrimary,
                '& fieldset': { borderColor: cyber.borderDefault },
                '&:hover fieldset': { borderColor: cyber.neonCyan },
                '&.Mui-focused fieldset': { borderColor: cyber.neonCyan },
              },
              '& .MuiInputBase-input': { color: cyber.textPrimary }
            }}
          />
          
          {/* Quick Suggestions - only show when in facilities mode */}
          {searchMode === 'facilities' && searchQuery.length === 0 && (
            <>
              <Typography sx={{ color: darkMode ? '#9ca3af' : '#666', mb: 2, mt: 3, fontWeight: 600 }}>
                Quick Suggestions — Try:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {[
                  'Wells near Carlsbad',
                  'SWDs in Lea County',
                  'Rigs near Hobbs',
                  'Show me EOG wells',
                  'Chevron locations',
                  'Active drilling rigs',
                  'Producing wells',
                  'Saltwater disposal sites',
                  'Gas wells only',
                  'Wells within 10 miles',
                  'Closest SWD',
                  'XTO wells in Eddy County',
                ].map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    onClick={() => {
                      setSearchQuery(suggestion);
                      handleSearch(suggestion);
                      telegram.hapticFeedback('light');
                    }}
                    sx={{
                      backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                      color: darkMode ? '#fff' : '#333',
                      border: `1px solid ${darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)'}`,
                      fontSize: '0.85rem',
                      '&:hover': {
                        backgroundColor: darkMode ? 'rgba(6, 182, 212, 0.2)' : 'rgba(6, 182, 212, 0.15)',
                        borderColor: '#06b6d4',
                      }
                    }}
                  />
                ))}
              </Box>
              <Typography sx={{ color: darkMode ? '#9ca3af' : '#666', mb: 1.5, fontWeight: 600 }}>
                Quick Filters:
              </Typography>
            </>
          )}
          
          {/* Quick search buttons - only show for facilities mode */}
          {searchMode === 'facilities' && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={<LocalGasStationIcon />}
                label="Wells"
                onClick={() => { setSearchQuery(''); handleSearch(''); }}
                sx={{ 
                  backgroundColor: `${cyber.neonYellow}20`,
                  color: cyber.neonYellow,
                  border: `1px solid ${cyber.neonYellow}`,
                  '&:hover': { backgroundColor: `${cyber.neonYellow}30` }
                }}
              />
              <Chip
                icon={<WaterDropIcon />}
                label="SWDs"
                onClick={() => handleSearch('swd')}
                sx={{ 
                  backgroundColor: `${cyber.neonGreen}20`,
                  color: cyber.neonGreen,
                  border: `1px solid ${cyber.neonGreen}`,
                  '&:hover': { backgroundColor: `${cyber.neonGreen}30` }
                }}
              />
              <Chip
                icon={<ConstructionIcon />}
                label="Rigs"
                onClick={() => handleSearch('rig')}
                sx={{ 
                  backgroundColor: `${cyber.neonYellow}20`,
                  color: cyber.neonYellow,
                  border: `1px solid ${cyber.neonYellow}`,
                  '&:hover': { backgroundColor: `${cyber.neonYellow}30` }
                }}
              />
            </Box>
          )}
          
          {/* Address Predictions - show when in address mode */}
          {searchMode === 'address' && addressPredictions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ color: cyber.textMuted, mb: 1, display: 'block' }}>
                📍 {addressPredictions.length} locations found
              </Typography>
              <List sx={{ 
                maxHeight: 300, 
                overflow: 'auto',
                backgroundColor: cyber.bgPrimary,
                borderRadius: 2,
                border: `1px solid ${cyber.borderDefault}`
              }}>
                {addressPredictions.map((prediction, idx) => (
                  <ListItem key={prediction.placeId || idx} disablePadding>
                    <ListItemButton
                      onClick={() => handleSelectAddress(prediction)}
                      sx={{
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: '#f59e0b15'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <PlaceIcon sx={{ color: '#f59e0b' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>
                            {prediction.mainText}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                            {prediction.secondaryText}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {/* Address mode hint */}
          {searchMode === 'address' && searchQuery.length < 3 && addressPredictions.length === 0 && (
            <Typography sx={{ mt: 2, color: cyber.textMuted, textAlign: 'center', fontSize: '0.85rem' }}>
              🚚 Type at least 3 characters to search for an address or location
            </Typography>
          )}
          
          {searchError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {searchError}
            </Alert>
          )}
          
          {/* Search Results - only show for facilities mode */}
          {searchMode === 'facilities' && searchResults.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ color: cyber.textMuted, mb: 1, display: 'block' }}>
                {searchResults.length} results found
              </Typography>
              <List sx={{ 
                maxHeight: 300, 
                overflow: 'auto',
                backgroundColor: cyber.bgPrimary,
                borderRadius: 2,
                border: `1px solid ${cyber.borderDefault}`
              }}>
                {searchResults.slice(0, 50).map((facility) => (
                  <ListItem key={facility.id || facility.api} disablePadding>
                    <ListItemButton
                      onClick={() => handleSelectSearchResult(facility)}
                      sx={{
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: `${cyber.neonCyan}15`
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {facility.type === 'swd' ? (
                          <WaterDropIcon sx={{ color: cyber.neonGreen }} />
                        ) : facility.type === 'rig' ? (
                          <ConstructionIcon sx={{ color: cyber.neonYellow }} />
                        ) : (
                          <LocalGasStationIcon sx={{ color: cyber.neonYellow }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography sx={{ color: cyber.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>
                            {facility.wellName}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                              {facility.operator}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Chip 
                                label={facility.type?.toUpperCase() || 'WELL'} 
                                size="small" 
                                sx={{ 
                                  height: 18, 
                                  fontSize: '0.65rem',
                                  backgroundColor: facility.type === 'swd' ? `${cyber.neonGreen}30` : `${cyber.neonYellow}30`,
                                  color: facility.type === 'swd' ? cyber.neonGreen : cyber.neonYellow
                                }} 
                              />
                              {facility.distance && (
                                <Typography variant="caption" sx={{ color: cyber.textMuted }}>
                                  {facility.distance.toFixed(1)} mi
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {searchMode === 'facilities' && searchQuery && !isSearching && searchResults.length === 0 && !searchError && (
            <Typography sx={{ mt: 2, color: cyber.textMuted, textAlign: 'center' }}>
              No facilities found. Try a different search or switch to Address mode.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setShowSearchDialog(false);
              setSearchQuery('');
              setSearchResults([]);
              setAddressPredictions([]);
              // Clear search result markers
              setSearchResultMarkers([]);
            }}
            sx={{ color: cyber.textMuted }}
          >
            Cancel
          </Button>
          {searchMode === 'facilities' && (
            <Button
              variant="contained"
              onClick={() => handleSearch(searchQuery)}
              disabled={isSearching}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: cyber.neonCyan,
                color: '#000',
                '&:hover': { backgroundColor: '#22d3ee' }
              }}
            >
              Search
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Caliche Routes Dialog - opened from right-click */}
      <CommunityRoutes
        open={showCommunityRoutes}
        onClose={() => {
          setShowCommunityRoutes(false);
          setRoutePhotoLocation(null);
        }}
        initialLocation={routePhotoLocation}
        startInAddMode={routePhotoLocation !== null}
      />

      {/* Caliche Route Turn-by-Turn Navigation Panel */}
      {showCalichemRouteDetail && selectedCalichemRoute && (
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'rgba(17, 24, 39, 0.98)',
          backdropFilter: 'blur(16px)',
          borderTop: `2px solid ${cyber.neonGreen}`,
          borderRadius: '16px 16px 0 0',
          p: 2,
          zIndex: 2000,
          boxShadow: `0 -8px 32px rgba(0,0,0,0.5), 0 0 30px ${cyber.neonGreen}20`,
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: cyber.neonGreen }}>
                🧭 {selectedCalichemRoute.name}
              </Typography>
              <Chip 
                label={`Step ${currentLandmarkIndex + 1}/${selectedCalichemRouteLandmarks.length}`}
                size="small"
                sx={{ 
                  backgroundColor: `${cyber.neonCyan}30`, 
                  color: cyber.neonCyan,
                  fontWeight: 600
                }}
              />
            </Box>
            <IconButton 
              onClick={() => {
                setShowCalichemRouteDetail(false);
                setSelectedCalichemRoute(null);
                setSelectedCalichemRouteLandmarks([]);
                setCurrentLandmarkIndex(0);
              }}
              sx={{ color: cyber.textMuted }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Current Landmark */}
          {selectedCalichemRouteLandmarks[currentLandmarkIndex] && (
            <Box sx={{ 
              backgroundColor: 'rgba(34, 197, 94, 0.1)', 
              border: `1px solid ${cyber.neonGreen}50`,
              borderRadius: 2, 
              p: 2, 
              mb: 2 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography sx={{ fontSize: '3rem' }}>
                  {(() => {
                    const type = selectedCalichemRouteLandmarks[currentLandmarkIndex].landmark_type;
                    const icons: Record<string, string> = {
                      start: '🚩', destination: '🏁', turn_left: '↰', turn_right: '↱',
                      fork_left: '⤹', fork_right: '⤸', gate: '🚧', cattle_guard: '🐄',
                      hazard: '⚠️', continue_straight: '⬆️', waypoint: '📍',
                    };
                    return icons[type] || '📍';
                  })()}
                </Typography>
                <Box>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: cyber.textPrimary }}>
                    {selectedCalichemRouteLandmarks[currentLandmarkIndex].name || 
                     selectedCalichemRouteLandmarks[currentLandmarkIndex].landmark_type.replace(/_/g, ' ')}
                  </Typography>
                  {selectedCalichemRouteLandmarks[currentLandmarkIndex].description && (
                    <Typography sx={{ fontSize: '0.85rem', color: cyber.textSecondary }}>
                      {selectedCalichemRouteLandmarks[currentLandmarkIndex].description}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Navigation Controls */}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={() => setCurrentLandmarkIndex(Math.max(0, currentLandmarkIndex - 1))}
              disabled={currentLandmarkIndex === 0}
              sx={{ 
                flex: 1, 
                py: 1.5,
                borderColor: cyber.borderDefault,
                color: cyber.textPrimary,
                '&:hover': { borderColor: cyber.neonCyan, backgroundColor: `${cyber.neonCyan}10` }
              }}
            >
              ← Previous
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                const nextIndex = currentLandmarkIndex + 1;
                if (nextIndex < selectedCalichemRouteLandmarks.length) {
                  setCurrentLandmarkIndex(nextIndex);
                  // Pan to next landmark
                  const landmark = selectedCalichemRouteLandmarks[nextIndex];
                  mapInstanceRef.current?.panTo({ lat: landmark.latitude, lng: landmark.longitude });
                  telegram.hapticFeedback('light');
                } else {
                  // Completed!
                  telegram.hapticFeedback('success');
                  setToast({ open: true, message: '🎉 You have arrived!', severity: 'success' });
                }
              }}
              sx={{ 
                flex: 2,
                py: 1.5,
                backgroundColor: currentLandmarkIndex === selectedCalichemRouteLandmarks.length - 1 ? cyber.neonGreen : cyber.neonCyan,
                color: '#000',
                fontWeight: 700,
                '&:hover': { 
                  backgroundColor: currentLandmarkIndex === selectedCalichemRouteLandmarks.length - 1 ? '#4ade80' : '#22d3ee'
                }
              }}
            >
              {currentLandmarkIndex === selectedCalichemRouteLandmarks.length - 1 ? '🏁 Arrived!' : 'Next →'}
            </Button>
          </Box>

          {/* External Navigation Options */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const landmark = selectedCalichemRouteLandmarks[currentLandmarkIndex];
                window.open(`https://maps.apple.com/?daddr=${landmark.latitude},${landmark.longitude}&dirflg=d`, '_blank');
              }}
              sx={{ flex: 1, color: cyber.textMuted, fontSize: '0.75rem' }}
            >
              🍎 Apple Maps
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const landmark = selectedCalichemRouteLandmarks[currentLandmarkIndex];
                window.open(`https://www.google.com/maps/dir/?api=1&destination=${landmark.latitude},${landmark.longitude}&travelmode=driving`, '_blank');
              }}
              sx={{ flex: 1, color: cyber.textMuted, fontSize: '0.75rem' }}
            >
              🗺️ Google Maps
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
}
